import React, { useState, useEffect, useRef } from "react";
import { supabase } from "./supabaseClient";

// ─── ERROR BOUNDARY ──────────────────────────────────────────────────────────
// Catches render errors in child components so a single crash doesn't
// take down the entire app. Shows a recovery UI with an reload option.
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error("CTC ErrorBoundary:", error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{padding:"60px 24px",textAlign:"center",fontFamily:"'DM Sans',sans-serif"}}>
          <div style={{fontSize:"48px",marginBottom:"16px"}}>🎾</div>
          <h2 style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"28px",letterSpacing:"2px",marginBottom:"12px"}}>
            Something went wrong
          </h2>
          <p style={{color:"#6B6B6B",fontSize:"14px",marginBottom:"24px",maxWidth:"400px",margin:"0 auto 24px"}}>
            An unexpected error occurred. Please try reloading the page.
          </p>
          <button onClick={()=>window.location.reload()}
            style={{padding:"12px 28px",background:"#CC0000",color:"white",border:"none",borderRadius:"3px",
              fontSize:"14px",fontWeight:"700",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
            Reload Page
          </button>
          {this.state.error && (
            <details style={{marginTop:"24px",fontSize:"12px",color:"#999",textAlign:"left",maxWidth:"500px",margin:"24px auto 0"}}>
              <summary style={{cursor:"pointer"}}>Technical details</summary>
              <pre style={{whiteSpace:"pre-wrap",wordBreak:"break-all",marginTop:"8px"}}>{this.state.error.toString()}</pre>
            </details>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const TIMES = ["Mornings","Mid-day","Afternoons","Nights"];
const SKILLS = ["Beginner","Intermediate","Advanced"];
const NTRPS = ["2.0","2.5","3.0","3.5","4.0","4.5","5.0"];
const PLAY_TYPES = ["Singles","Doubles","Both"];
const LESSON_TYPES = ["Group","Individual","Youth","Adult","Beginner","Intermediate","Advanced"];
const GENDERS = ["Male","Female","Prefer not to say"];
const AGE_RANGES = ["18–29","30–49","50–69","70+"];
const EXCHANGE_CATS = ["Racquets","Bags & Cases","Shoes & Apparel","Ball Machines","Other / Misc"];

const VIEWS = {
  HOME: "home", LOGIN: "login", REGISTER: "register",
  COURTS: "courts", INSTRUCTORS: "instructors", STRINGING: "stringing",
  BLOG: "blog", CONNECTIONS: "connections", ADMIN: "admin",
  BULLETIN: "bulletin", EXCHANGE: "exchange", DASHBOARD: "dashboard",
  TERMS: "terms", PRIVACY: "privacy", RESET_PASSWORD: "reset_password",
  CONTACT: "contact",
};

// ─── EDGE FUNCTION URLs (centralized for maintenance) ────────────────────────
const EDGE_FN_BASE = "https://fggtrmmxfetarsuoyair.supabase.co/functions/v1";
const EDGE_FN_URLS = {
  NOTIFY_REGISTRATION: `${EDGE_FN_BASE}/notify-registration`,
  NOTIFY_APPROVAL:     `${EDGE_FN_BASE}/notify-approval`,
  NOTIFY_CONTACT_REQ:  `${EDGE_FN_BASE}/notify-contact-request`,
  NOTIFY_CONTACT_ACC:  `${EDGE_FN_BASE}/notify-contact-accepted`,
  NOTIFY_NEW_POST:     `${EDGE_FN_BASE}/notify-new-post`,
  NOTIFY_CONTACT_US:   `${EDGE_FN_BASE}/notify-contact-us`,
};

// ─── MOCK DATA ────────────────────────────────────────────────────────────────
// MOCK_COURTS removed — courts loaded from Supabase

// MOCK_INSTRUCTORS removed — loaded from Supabase

// MOCK_STRINGERS removed — loaded from Supabase

// MOCK_POSTS removed — loaded from Supabase

// MOCK_PLAYERS, MOCK_ADMIN_QUEUE, MOCK_CONTACT_REQUESTS removed — using live Supabase data

// MOCK_BULLETIN and MOCK_EXCHANGE removed — using live Supabase data

// ─── SVG COMPONENTS ───────────────────────────────────────────────────────────

// Trash and pencil icons for edit/delete action buttons
const IconTrash = ({ size=16 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 4h12M5 4V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1M6 7v5M10 7v5M3 4l.9 8.1A1 1 0 0 0 4.9 13h6.2a1 1 0 0 0 1-.9L13 4"
      stroke="var(--charcoal)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconPencil = ({ size=16 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11.5 2.5a1.414 1.414 0 0 1 2 2L5 13H3v-2L11.5 2.5z"
      stroke="var(--charcoal)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Hero background image — change this URL to swap hero images for testing
const HERO_IMAGE_URL = "https://fggtrmmxfetarsuoyair.supabase.co/storage/v1/object/public/site-images/Hero%20NEW%205.jpg";

const TennisBall = ({ size=40, opacity=1, className="" }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" className={className} style={{opacity}}>
    <circle cx="50" cy="50" r="48" fill="#C8E632" stroke="#A8C420" strokeWidth="2"/>
    <path d="M 18 28 Q 50 50 18 72" fill="none" stroke="white" strokeWidth="5" strokeLinecap="round"/>
    <path d="M 82 28 Q 50 50 82 72" fill="none" stroke="white" strokeWidth="5" strokeLinecap="round"/>
  </svg>
);

// RallyBall — JS-driven, random angle each pass, simulates top-down tennis rally
// Ball travels to near-baseline depths (±530px from center) matching widened court.
// Spin: CSS rotate transitions 1440° per shot (4 full rotations), direction matches travel.
const RallyBall = () => {
  // Court is 1140px wide, centered. Baselines at ±570px. Ball stops ~40px short.
  // Singles sidelines at ±197px from center vertically.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const POSITIONS = [
    [-530,  160],  // deep left baseline, lower
    [-530,  -60],  // deep left baseline, upper
    [-530,   90],  // deep left, mid
    [-530, -180],  // deep left, near top singles line
    [-530,  190],  // deep left, near bottom singles line
  ];
  const RETURN_POSITIONS = [
    [ 530, -180],  // deep right, near top singles line
    [ 530,  190],  // deep right, near bottom singles line
    [ 530,   30],  // deep right, center
    [ 530,  -90],  // deep right, upper mid
    [ 530,  110],  // deep right, lower mid
  ];

  const [pos, setPos]       = useState({x:-530, y:160});
  const [spinDeg, setSpinDeg] = useState(0);
  const [opacity, setOpacity] = useState(0.55);
  const dirRef   = useRef(1);   // 1 = going right, -1 = going left
  const shotRef  = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    const shoot = () => {
      const shot   = shotRef.current;
      const going  = dirRef.current;
      let target;
      if (going === 1) {
        target = RETURN_POSITIONS[shot % RETURN_POSITIONS.length];
      } else {
        target = POSITIONS[(shot + 1) % POSITIONS.length];
      }
      setPos({x: target[0], y: target[1]});
      setOpacity(0.55);
      // 4 full rotations per shot; direction matches ball travel direction
      setSpinDeg(prev => prev + going * 1440);
      shotRef.current  = shot + 1;
      dirRef.current   = going * -1;
      const duration   = 1800 + (shot % 4) * 200;
      timerRef.current = setTimeout(shoot, duration + 400);
    };
    timerRef.current = setTimeout(shoot, 600);
    return () => clearTimeout(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const duration = 1800 + (shotRef.current % 4) * 200;
  return (
    <div style={{
      position:"absolute", left:"50%", top:"50%",
      transform:`translateX(-50%) translateY(-50%) translate(${pos.x}px, ${pos.y}px)`,
      transition:`transform ${duration}ms cubic-bezier(0.25,0.46,0.45,0.94)`,
      opacity, zIndex:3, pointerEvents:"none"
    }}>
      <div style={{
        transform:`rotate(${spinDeg}deg)`,
        transition:`transform ${duration}ms linear`,
      }}>
        <TennisBall size={86} opacity={0.55}/>
      </div>
    </div>
  );
};

// Authentic Paul Rand Dancing C — bold geometric sans-serif, consistent tilt, flat color disc
const DANCING_C_COLORS = ["#CC0000","#F5C800","#0057A8","#3A7D44","#F5F0E8"];
const CONSISTENT_TILT = -35; // matches "Club" C rotation per design feedback

const DancingCMark = ({ size=48, colorIndex=0 }) => {
  const color = DANCING_C_COLORS[colorIndex % DANCING_C_COLORS.length];
  const textColor = color === "#F5F0E8" ? "#1A1A1A" : "white";
  // Filled path matching reference image: r_outer=42, r_inner=16, stroke=26 (27% of disc).
  // gap_half=8 → gap height=16 (~62% of stroke width — narrow opening like reference).
  // Endpoints at same y (42 & 58) → perfectly horizontal rectangular gap ends.
  // Opening faces right, rotated -35° (1:10 o'clock) to match reference orientation.
  const cPath = "M 91.2 42 A 42 42 0 1 0 91.2 58 L 63.9 58 A 16 16 0 1 1 63.9 42 Z";
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" style={{display:"block",flexShrink:0}}>
      <circle cx="50" cy="50" r="48" fill={color}/>
      <path
        d={cPath}
        fill={textColor}
        transform={`rotate(${CONSISTENT_TILT}, 50, 50)`}
      />
    </svg>
  );
};

// WordMark — Dancing C discs sized to match Bebas Neue cap height, tight letter spacing
// eslint-disable-next-line no-unused-vars
const WordMark = ({ size=1 }) => {
  const fontSize = 26 * size;
  // Bebas Neue cap height is ~72% of font-size; add slight extra so disc feels same visual weight
  const discSize = Math.round(fontSize * 1.05);
  return (
    <div style={{display:"flex",alignItems:"center",gap:"0px",lineHeight:1}}>
      <DancingCMark size={discSize} colorIndex={0}/>
      <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:`${fontSize}px`,
        letterSpacing:"2px",color:"var(--cream)",marginLeft:"1px"}}>OLUMBUS</span>
      <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:`${fontSize}px`,
        letterSpacing:"2px",color:"rgba(245,240,232,0.4)",margin:`0 ${Math.round(fontSize*0.3)}px`}}>·</span>
      <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:`${fontSize}px`,
        letterSpacing:"2px",color:"var(--cream)"}}>TENNIS</span>
      <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:`${fontSize}px`,
        letterSpacing:"2px",color:"rgba(245,240,232,0.4)",margin:`0 ${Math.round(fontSize*0.3)}px`}}>·</span>
      <DancingCMark size={discSize} colorIndex={3}/>
      <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:`${fontSize}px`,
        letterSpacing:"2px",color:"var(--cream)",marginLeft:"1px"}}>LUB</span>
    </div>
  );
};

// TennisBallC — Real tennis ball texture C image, replaces SVG version.
// Uses uploaded PNG with transparent background.
const TENNIS_C_URL = "https://fggtrmmxfetarsuoyair.supabase.co/storage/v1/object/public/site-images/Real%20Tennis%204.png";
const TennisBallC = ({ size=48 }) => (
  <img src={TENNIS_C_URL} alt="C" width={size} height={size}
    style={{display:"block",flexShrink:0,objectFit:"contain"}}/>
);

const RatingBadge = ({ ntrp, skill }) => {
  const colors = { Beginner:"#4A7C4A", Intermediate:"#C49010", Advanced:"#CC0000" };
  return (
    <span style={{display:"inline-flex",alignItems:"center",gap:"5px",background:colors[skill]||"#666",
      color:"white",borderRadius:"20px",padding:"3px 10px",fontSize:"11px",fontWeight:"700",letterSpacing:"0.5px"}}>
      {ntrp && <span>{ntrp}</span>}
      {ntrp && skill && <span style={{opacity:0.5}}>·</span>}
      {skill && <span>{skill}</span>}
    </span>
  );
};

const AvailPill = ({ label, active, onClick }) => (
  <button onClick={onClick} style={{padding:"5px 12px",borderRadius:"20px",fontSize:"12px",fontWeight:"600",
    cursor:"pointer",border:"2px solid",borderColor:active?"var(--red)":"var(--grey-light)",
    background:active?"var(--red)":"transparent",color:active?"white":"var(--grey)",transition:"all 0.15s",
    fontFamily:"'DM Sans',sans-serif"}}>
    {label}
  </button>
);

// ─── TILE / PAGE ICONS ────────────────────────────────────────────────────────

// ─── ICON THEMING ─────────────────────────────────────────────────────────────
// Icons accept `dark` prop. dark=false (default) = light strokes for dark tile bg.
// dark=true = dark strokes for light sub-page bg.
// Helper: returns appropriate stroke/fill colors based on dark prop
const ic = (dark) => ({
  fg:   dark ? "var(--black)"   : "var(--cream)",
  fgA:  (a) => dark ? `rgba(26,26,26,${a})` : `rgba(245,240,232,${a})`,
  pin:  dark ? "var(--charcoal)": "var(--black)",
  red:  "var(--red)",
  gold: "var(--gold)",
});

// Handshake — two clearly defined geometric hands gripping from left (cream) and right (red)
// High-Five — two stick figures facing each other, arms raised meeting at contact point
// Inspired by reference: bold filled silhouettes, circular heads, rounded torsos, impact sparks
const IconHandshake = ({ size=40, dark=false }) => {
  const c = ic(dark);
  return (
  <svg width={size} height={size} viewBox="0 0 80 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* LEFT FIGURE — cream */}
    {/* Head */}
    <circle cx="18" cy="16" r="8" fill={c.fg}/>
    {/* Torso — rounded rectangle body */}
    <path d="M10 28 Q10 24 18 24 Q26 24 26 28 L26 50 Q26 54 22 54 L14 54 Q10 54 10 50 Z" fill={c.fg}/>
    {/* Left arm — raised diagonally toward center-top */}
    <path d="M24 32 Q34 22 38 14" stroke={c.fg} strokeWidth="7" strokeLinecap="round" fill="none"/>
    {/* Left hand — small circle at contact */}
    <circle cx="38" cy="13" r="4.5" fill={c.fg}/>

    {/* RIGHT FIGURE — red */}
    {/* Head */}
    <circle cx="62" cy="16" r="8" fill={c.red}/>
    {/* Torso */}
    <path d="M54 28 Q54 24 62 24 Q70 24 70 28 L70 50 Q70 54 66 54 L58 54 Q54 54 54 50 Z" fill={c.red}/>
    {/* Right arm — raised diagonally toward center-top */}
    <path d="M56 32 Q46 22 42 14" stroke={c.red} strokeWidth="7" strokeLinecap="round" fill="none"/>
    {/* Right hand — small circle at contact */}
    <circle cx="42" cy="13" r="4.5" fill={c.red}/>

    {/* IMPACT SPARKS at contact zone — center top */}
    <line x1="40" y1="4"  x2="40" y2="8"  stroke={c.fg} strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="33" y1="6"  x2="35" y2="9"  stroke={c.fg} strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="47" y1="6"  x2="45" y2="9"  stroke={c.fg} strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="30" y1="10" x2="33" y2="11" stroke={c.fg} strokeWidth="2"   strokeLinecap="round" opacity="0.7"/>
    <line x1="50" y1="10" x2="47" y2="11" stroke={c.fg} strokeWidth="2"   strokeLinecap="round" opacity="0.7"/>
  </svg>
  );
};

// Tennis ball hopper — Lesson Instructors tile — square 64×64 viewBox
// Basket: WIDER AT TOP (rim), NARROWER AT BOTTOM — correct bucket/hopper shape
// Tennis ball hopper — wide at TOP, tapers toward bottom, taller handle matching reference proportions
// Handle height ≈ basket height (roughly 1:1 ratio per reference image)
const IconInstructor = ({ size=40, dark=false }) => {
  const c = ic(dark);
  const ball = "#7DC832";
  const clip = `hClip${Math.random().toString(36).slice(2,6)}`;
  return (
  <svg width={size} height={size} viewBox="0 0 64 80" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* BASKET: wider at TOP (x:8..56), tapers to bottom (x:14..50), pushed down for tall handle */}
    <path d="M8 40 L14 78 L50 78 L56 40 Z"
      stroke={c.fg} strokeWidth="3.5" fill="none" strokeLinejoin="round"/>
    {/* TOP RIM */}
    <line x1="8" y1="40" x2="56" y2="40" stroke={c.fg} strokeWidth="3.5" strokeLinecap="round"/>

    {/* BRACKET CLIPS at top rim corners */}
    <rect x="5" y="36" width="8" height="8" rx="2" fill={c.fgA(0.55)}/>
    <rect x="51" y="36" width="8" height="8" rx="2" fill={c.fgA(0.55)}/>

    {/* HANDLE ARCH: tall U-shape — legs run from y=36 up to y=10 before curving */}
    <path d="M11 36 L11 10 Q11 4 21 4 L43 4 Q53 4 53 10 L53 36"
      stroke={c.fg} strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>

    {/* GRIP BAR at top of arch */}
    <rect x="19" y="1" width="26" height="7" rx="3" fill={c.fgA(0.55)}/>

    {/* WIRE SLATS angled to follow basket taper */}
    <line x1="19" y1="40" x2="21" y2="78" stroke={c.fg} strokeWidth="1.5" opacity="0.4"/>
    <line x1="28" y1="40" x2="28" y2="78" stroke={c.fg} strokeWidth="1.5" opacity="0.4"/>
    <line x1="37" y1="40" x2="37" y2="78" stroke={c.fg} strokeWidth="1.5" opacity="0.4"/>
    <line x1="46" y1="40" x2="44" y2="78" stroke={c.fg} strokeWidth="1.5" opacity="0.4"/>

    {/* BALLS: staggered rows (4-3-4) for natural pile look, r=5 */}
    <clipPath id={clip}>
      <path d="M8 40 L14 78 L50 78 L56 40 Z"/>
    </clipPath>
    <g clipPath={`url(#${clip})`}>
      {/* Row 1 (top): 4 balls */}
      <circle cx="16" cy="51" r="5" fill={ball}/>
      <circle cx="27" cy="51" r="5" fill={ball}/>
      <circle cx="38" cy="51" r="5" fill={ball}/>
      <circle cx="49" cy="51" r="5" fill={ball}/>
      {/* Row 2: 3 balls offset — nestled between row 1 */}
      <circle cx="21" cy="61" r="5" fill={ball}/>
      <circle cx="32" cy="61" r="5" fill={ball}/>
      <circle cx="43" cy="61" r="5" fill={ball}/>
      {/* Row 3 (bottom): 4 balls */}
      <circle cx="17" cy="71" r="5" fill={ball}/>
      <circle cx="27" cy="72" r="5" fill={ball}/>
      <circle cx="37" cy="71" r="5" fill={ball}/>
      <circle cx="47" cy="72" r="5" fill={ball}/>
    </g>

    {/* Redraw basket rim and sides on top of balls */}
    <line x1="8" y1="40" x2="56" y2="40" stroke={c.fg} strokeWidth="3.5" strokeLinecap="round"/>
    <path d="M8 40 L14 78 L50 78 L56 40"
      stroke={c.fg} strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
  );
};

// Racquet Stringing — thinner frame (rx=18,ry=20), subtle butt cap, exaggerated snap
const IconStringing = ({ size=40, dark=false }) => {
  const c = ic(dark);
  const bedId = `bed-${Math.random().toString(36).slice(2,7)}`;
  const fill = c.fg;
  // eslint-disable-next-line no-unused-vars
  const bg   = c.pin;
  return (
  <svg width={size} height={size} viewBox="0 0 52 84" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* OUTER SILHOUETTE with THROAT CUT OUT via evenodd rule */}
    <path fillRule="evenodd"
      d="M22 80 L22 76 L23 76 L23 62 L14.4 37.3 A18 20 0 1 1 37.6 37.3 L29 62 L29 76 L30 76 L30 80 Z
         M18 43 L34 43 L26 62 Z"
      fill={fill}/>

    {/* STRING BED — always cream background so strings contrast */}
    <ellipse cx="26" cy="22" rx="15" ry="17" fill="var(--cream)"/>

    {/* STRING GRID — always contrast against bed */}
    <clipPath id={bedId}><ellipse cx="26" cy="22" rx="15" ry="17"/></clipPath>
    <g clipPath={`url(#${bedId})`}>
      <line x1="14" y1="5"  x2="14" y2="39" stroke="var(--black)" strokeWidth="1.6"/>
      <line x1="17" y1="5"  x2="17" y2="39" stroke="var(--black)" strokeWidth="1.6"/>
      <line x1="20" y1="5"  x2="20" y2="39" stroke="var(--black)" strokeWidth="1.6"/>
      <line x1="23" y1="5"  x2="23" y2="39" stroke="var(--black)" strokeWidth="1.6"/>
      <line x1="26" y1="5"  x2="26" y2="39" stroke="var(--black)" strokeWidth="1.6"/>
      <line x1="29" y1="5"  x2="29" y2="39" stroke="var(--black)" strokeWidth="1.6"/>
      <line x1="32" y1="5"  x2="32" y2="39" stroke="var(--black)" strokeWidth="1.6"/>
      <line x1="35" y1="6"  x2="35" y2="38" stroke="var(--black)" strokeWidth="1.6"/>
      <line x1="38" y1="7"  x2="38" y2="37" stroke="var(--black)" strokeWidth="1.6"/>
      <line x1="11" y1="8"  x2="41" y2="8"  stroke="var(--black)" strokeWidth="1.6"/>
      <line x1="11" y1="11" x2="41" y2="11" stroke="var(--black)" strokeWidth="1.6"/>
      <line x1="11" y1="14" x2="41" y2="14" stroke="var(--black)" strokeWidth="1.6"/>
      <line x1="11" y1="17" x2="41" y2="17" stroke="var(--black)" strokeWidth="1.6"/>
      {/* BROKEN CENTER ROW — stubs only */}
      <line x1="11" y1="20" x2="20" y2="20" stroke="var(--black)" strokeWidth="1.6"/>
      <line x1="32" y1="20" x2="41" y2="20" stroke="var(--black)" strokeWidth="1.6"/>
      <line x1="11" y1="23" x2="41" y2="23" stroke="var(--black)" strokeWidth="1.6"/>
      <line x1="11" y1="26" x2="41" y2="26" stroke="var(--black)" strokeWidth="1.6"/>
      <line x1="11" y1="29" x2="41" y2="29" stroke="var(--black)" strokeWidth="1.6"/>
      <line x1="11" y1="32" x2="41" y2="32" stroke="var(--black)" strokeWidth="1.6"/>
      <line x1="11" y1="35" x2="41" y2="35" stroke="var(--black)" strokeWidth="1.6"/>
    </g>

    {/* BROKEN STRING SNAP: exaggerated — long dramatic curls, thick strokes, prominent sparks */}
    <path d="M20 20 Q14 14 11 9 Q9 6 12 5"
      stroke={c.red} strokeWidth="3" fill="none" strokeLinecap="round"/>
    <path d="M32 20 Q38 26 41 31 Q43 34 40 35"
      stroke={c.red} strokeWidth="3" fill="none" strokeLinecap="round"/>
    <line x1="24" y1="16" x2="21" y2="10" stroke={c.red} strokeWidth="2.2" strokeLinecap="round"/>
    <line x1="28" y1="16" x2="31" y2="10" stroke={c.red} strokeWidth="2" strokeLinecap="round" opacity="0.85"/>
    <line x1="24" y1="24" x2="21" y2="30" stroke={c.red} strokeWidth="2.2" strokeLinecap="round"/>
    <line x1="28" y1="24" x2="31" y2="30" stroke={c.red} strokeWidth="2" strokeLinecap="round" opacity="0.85"/>
  </svg>
  );
};

// Court pin — red map pin with tennis ball center (red contrasts better on dark tile)
const IconCourts = ({ size=40, dark=false }) => {
  return (
  <svg width={size} height={size} viewBox="0 0 48 56" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Red map pin */}
    <path d="M24 2 C13 2 4 11 4 22 C4 35 24 54 24 54 C24 54 44 35 44 22 C44 11 35 2 24 2 Z" fill="#CC0000"/>
    {/* Tennis ball — yellow-green fill */}
    <circle cx="24" cy="22" r="10" fill="#C8E632"/>
    {/* Tennis ball seam curves — white arcs */}
    <path d="M16 17 Q20 22 16 27" stroke="white" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
    <path d="M32 17 Q28 22 32 27" stroke="white" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
  </svg>
  );
};

// News/calendar — Events & News tile
const IconEvents = ({ size=40, dark=false }) => {
  const c = ic(dark);
  return (
  <svg width={size} height={size} viewBox="0 0 48 52" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="8" width="40" height="38" rx="4" stroke={c.fg} strokeWidth="2.5" fill="none"/>
    <line x1="4" y1="18" x2="44" y2="18" stroke={c.fg} strokeWidth="2.5"/>
    <line x1="16" y1="4" x2="16" y2="14" stroke={c.fg} strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="32" y1="4" x2="32" y2="14" stroke={c.fg} strokeWidth="2.5" strokeLinecap="round"/>
    <rect x="10" y="24" width="6" height="5" rx="1.5" fill={c.red}/>
    <rect x="21" y="24" width="6" height="5" rx="1.5" fill={c.fgA(0.5)}/>
    <rect x="32" y="24" width="6" height="5" rx="1.5" fill={c.fgA(0.5)}/>
    <rect x="10" y="33" width="6" height="5" rx="1.5" fill={c.fgA(0.5)}/>
    <rect x="21" y="33" width="6" height="5" rx="1.5" fill={c.fgA(0.5)}/>
    <rect x="32" y="33" width="6" height="5" rx="1.5" fill={c.fgA(0.35)}/>
  </svg>
  );
};

// Member / person icon
const IconMember = ({ size=40, loggedIn=false, dark=false }) => {
  const c = ic(dark);
  return (
  <svg width={size} height={size} viewBox="0 0 48 52" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="14" r="10" stroke={c.fg} strokeWidth="2.5" fill="none"/>
    <path d="M4 46 C4 34 12 28 24 28 C36 28 44 34 44 46" stroke={c.fg} strokeWidth="2.5" strokeLinecap="round" fill="none"/>
    {loggedIn && <path d="M32 8 L36 12 L44 4" stroke={c.red} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>}
  </svg>
  );
};

// Bulletin board / pinboard icon
const IconBulletin = ({ size=40, dark=false }) => {
  const c = ic(dark);
  return (
  <svg width={size} height={size} viewBox="0 0 52 56" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="6" width="48" height="46" rx="3" stroke={c.fg} strokeWidth="2.5" fill="none"/>
    <rect x="2" y="6" width="48" height="8" rx="3" fill={c.fgA(0.15)} stroke={c.fg} strokeWidth="2.5"/>
    <circle cx="26" cy="10" r="3" fill={c.red} stroke={c.fg} strokeWidth="1"/>
    <rect x="8" y="20" width="16" height="13" rx="2" fill={c.fgA(0.12)} stroke={c.fg} strokeWidth="1.8"/>
    <line x1="11" y1="25" x2="21" y2="25" stroke={c.fg} strokeWidth="1.2" opacity="0.6"/>
    <line x1="11" y1="28" x2="19" y2="28" stroke={c.fg} strokeWidth="1.2" opacity="0.6"/>
    <circle cx="11" cy="22" r="1.5" fill={c.red}/>
    <rect x="28" y="18" width="16" height="13" rx="2" fill={c.fgA(0.12)} stroke={c.fg} strokeWidth="1.8"/>
    <line x1="31" y1="23" x2="41" y2="23" stroke={c.fg} strokeWidth="1.2" opacity="0.6"/>
    <line x1="31" y1="26" x2="39" y2="26" stroke={c.fg} strokeWidth="1.2" opacity="0.6"/>
    <circle cx="31" cy="20" r="1.5" fill={c.gold}/>
    <rect x="8" y="37" width="34" height="10" rx="2" fill={c.fgA(0.12)} stroke={c.fg} strokeWidth="1.8"/>
    <line x1="12" y1="41" x2="34" y2="41" stroke={c.fg} strokeWidth="1.2" opacity="0.6"/>
    <line x1="12" y1="44" x2="28" y2="44" stroke={c.fg} strokeWidth="1.2" opacity="0.6"/>
    <circle cx="12" cy="39" r="1.5" fill={c.fg} opacity="0.6"/>
  </svg>
  );
};

// Equipment Exchange — two wide price tags, large single-line text "FOR SALE" and "FREE"
// Equipment Exchange — two wide price tags stacked, large readable text — square 64×64
const IconExchange = ({ size=40, dark=false }) => {
  const c = ic(dark);
  return (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* FOR SALE tag — red, top half, tilted slightly CCW */}
    <g transform="rotate(-7, 32, 20)">
      <path d="M2 8 L2 28 L50 28 L60 18 L50 8 Z"
        fill="rgba(204,0,0,0.25)" stroke={c.red} strokeWidth="2" strokeLinejoin="round"/>
      <circle cx="10" cy="18" r="3" fill="none" stroke={c.red} strokeWidth="1.8"/>
      <text x="16" y="22" fontFamily="'Bebas Neue',Arial Narrow,sans-serif"
        fontSize="11" fontWeight="700" fill={c.red} letterSpacing="1">FOR SALE</text>
    </g>

    {/* FREE tag — green, bottom half, tilted slightly CW */}
    <g transform="rotate(5, 32, 46)">
      <path d="M4 36 L4 56 L46 56 L56 46 L46 36 Z"
        fill="rgba(58,125,68,0.25)" stroke="#3A7D44" strokeWidth="2" strokeLinejoin="round"/>
      <circle cx="12" cy="46" r="3" fill="none" stroke="#3A7D44" strokeWidth="1.8"/>
      <text x="18" y="50" fontFamily="'Bebas Neue',Arial Narrow,sans-serif"
        fontSize="11" fontWeight="700" fill="#3A7D44" letterSpacing="1">FREE ★</text>
    </g>
  </svg>
  );
};

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
// ── REGISTER VIEW (defined outside App to prevent remounting) ─────────────
function RegisterView({ nav, showToast, setAdminQueue, VIEWS, toggleArr,
  GENDERS, AGE_RANGES, NTRPS, SKILLS, PLAY_TYPES, DAYS, TIMES, DancingCMark, TennisBall, AvailPill }) {
  const [form, setForm] = useState({name:"",email:"",password:"",phone:"",ntrp:"",skill:"",play:"",days:[],times:[],gender:"",ageRange:""});
  const [errors, setErrors] = useState({});
  const [showTnC, setShowTnC] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showNtrpInfo, setShowNtrpInfo] = useState(false);
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const toggleF=(k,v)=>setForm(f=>({...f,[k]:toggleArr(f[k],v)}));

  const validate = () => {
    const e={};
    if(!form.name) e.name=true;
    if(!form.email) e.email=true;
    if(!form.password || form.password.length < 8) e.password=true;
    if(!form.phone) e.phone=true;
    if(!form.gender) e.gender=true;
    if(!form.ageRange) e.ageRange=true;
    if(!form.skill) e.skill=true;
    if(!form.play) e.play=true;
    setErrors(e);
    return Object.keys(e).length===0;
  };

  const handleSubmit = () => {
    if(!validate()) { showToast("Please fill in all required fields.","error"); return; }
    setShowTnC(true);
  };

  const handleAcceptTnC = async () => {
    setShowTnC(false);
    // 1. Create auth user in Supabase
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: form.email,
      password: form.password,
      options: { data: { name: form.name } }
    });
    if (authError) {
      showToast(authError.message || "Registration failed. Please try again.", "error");
      return;
    }
    // 2. Update the profile row (auto-created by trigger) with full details
    const userId = authData.user?.id;
    if (userId) {
      await supabase.from("profiles").update({
        name: form.name, phone: form.phone,
        ntrp: form.ntrp || null, skill: form.skill, play: form.play,
        days: form.days, times: form.times,
        gender: form.gender, age_range: form.ageRange,
        is_approved: false, is_active: true
      }).eq("id", userId);
      // Notify admin of new registration (fire and forget)
      fetch(EDGE_FN_URLS.NOTIFY_REGISTRATION, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name, email: form.email, phone: form.phone,
          ntrp: form.ntrp, skill: form.skill, play: form.play,
        }),
      }).catch(() => {});
      // Sign out using safe localStorage-clear pattern (avoids Supabase auth lock deadlock)
      Object.keys(localStorage).filter(k => k.startsWith('sb-')).forEach(k => localStorage.removeItem(k));
    }
    // Show success modal
    setShowSuccess(true);
  };

  const inputStyle = (field) => ({
    outline: errors[field] ? "2px solid var(--red)" : undefined,
    borderColor: errors[field] ? "var(--red)" : undefined,
  });

  return (
    <div style={{padding:"40px 20px",maxWidth:"660px",margin:"0 auto"}}>
      <button className="page-back" onClick={()=>nav(VIEWS.HOME)}>← Back to Home</button>
      <div style={{background:"white",borderRadius:"6px",overflow:"hidden",boxShadow:"var(--shadow-md)"}}>
        <div style={{background:"var(--black)",padding:"22px 28px",borderBottom:"3px solid var(--red)",
          display:"flex",alignItems:"center",gap:"14px"}}>
          <TennisBallC size={40}/>
          <div>
            <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"20px",letterSpacing:"2px",color:"var(--cream)"}}>
              Join Columbus Tennis Club
            </div>
            <div style={{fontSize:"11px",color:"rgba(245,240,232,0.5)",letterSpacing:"2px"}}>NEW MEMBER REGISTRATION</div>
          </div>
        </div>
        <div style={{padding:"28px"}}>
          <div className="form-grid">
            <div className="form-group">
              <label>Full Name *{errors.name&&<span style={{color:"var(--red)",marginLeft:"6px",fontSize:"11px"}}>Required</span>}</label>
              <input style={inputStyle("name")} value={form.name} onChange={e=>{set("name",e.target.value);setErrors(x=>({...x,name:false}));}} placeholder="Jane Smith"/>
            </div>
            <div className="form-group">
              <label>Email Address *{errors.email&&<span style={{color:"var(--red)",marginLeft:"6px",fontSize:"11px"}}>Required</span>}</label>
              <input style={inputStyle("email")} type="email" value={form.email} onChange={e=>{set("email",e.target.value);setErrors(x=>({...x,email:false}));}} placeholder="jane@email.com"/>
            </div>
            <div className="form-group">
              <label>Password *{errors.password&&<span style={{color:"var(--red)",marginLeft:"6px",fontSize:"11px"}}>Min 8 characters</span>}</label>
              <input style={inputStyle("password")} type="password" value={form.password} onChange={e=>{set("password",e.target.value);setErrors(x=>({...x,password:false}));}} placeholder="Minimum 8 characters"/>
            </div>
            <div className="form-group">
              <label>Phone Number *{errors.phone&&<span style={{color:"var(--red)",marginLeft:"6px",fontSize:"11px"}}>Required</span>}</label>
              <input style={inputStyle("phone")} value={form.phone} onChange={e=>{set("phone",e.target.value);setErrors(x=>({...x,phone:false}));}} placeholder="812-555-0100"/>
            </div>
            <div className="form-group">
              <label>Gender *{errors.gender&&<span style={{color:"var(--red)",marginLeft:"6px",fontSize:"11px"}}>Required</span>}</label>
              <select style={inputStyle("gender")} value={form.gender} onChange={e=>{set("gender",e.target.value);setErrors(x=>({...x,gender:false}));}}>
                <option value="">Select…</option>{GENDERS.map(g=><option key={g}>{g}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Age Range *{errors.ageRange&&<span style={{color:"var(--red)",marginLeft:"6px",fontSize:"11px"}}>Required</span>}</label>
              <select style={inputStyle("ageRange")} value={form.ageRange} onChange={e=>{set("ageRange",e.target.value);setErrors(x=>({...x,ageRange:false}));}}>
                <option value="">Select…</option>{AGE_RANGES.map(a=><option key={a}>{a}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label style={{display:"flex",alignItems:"center",gap:"6px"}}>
                NTRP Rating
                <span onClick={()=>setShowNtrpInfo(true)}
                  style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:"16px",height:"16px",
                    borderRadius:"50%",background:"var(--charcoal)",color:"white",fontSize:"10px",fontWeight:"700",
                    cursor:"pointer",lineHeight:1,flexShrink:0}} title="What is NTRP?">?</span>
              </label>
              <select value={form.ntrp} onChange={e=>set("ntrp",e.target.value)}>
                <option value="">Select…</option>{NTRPS.map(r=><option key={r}>{r}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Skill Level *{errors.skill&&<span style={{color:"var(--red)",marginLeft:"6px",fontSize:"11px"}}>Required</span>}</label>
              <select style={inputStyle("skill")} value={form.skill} onChange={e=>{set("skill",e.target.value);setErrors(x=>({...x,skill:false}));}}>
                <option value="">Select…</option>{SKILLS.map(s=><option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Play Type *{errors.play&&<span style={{color:"var(--red)",marginLeft:"6px",fontSize:"11px"}}>Required</span>}</label>
              <select style={inputStyle("play")} value={form.play} onChange={e=>{set("play",e.target.value);setErrors(x=>({...x,play:false}));}}>
                <option value="">Select…</option>{PLAY_TYPES.map(p=><option key={p}>{p}</option>)}
              </select>
            </div>
            <div className="form-group full"><label>Days Available</label>
              <div className="pill-group">{DAYS.map(d=><AvailPill key={d} label={d} active={form.days.includes(d)} onClick={()=>toggleF("days",d)}/>)}</div>
            </div>
            <div className="form-group full"><label>Times Available</label>
              <div className="pill-group">{TIMES.map(t=><AvailPill key={t} label={t} active={form.times.includes(t)} onClick={()=>toggleF("times",t)}/>)}</div>
            </div>
          </div>
          <button className="submit-btn" style={{width:"100%",marginTop:"28px"}} onClick={handleSubmit}>Submit Registration →</button>
        </div>
      </div>

      {/* NTRP Info Modal */}
      {showNtrpInfo&&(
        <div className="modal-overlay" onClick={()=>setShowNtrpInfo(false)}>
          <div className="modal-box" onClick={e=>e.stopPropagation()} style={{maxWidth:"480px"}}>
            <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"22px",letterSpacing:"2px",marginBottom:"14px"}}>What is an NTRP Rating?</div>
            <p style={{fontSize:"14px",lineHeight:"1.7",color:"var(--charcoal)",marginBottom:"12px"}}>
              The <strong>National Tennis Rating Program (NTRP)</strong> is the official USTA rating system that classifies players from <strong>1.0 (beginner)</strong> to <strong>7.0 (touring professional)</strong>. Most recreational players fall between 2.5 and 5.0.
            </p>
            <div style={{background:"var(--cream-dark)",borderRadius:"4px",padding:"12px 16px",fontSize:"13px",lineHeight:"1.8",marginBottom:"16px"}}>
              <div><strong>2.0–2.5</strong> — Beginner, learning basic strokes</div>
              <div><strong>3.0</strong> — Can sustain rallies, developing consistency</div>
              <div><strong>3.5</strong> — More reliable strokes, starting to direct the ball</div>
              <div><strong>4.0</strong> — Dependable strokes, plays competitive recreational tennis</div>
              <div><strong>4.5</strong> — Strong all-around game, good shot anticipation</div>
              <div><strong>5.0+</strong> — Tournament-level competitive player</div>
            </div>
            <p style={{fontSize:"12px",color:"var(--grey)",marginBottom:"16px"}}>Not sure of your rating? Choose your best estimate — you can always update it later.</p>
            <div style={{display:"flex",gap:"10px"}}>
              <a href="https://www.usta.com/en/home/improve/tips-and-instruction/national/ntrp-general-guidelines.html" target="_blank" rel="noreferrer"
                style={{flex:1,padding:"10px",background:"var(--red)",color:"white",border:"none",borderRadius:"3px",
                  fontSize:"13px",fontWeight:"600",cursor:"pointer",textAlign:"center",textDecoration:"none",fontFamily:"'DM Sans',sans-serif"}}>
                View Full USTA Guidelines ↗
              </a>
              <button onClick={()=>setShowNtrpInfo(false)}
                style={{padding:"10px 20px",background:"var(--charcoal)",color:"white",border:"none",borderRadius:"3px",
                  fontSize:"13px",fontWeight:"600",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Terms & Conditions Modal */}
      {showTnC&&(
        <div className="modal-overlay">
          <div className="modal-box" onClick={e=>e.stopPropagation()} style={{maxWidth:"520px"}}>
            <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"22px",letterSpacing:"2px",marginBottom:"4px"}}>Terms & Community Guidelines</div>
            <div style={{fontSize:"12px",color:"var(--grey)",marginBottom:"16px"}}>Please read and accept before completing your registration.</div>
            <div style={{background:"var(--cream-dark)",borderRadius:"4px",padding:"16px",fontSize:"13px",lineHeight:"1.8",
              maxHeight:"320px",overflowY:"auto",marginBottom:"16px",color:"var(--charcoal)"}}>
              <p style={{marginBottom:"10px"}}><strong>Age Requirement:</strong> Columbus Tennis Club is open to members who are 18 years of age or older. By registering, you confirm that you meet this requirement.</p>
              <p style={{marginBottom:"10px"}}><strong>Respectful Communication:</strong> Members agree to communicate respectfully at all times. Inappropriate, offensive, or abusive language in posts, messages, or listings is strictly prohibited and may result in account suspension.</p>
              <p style={{marginBottom:"10px"}}><strong>Intended Use:</strong> This platform is exclusively for tennis-related community activity, including finding playing partners, posting match availability, listing equipment, and connecting with instructors and stringers in the Columbus, Indiana area. Use of the site for commercial solicitation, spam, or any purpose unrelated to tennis is not permitted.</p>
              <p style={{marginBottom:"10px"}}><strong>Accurate Information:</strong> Members agree to provide truthful and accurate information in their profiles and posts. Misrepresentation of skill level, contact information, or any other details is prohibited.</p>
              <p style={{marginBottom:"10px"}}><strong>Privacy & Contact Info:</strong> Contact information shared through CTC Connections is provided voluntarily and should be used solely to arrange tennis matches or activities. Sharing or misusing another member's contact information is strictly prohibited.</p>
              <p style={{marginBottom:"10px"}}><strong>Equipment Exchange:</strong> CTC is not responsible for transactions made through the Equipment Exchange. Members participate at their own discretion. Listings must be for legitimate tennis equipment only.</p>
              <p style={{marginBottom:"0"}}><strong>Enforcement:</strong> Columbus Tennis Club administrators reserve the right to remove any content, decline or revoke membership, or take other appropriate action for violations of these guidelines. We're here to build a friendly, welcoming tennis community — please help us keep it that way!</p>
            </div>
            <div style={{display:"flex",gap:"10px"}}>
              <button onClick={handleAcceptTnC}
                style={{flex:1,padding:"12px",background:"var(--red)",color:"white",border:"none",borderRadius:"3px",
                  fontSize:"14px",fontWeight:"700",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                I Agree & Submit Registration
              </button>
              <button onClick={()=>setShowTnC(false)}
                style={{padding:"12px 20px",background:"transparent",color:"var(--charcoal)",border:"1.5px solid var(--grey-light)",
                  borderRadius:"3px",fontSize:"13px",fontWeight:"600",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccess&&(
        <div className="modal-overlay">
          <div className="modal-box" onClick={e=>e.stopPropagation()} style={{maxWidth:"460px",textAlign:"center"}}>
            <TennisBall size={56}/>
            <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"28px",letterSpacing:"2px",margin:"16px 0 8px"}}>Registration Submitted!</div>
            <p style={{fontSize:"14px",color:"var(--charcoal)",lineHeight:"1.7",marginBottom:"10px"}}>
              Thank you for joining Columbus Tennis Club! Your registration has been received and is pending admin review.
            </p>
            <div style={{background:"var(--cream-dark)",borderRadius:"4px",padding:"12px 16px",fontSize:"13px",
              color:"var(--grey)",lineHeight:"1.6",marginBottom:"20px"}}>
              ⏱ Please allow <strong>up to 72 hours</strong> for your account to be approved. You'll receive an email confirmation once your membership is active.
            </div>
            <button onClick={()=>{ setShowSuccess(false); nav(VIEWS.HOME); }}
              style={{width:"100%",padding:"12px",background:"var(--red)",color:"white",border:"none",borderRadius:"3px",
                fontSize:"14px",fontWeight:"700",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
              Back to Home →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── MEMBERS TAB (standalone to preserve deactivated state) ────────────────
function MembersTab({ showToast, RatingBadge }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from("profiles").select("*").eq("is_approved", true).order("name")
      .then(({ data, error }) => {
        if (!error && data) setMembers(data);
        setLoading(false);
      });
  }, []);

  const toggle = async (p) => {
    const newActive = !p.is_active;
    const { error } = await supabase.from("profiles").update({ is_active: newActive }).eq("id", p.id);
    if (error) { showToast("Error updating member.", "error"); return; }
    setMembers(prev => prev.map(m => m.id === p.id ? { ...m, is_active: newActive } : m));
    showToast(newActive ? `✅ ${p.name} restored.` : `${p.name} deactivated.`, newActive ? undefined : "error");
  };

  const toggleAdmin = async (p) => {
    const newAdmin = !p.is_admin;
    const { error } = await supabase.from("profiles").update({ is_admin: newAdmin }).eq("id", p.id);
    if (error) { showToast("Error updating admin status.", "error"); return; }
    setMembers(prev => prev.map(m => m.id === p.id ? { ...m, is_admin: newAdmin } : m));
    showToast(newAdmin ? `✅ ${p.name} is now an admin.` : `${p.name} admin access removed.`, newAdmin ? undefined : "error");
  };

  if (loading) return <div style={{color:"var(--grey)",padding:"20px"}}>Loading members...</div>;
  if (!members.length) return <div style={{color:"var(--grey)",padding:"20px"}}>No approved members yet.</div>;

  return (
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:"14px"}}>
      {members.map(p => {
        const inactive = !p.is_active;
        const initials = p.name?.split(" ").map(n=>n[0]).join("").toUpperCase() || "?";
        return (
          <div key={p.id} style={{background:"white",borderRadius:"6px",padding:"16px",
            boxShadow:"var(--shadow-sm)",borderTop:`3px solid ${inactive?"var(--grey-light)":"var(--charcoal)"}`,
            opacity: inactive ? 0.6 : 1}}>
            <div className="player-top">
              <div className="player-avatar" style={{background: inactive ? "var(--grey)" : undefined}}>
                {initials}
              </div>
              <div>
                <div className="player-name">{p.name}</div>
                {inactive
                  ? <span style={{fontSize:"11px",color:"var(--grey)",fontWeight:"600",letterSpacing:"1px"}}>DEACTIVATED</span>
                  : <RatingBadge ntrp={p.ntrp} skill={p.skill}/>}
              </div>
            </div>
            {p.is_admin && (
              <div style={{marginTop:"8px"}}>
                <span style={{fontSize:"10px",fontWeight:"700",letterSpacing:"1.5px",
                  color:"var(--red)",textTransform:"uppercase"}}>⚙️ Admin</span>
              </div>
            )}
            <div style={{display:"flex",gap:"8px",marginTop:"10px",flexWrap:"wrap"}}>
              {!inactive && (
                <button onClick={async()=>{
                  const { error } = await supabase.auth.resetPasswordForEmail(p.email, {
                    redirectTo: window.location.origin,
                  });
                  if (error) { showToast(`Error sending reset: ${error.message}`, "error"); return; }
                  showToast(`✅ Password reset email sent to ${p.name}.`);
                }}
                  style={{flex:1,padding:"7px",background:"transparent",border:"1.5px solid var(--grey-light)",
                    borderRadius:"3px",fontSize:"12px",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontWeight:"600"}}>
                  Reset PW
                </button>
              )}
              <button onClick={()=>toggle(p)}
                style={{flex: inactive ? undefined : 1, padding:"7px 14px",background:"transparent",
                  border:`1.5px solid ${inactive?"#2A6E2A":"var(--red)"}`,
                  color: inactive ? "#2A6E2A" : "var(--red)",
                  borderRadius:"3px",fontSize:"12px",cursor:"pointer",
                  fontFamily:"'DM Sans',sans-serif",fontWeight:"600"}}>
                {inactive ? "Restore" : "Deactivate"}
              </button>
            </div>
            {!inactive && (
              <div style={{marginTop:"6px"}}>
                <button onClick={()=>toggleAdmin(p)}
                  style={{width:"100%",padding:"6px",background:p.is_admin?"#fff3f3":"transparent",
                    border:`1.5px solid ${p.is_admin?"var(--red)":"var(--grey-light)"}`,
                    color:p.is_admin?"var(--red)":"var(--grey)",
                    borderRadius:"3px",fontSize:"11px",cursor:"pointer",
                    fontFamily:"'DM Sans',sans-serif",fontWeight:"700",letterSpacing:"0.5px"}}>
                  {p.is_admin ? "Remove Admin Access" : "Grant Admin Access"}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── ADMIN VIEW (defined outside App to preserve tab state) ────────────────
function AdminView({ nav, showToast, VIEWS, adminQueue, setAdminQueue,
  instructors, setInstructors, stringers, setStringers,
  bulletinPosts, setBulletinPosts, exchangeListings, setExchangeListings,
  LESSON_TYPES, toggleArr, RatingBadge, TennisBall, MembersTab, onRefreshMembers }) {

  const [tab, setTab] = useState("queue");
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({});
  const tabs=[
    {id:"queue",label:"Approval Queue",badge:adminQueue.length},
    {id:"members",label:"Members"},
    {id:"instructors",label:"Instructor Listings"},
    {id:"stringers",label:"Stringing Listings"},
    {id:"moderation",label:"Content Moderation"},
  ];

  const registrationQueue = adminQueue.filter(u=>!u.type||u.type==="registration");
  const otherQueue = adminQueue.filter(u=>u.type&&u.type!=="registration");

  const openEdit = (type, record) => { setEditTarget({type, record}); setEditForm({...record}); };
  const saveEdit = async () => {
    if(editTarget.type==="instructor") {
      const { error } = await supabase.from("instructors").update({
        name: editForm.name, email: editForm.email, phone: editForm.phone,
        lesson_types: editForm.lessonTypes, notes: editForm.notes,
      }).eq("id", editForm.id);
      if(error) { showToast("Error saving changes.","error"); return; }
      setInstructors(prev=>prev.map(i=>i.id===editForm.id?{...editForm}:i));
    }
    if(editTarget.type==="stringer") {
      const { error } = await supabase.from("stringers").update({
        name: editForm.name, email: editForm.email, phone: editForm.phone,
        notes: editForm.notes,
      }).eq("id", editForm.id);
      if(error) { showToast("Error saving changes.","error"); return; }
      setStringers(prev=>prev.map(s=>s.id===editForm.id?{...editForm}:s));
    }
    showToast(`✅ ${editForm.name} updated.`);
    setEditTarget(null);
  };

  return (
    <div className="page-wrap">
      <div className="page-header">
        <button className="page-back" onClick={()=>nav(VIEWS.HOME)}>← Back to Home</button>
        <div className="page-title">⚙️ Admin Dashboard</div>
        <div className="page-sub">Columbus Tennis Club · Content & Member Management</div>
        <div className="red-bar"/>
      </div>
      <div className="admin-tabs">
        {tabs.map(t=>(
          <button key={t.id} className={`admin-tab${tab===t.id?" active":""}`} onClick={()=>setTab(t.id)}>
            {t.label}{t.badge>0&&<span className="badge">{t.badge}</span>}
          </button>
        ))}
      </div>

      {tab==="queue"&&(
        <>
          {adminQueue.length===0&&<div style={{background:"white",borderRadius:"6px",padding:"28px",textAlign:"center",color:"var(--grey)"}}>✅ No pending approvals.</div>}
          {registrationQueue.length>0&&(
            <>
              <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"16px",letterSpacing:"3px",color:"var(--grey)",
                margin:"0 0 12px",paddingBottom:"6px",borderBottom:"2px solid var(--grey-light)"}}>
                👤 MEMBER REGISTRATIONS ({registrationQueue.length})
              </div>
              {registrationQueue.map(u=>(
                <div className="queue-card" key={u.id} style={{marginBottom:"12px"}}>
                  <div className="queue-info" style={{flex:1}}>
                    <h3 style={{marginBottom:"4px"}}>{u.name}</h3>
                    <p style={{fontSize:"13px",color:"var(--grey)",marginBottom:"6px"}}>✉️ {u.email} · 📞 {u.phone}</p>
                    <div style={{display:"flex",flexWrap:"wrap",gap:"6px",alignItems:"center",marginBottom:"6px"}}>
                      <RatingBadge ntrp={u.ntrp} skill={u.skill}/>
                      {u.play&&<span style={{fontSize:"12px",background:"var(--cream-dark)",padding:"2px 8px",borderRadius:"10px"}}>{u.play}</span>}
                      {u.gender&&<span style={{fontSize:"12px",background:"var(--cream-dark)",padding:"2px 8px",borderRadius:"10px"}}>{u.gender}</span>}
                      {u.ageRange&&<span style={{fontSize:"12px",background:"var(--cream-dark)",padding:"2px 8px",borderRadius:"10px"}}>{u.ageRange}</span>}
                    </div>
                    {u.days&&u.days.length>0&&<div style={{display:"flex",flexWrap:"wrap",gap:"4px",marginBottom:"4px"}}>{u.days.map(d=><span key={d} className="avail-chip">{d}</span>)}</div>}
                    {u.times&&u.times.length>0&&<div style={{display:"flex",flexWrap:"wrap",gap:"4px",marginBottom:"6px"}}>{u.times.map(t=><span key={t} className="avail-chip avail-chip-time">{t}</span>)}</div>}
                    <div style={{fontSize:"11px",color:"var(--grey)"}}>Submitted {u.submitted}</div>
                  </div>
                  <div className="queue-actions">
                    <button className="btn-approve" onClick={async ()=>{
                      await supabase.from("profiles").update({is_approved:true}).eq("id",u.id);
                      setAdminQueue(q=>q.filter(x=>x.id!==u.id));
                      showToast(`✅ ${u.name} approved.`);
                      // Reload approvedMembers so Connections updates immediately
                      if (onRefreshMembers) onRefreshMembers();
                      // Notify member of approval (fire and forget)
                      fetch(EDGE_FN_URLS.NOTIFY_APPROVAL, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ name: u.name, email: u.email }),
                      }).catch(() => {});
                    }}>Approve</button>
                    <button className="btn-decline" onClick={async()=>{
                      await supabase.from("profiles").update({is_active:false}).eq("id",u.id);
                      setAdminQueue(q=>q.filter(x=>x.id!==u.id));
                      showToast(`${u.name} declined and deactivated.`,"error");
                    }}>Decline</button>
                  </div>
                </div>
              ))}
            </>
          )}
          {otherQueue.length>0&&(
            <>
              <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"16px",letterSpacing:"3px",color:"var(--grey)",
                margin:"20px 0 12px",paddingBottom:"6px",borderBottom:"2px solid var(--grey-light)"}}>
                📋 OTHER PENDING APPROVALS ({otherQueue.length})
              </div>
              {otherQueue.map(u=>(
                <div className="queue-card" key={`${u.type}-${u.id}`} style={{marginBottom:"12px"}}>
                  <div className="queue-info" style={{flex:1}}>
                    <div style={{fontSize:"10px",fontWeight:"700",letterSpacing:"2px",color:"var(--red)",marginBottom:"4px",textTransform:"uppercase"}}>{u.type}</div>
                    <h3>{u.name}</h3>
                    <p style={{fontSize:"13px",color:"var(--grey)"}}>✉️ {u.email}{u.phone && u.phone !== "—" ? ` · 📞 ${u.phone}` : ""}</p>
                    {u.notes&&<div style={{fontSize:"12px",color:"var(--charcoal)",marginTop:"4px",fontStyle:"italic"}}>"{u.notes}"</div>}
                    {u.lessonTypes&&u.lessonTypes.length>0&&<div style={{display:"flex",flexWrap:"wrap",gap:"4px",marginTop:"6px"}}>{u.lessonTypes.map(t=><span key={t} style={{fontSize:"11px",background:"var(--cream-dark)",padding:"2px 8px",borderRadius:"10px"}}>{t}</span>)}</div>}
                    <div style={{fontSize:"11px",color:"var(--grey)",marginTop:"6px"}}>Submitted {u.submitted}</div>
                  </div>
                  <div className="queue-actions">
                    <button className="btn-approve" onClick={async ()=>{
                      await supabase.from(u.table).update({approved:true}).eq("id",u.id);
                      setAdminQueue(q=>q.filter(x=>!(x.id===u.id && x.type===u.type)));
                      if(u.table==="stringers") setStringers(prev=>[...prev,{id:u.id,name:u.name,email:u.email,phone:u.phone,notes:u.notes,approved:true}]);
                      if(u.table==="instructors") setInstructors(prev=>[...prev,{id:u.id,name:u.name,email:u.email,phone:u.phone,lessonTypes:u.lessonTypes,notes:u.notes,approved:true}]);
                      showToast(`✅ ${u.name} listing approved.`);
                    }}>Approve</button>
                    <button className="btn-decline" onClick={async()=>{
                      await supabase.from(u.table).delete().eq("id",u.id);
                      setAdminQueue(q=>q.filter(x=>!(x.id===u.id && x.type===u.type)));
                      showToast(`${u.name} listing declined and removed.`,"error");
                    }}>Decline</button>
                  </div>
                </div>
              ))}
            </>
          )}
        </>
      )}

      {tab==="members"&&<MembersTab showToast={showToast} RatingBadge={RatingBadge}/>}

      {tab==="instructors"&&(
        <div>
          <p style={{fontSize:"13px",color:"var(--grey)",marginBottom:"16px"}}>Manage instructor listings. Visit the <button onClick={()=>nav(VIEWS.INSTRUCTORS)} style={{background:"none",border:"none",color:"var(--red)",cursor:"pointer",fontWeight:"600",fontSize:"13px",fontFamily:"'DM Sans',sans-serif"}}>Instructors page</button> to add new listings.</p>
          <div className="card-grid">
            {instructors.map(i=>(
              <div className="card" key={i.id}>
                <div className="card-body">
                  <div className="card-name" style={{marginBottom:"6px"}}>{i.name}</div>
                  <div className="lesson-chips">{(i.lessonTypes||[]).map(t=><span key={t} className="lesson-chip">{t}</span>)}</div>
                  <div className="card-contact"><a href={`mailto:${i.email}`}>✉️ {i.email}</a></div>
                  <div style={{display:"flex",gap:"8px",marginTop:"12px"}}>
                    <button onClick={()=>openEdit("instructor",i)}
                      style={{flex:1,padding:"7px",background:"transparent",border:"1.5px solid var(--charcoal)",borderRadius:"3px",fontSize:"12px",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontWeight:"600"}}>Edit Record</button>
                    <button onClick={async()=>{await supabase.from("instructors").delete().eq("id",i.id);setInstructors(prev=>prev.filter(x=>x.id!==i.id));showToast(`${i.name} removed.`,"error");}}
                      style={{flex:1,padding:"7px",background:"transparent",border:"1.5px solid var(--red)",color:"var(--red)",borderRadius:"3px",fontSize:"12px",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontWeight:"600"}}>Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab==="stringers"&&(
        <div>
          <p style={{fontSize:"13px",color:"var(--grey)",marginBottom:"16px"}}>Manage stringer listings. Visit the <button onClick={()=>nav(VIEWS.STRINGING)} style={{background:"none",border:"none",color:"var(--red)",cursor:"pointer",fontWeight:"600",fontSize:"13px",fontFamily:"'DM Sans',sans-serif"}}>Stringing page</button> to add new listings.</p>
          <div className="card-grid">
            {stringers.map(s=>(
              <div className="card card-gold" key={s.id}>
                <div className="card-body">
                  <div className="card-name" style={{marginBottom:"6px"}}>{s.name}</div>
                  <div className="card-contact"><a href={`mailto:${s.email}`}>✉️ {s.email}</a></div>
                  {s.notes&&<div className="card-notes">"{s.notes}"</div>}
                  <div style={{display:"flex",gap:"8px",marginTop:"12px"}}>
                    <button onClick={()=>openEdit("stringer",s)}
                      style={{flex:1,padding:"7px",background:"transparent",border:"1.5px solid var(--charcoal)",borderRadius:"3px",fontSize:"12px",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontWeight:"600"}}>Edit Record</button>
                    <button onClick={async()=>{await supabase.from("stringers").delete().eq("id",s.id);setStringers(prev=>prev.filter(x=>x.id!==s.id));showToast(`${s.name} removed.`,"error");}}
                      style={{flex:1,padding:"7px",background:"transparent",border:"1.5px solid var(--red)",color:"var(--red)",borderRadius:"3px",fontSize:"12px",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontWeight:"600"}}>Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab==="moderation"&&(
        <div>
          <p style={{fontSize:"13px",color:"var(--grey)",marginBottom:"20px"}}>Review and remove inappropriate Match Bulletin Board posts or Equipment Exchange listings.</p>
          <div style={{marginBottom:"28px"}}>
            <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"16px",letterSpacing:"3px",color:"var(--grey)",
              marginBottom:"12px",paddingBottom:"6px",borderBottom:"2px solid var(--grey-light)"}}>
              📋 MATCH BULLETIN BOARD ({bulletinPosts.length} active posts)
            </div>
            {bulletinPosts.length===0&&<div style={{color:"var(--grey)",fontSize:"13px",padding:"12px 0"}}>No active posts.</div>}
            {bulletinPosts.map(p=>(
              <div key={p.id} style={{background:"white",borderRadius:"6px",padding:"14px 18px",marginBottom:"10px",
                boxShadow:"var(--shadow-sm)",display:"flex",justifyContent:"space-between",alignItems:"center",gap:"16px",flexWrap:"wrap"}}>
                <div style={{minWidth:0,flex:"1 1 200px"}}>
                  <div style={{fontWeight:"600",fontSize:"14px",marginBottom:"3px"}}>{p.day}, {p.date} · {p.startTime}–{p.endTime}</div>
                  <div style={{fontSize:"13px",color:"var(--grey)"}}>{p.court} · {p.skill} · Posted by {p.postedBy}</div>
                  {p.note&&<div style={{fontSize:"12px",color:"var(--charcoal)",marginTop:"4px",fontStyle:"italic"}}>"{p.note}"</div>}
                </div>
                <button onClick={async()=>{await supabase.from("bulletin_posts").delete().eq("id",p.id);setBulletinPosts(prev=>prev.filter(x=>x.id!==p.id));showToast("Post removed by admin.","error");}}
                  style={{flexShrink:0,padding:"7px 14px",background:"transparent",border:"1.5px solid var(--red)",color:"var(--red)",
                    borderRadius:"3px",fontSize:"12px",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontWeight:"600",whiteSpace:"nowrap"}}>
                  Remove Post
                </button>
              </div>
            ))}
          </div>
          <div>
            <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"16px",letterSpacing:"3px",color:"var(--grey)",
              marginBottom:"12px",paddingBottom:"6px",borderBottom:"2px solid var(--grey-light)"}}>
              🛒 EQUIPMENT EXCHANGE ({exchangeListings.filter(l=>!l.sold).length} active listings)
            </div>
            {exchangeListings.filter(l=>!l.sold).length===0&&<div style={{color:"var(--grey)",fontSize:"13px",padding:"12px 0"}}>No active listings.</div>}
            {exchangeListings.filter(l=>!l.sold).map(l=>(
              <div key={l.id} style={{background:"white",borderRadius:"6px",padding:"14px 18px",marginBottom:"10px",
                boxShadow:"var(--shadow-sm)",display:"flex",justifyContent:"space-between",alignItems:"center",gap:"16px",flexWrap:"wrap"}}>
                <div style={{minWidth:0,flex:"1 1 200px"}}>
                  <div style={{fontWeight:"600",fontSize:"14px",marginBottom:"3px"}}>{l.title}</div>
                  <div style={{fontSize:"13px",color:"var(--grey)"}}>{l.category} · {l.listingType}{l.price?` · $${l.price}`:""} · {l.postedBy}</div>
                  {l.description&&<div style={{fontSize:"12px",color:"var(--charcoal)",marginTop:"4px",fontStyle:"italic"}}>"{l.description.slice(0,80)}{l.description.length>80?"…":""}"</div>}
                </div>
                <button onClick={()=>{setExchangeListings(prev=>prev.filter(x=>x.id!==l.id));showToast("Listing removed by admin.","error");}}
                  style={{flexShrink:0,padding:"7px 14px",background:"transparent",border:"1.5px solid var(--red)",color:"var(--red)",
                    borderRadius:"3px",fontSize:"12px",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontWeight:"600",whiteSpace:"nowrap"}}>
                  Remove Listing
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Edit Record Modal */}
      {editTarget&&(
        <div className="modal-overlay" onClick={()=>setEditTarget(null)}>
          <div className="modal-box" onClick={e=>e.stopPropagation()} style={{maxWidth:"520px"}}>
            <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"20px",letterSpacing:"2px",marginBottom:"16px"}}>
              Edit {editTarget.type==="instructor"?"Instructor":"Stringer"} Record
            </div>
            <div className="form-grid">
              <div className="form-group full"><label>Name *</label>
                <input value={editForm.name||""} onChange={e=>setEditForm(f=>({...f,name:e.target.value}))}/>
              </div>
              <div className="form-group full"><label>Email</label>
                <input value={editForm.email||""} onChange={e=>setEditForm(f=>({...f,email:e.target.value}))}/>
              </div>
              <div className="form-group full"><label>Phone</label>
                <input value={editForm.phone||""} onChange={e=>setEditForm(f=>({...f,phone:e.target.value}))} placeholder="812-555-0000"/>
              </div>
              {editTarget.type==="instructor"&&(
                <div className="form-group full"><label>Lesson Types</label>
                  <div className="pill-group">
                    {LESSON_TYPES.map(t=>(
                      <button key={t} onClick={()=>setEditForm(f=>({...f,lessonTypes:toggleArr(f.lessonTypes||[],t)}))}
                        style={{padding:"6px 14px",borderRadius:"20px",border:"1.5px solid",fontSize:"13px",cursor:"pointer",
                          fontFamily:"'DM Sans',sans-serif",fontWeight:"600",
                          background:(editForm.lessonTypes||[]).includes(t)?"var(--charcoal)":"transparent",
                          color:(editForm.lessonTypes||[]).includes(t)?"white":"var(--charcoal)",
                          borderColor:(editForm.lessonTypes||[]).includes(t)?"var(--charcoal)":"var(--grey-light)"}}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="form-group full"><label>{editTarget.type==="instructor"?"Bio / Notes":"Service Notes"}</label>
                <textarea value={editForm.notes||""} onChange={e=>setEditForm(f=>({...f,notes:e.target.value}))}
                  rows={3} style={{width:"100%",padding:"10px",border:"1.5px solid var(--grey-light)",borderRadius:"4px",
                    fontFamily:"'DM Sans',sans-serif",fontSize:"14px",resize:"vertical",boxSizing:"border-box"}}
                  placeholder={editTarget.type==="instructor"?"Background, availability, certifications…":"Turnaround time, brands carried, pricing…"}/>
              </div>
              <div className="form-group"><label>Approved</label>
                <select value={editForm.approved?"yes":"no"} onChange={e=>setEditForm(f=>({...f,approved:e.target.value==="yes"}))}>
                  <option value="yes">Yes — visible on site</option>
                  <option value="no">No — hidden from site</option>
                </select>
              </div>
            </div>
            <div style={{display:"flex",gap:"10px",marginTop:"16px"}}>
              <button onClick={saveEdit}
                style={{flex:1,padding:"10px",background:"var(--red)",color:"white",border:"none",borderRadius:"3px",
                  fontSize:"13px",fontWeight:"700",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>Save Changes</button>
              <button onClick={()=>setEditTarget(null)}
                style={{padding:"10px 20px",background:"transparent",color:"var(--charcoal)",border:"1.5px solid var(--grey-light)",
                  borderRadius:"3px",fontSize:"13px",fontWeight:"600",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── TERMS & COMMUNITY GUIDELINES ─────────────────────────────────────────
function TermsView({ nav, VIEWS }) {
  const Section = ({ title, children }) => (
    <div style={{marginBottom:"24px"}}>
      <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"16px",letterSpacing:"2px",
        color:"var(--charcoal)",marginBottom:"8px",paddingBottom:"6px",
        borderBottom:"2px solid var(--grey-light)"}}>{title}</div>
      <div style={{fontSize:"14px",lineHeight:"1.8",color:"var(--charcoal)"}}>{children}</div>
    </div>
  );
  return (
    <div className="page-wrap">
      <div className="page-header">
        <button className="page-back" onClick={()=>nav(VIEWS.HOME)}>← Back to Home</button>
        <div className="page-title">Terms &amp; Community Guidelines</div>
        <div className="page-sub">Columbus Tennis Club · Columbus, Indiana · Last updated March 2026</div>
        <div className="red-bar"/>
      </div>
      <div style={{background:"white",borderRadius:"6px",padding:"32px",boxShadow:"var(--shadow-sm)",maxWidth:"760px",margin:"0 auto"}}>
        <Section title="Who We Are">
          Columbus Tennis Club (CTC) is a free community hub for tennis players in Columbus, Indiana and the surrounding area. Our platform connects players, lists local courts, instructors, and stringers, and provides community features for active members.
        </Section>
        <Section title="Age Requirement">
          CTC membership is open to individuals 18 years of age or older. By registering, you confirm that you meet this requirement.
        </Section>
        <Section title="Membership & Account Approval">
          New member registrations are reviewed and approved by CTC administrators. We reserve the right to decline any registration at our discretion. Membership is free and intended for players in the Columbus, Indiana area and surrounding communities.
        </Section>
        <Section title="Respectful Communication">
          Members agree to communicate respectfully at all times. Offensive, abusive, or harassing language in posts, messages, or listings is strictly prohibited and may result in immediate account suspension.
        </Section>
        <Section title="Intended Use">
          This platform is exclusively for tennis-related community activity — finding playing partners, posting match availability, listing equipment, and connecting with local instructors and stringers. Use of the site for commercial solicitation, spam, or any purpose unrelated to tennis is not permitted.
        </Section>
        <Section title="Accurate Information">
          Members agree to provide truthful and accurate information in their profiles and posts. Misrepresentation of skill level, contact details, or any other information is prohibited.
        </Section>
        <Section title="Contact Information & Privacy">
          Contact information shared through CTC Connections is provided voluntarily and must be used solely to arrange tennis-related activities. Sharing or misusing another member's contact information is strictly prohibited.
        </Section>
        <Section title="Equipment Exchange">
          CTC facilitates — but is not a party to — transactions made through the Equipment Exchange. Members participate at their own discretion. CTC is not responsible for the accuracy of listings, the condition of items, or the outcome of any transaction. Listings must be for legitimate tennis equipment only.
        </Section>
        <Section title="Content Moderation">
          CTC administrators reserve the right to remove any content, decline or revoke membership, or take other appropriate action for violations of these guidelines at their sole discretion.
        </Section>
        <Section title="Changes to These Terms">
          We may update these guidelines from time to time. Continued use of the platform following any changes constitutes acceptance of the revised terms.
        </Section>
        <div style={{fontSize:"13px",color:"var(--grey)",marginTop:"12px"}}>
          Questions? Contact us at <a href="mailto:admin@columbustennis.com" style={{color:"var(--red)"}}>admin@columbustennis.com</a>
        </div>
      </div>
    </div>
  );
}

// ── PRIVACY POLICY ────────────────────────────────────────────────────────
function PrivacyView({ nav, VIEWS }) {
  const Section = ({ title, children }) => (
    <div style={{marginBottom:"24px"}}>
      <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"16px",letterSpacing:"2px",
        color:"var(--charcoal)",marginBottom:"8px",paddingBottom:"6px",
        borderBottom:"2px solid var(--grey-light)"}}>{title}</div>
      <div style={{fontSize:"14px",lineHeight:"1.8",color:"var(--charcoal)"}}>{children}</div>
    </div>
  );
  return (
    <div className="page-wrap">
      <div className="page-header">
        <button className="page-back" onClick={()=>nav(VIEWS.HOME)}>← Back to Home</button>
        <div className="page-title">Privacy Policy</div>
        <div className="page-sub">Columbus Tennis Club · Columbus, Indiana · Last updated March 2026</div>
        <div className="red-bar"/>
      </div>
      <div style={{background:"white",borderRadius:"6px",padding:"32px",boxShadow:"var(--shadow-sm)",maxWidth:"760px",margin:"0 auto"}}>
        <Section title="Information We Collect">
          When you register as a CTC member, we collect your name, email address, phone number, gender, age range, tennis skill level, and availability preferences. This information is used to facilitate connections between local tennis players.
        </Section>
        <Section title="How We Use Your Information">
          Your profile information is displayed to other approved CTC members in the Connections directory to help match you with compatible playing partners. Your contact information (email and phone) is only shared with another member when both parties have mutually agreed to connect through the contact request system.
        </Section>
        <Section title="Information You Post">
          Content you post publicly on the Match Bulletin Board or Equipment Exchange is visible to all logged-in CTC members. Please do not include sensitive personal information in public posts.
        </Section>
        <Section title="Data Storage">
          Your information is stored securely using industry-standard practices. We do not sell, rent, or share your personal information with third parties for marketing purposes.
        </Section>
        <Section title="Cookies & Analytics">
          CTC may use basic cookies and analytics tools to understand how the site is used and to improve the member experience. We do not use advertising cookies or tracking pixels.
        </Section>
        <Section title="Account Deletion">
          Members may request deletion of their account and associated data at any time by contacting the CTC admin team. Deactivated accounts are retained for a reasonable period before permanent deletion to maintain the integrity of community activity records.
        </Section>
        <Section title="Children's Privacy">
          CTC is not intended for individuals under 18 years of age. We do not knowingly collect information from minors.
        </Section>
        <Section title="Contact">
          For questions or concerns about your privacy or data, contact us at <a href="mailto:admin@columbustennis.com" style={{color:"var(--red)"}}>admin@columbustennis.com</a>
        </Section>
        <Section title="Changes to This Policy">
          We may update this Privacy Policy from time to time. Continued use of the platform constitutes acceptance of any revised policy.
        </Section>
      </div>
    </div>
  );
}

// ── RESET PASSWORD VIEW (defined outside App to prevent remounting) ──────
function ResetPasswordView({ nav, showToast, VIEWS }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const handleReset = async () => {
    if (!pw || pw.length < 8) { showToast("Password must be at least 8 characters.", "error"); return; }
    if (pw !== pw2) { showToast("Passwords do not match.", "error"); return; }
    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password: pw });
    setSaving(false);
    if (error) { showToast("Error updating password: " + error.message, "error"); return; }
    setDone(true);
  };

  return (
    <div className="page-wrap" style={{maxWidth:480,margin:"0 auto",padding:"60px 24px"}}>
      <div className="page-header">
        <div className="page-title">Reset Password</div>
        <div className="red-bar"/>
      </div>
      {done ? (
        <div style={{textAlign:"center",padding:"40px 0"}}>
          <div style={{fontSize:"48px",marginBottom:"16px"}}>✅</div>
          <div style={{fontSize:"18px",fontWeight:"700",marginBottom:"8px"}}>Password updated!</div>
          <div style={{color:"var(--grey)",marginBottom:"32px"}}>You can now sign in with your new password.</div>
          <button className="btn-primary" onClick={() => nav(VIEWS.LOGIN)}>Go to Sign In</button>
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:"16px",marginTop:"32px"}}>
          <div>
            <label style={{display:"block",fontWeight:"600",marginBottom:"6px",fontSize:"13px"}}>New Password</label>
            <input type="password" defaultValue={pw}
              onChange={e => setPw(e.target.value)}
              placeholder="At least 8 characters"
              style={{width:"100%",padding:"10px 12px",border:"1px solid #ccc",borderRadius:"6px",fontSize:"15px",boxSizing:"border-box"}}/>
          </div>
          <div>
            <label style={{display:"block",fontWeight:"600",marginBottom:"6px",fontSize:"13px"}}>Confirm New Password</label>
            <input type="password" defaultValue={pw2}
              onChange={e => setPw2(e.target.value)}
              placeholder="Re-enter password"
              style={{width:"100%",padding:"10px 12px",border:"1px solid #ccc",borderRadius:"6px",fontSize:"15px",boxSizing:"border-box"}}/>
          </div>
          <button className="btn-primary" onClick={handleReset} disabled={saving}
            style={{marginTop:"8px"}}>
            {saving ? "Saving…" : "Set New Password"}
          </button>
        </div>
      )}
    </div>
  );
}

// ── CONTACT US VIEW ──────────────────────────────────────────────────────
function ContactUsView({ nav, showToast, VIEWS }) {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [honeypot, setHoneypot] = useState(""); // anti-spam honeypot field
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [submitTime] = useState(Date.now()); // anti-spam: track form load time
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    // Anti-spam: reject if honeypot filled
    if (honeypot) { showToast("Submission blocked.", "error"); return; }
    // Anti-spam: reject if submitted too fast (< 3 seconds)
    if (Date.now() - submitTime < 3000) { showToast("Please wait a moment before submitting.", "error"); return; }
    if (!form.name || !form.email || !form.message) {
      showToast("Please fill in all fields.", "error"); return;
    }
    // Basic email format check
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      showToast("Please enter a valid email address.", "error"); return;
    }
    setSending(true);
    try {
      await fetch(EDGE_FN_URLS.NOTIFY_CONTACT_US, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: form.name, email: form.email, message: form.message }),
      });
      setSent(true);
    } catch {
      showToast("Error sending message. Please try again.", "error");
    }
    setSending(false);
  };

  return (
    <div className="page-wrap" style={{ maxWidth: 580, margin: "0 auto" }}>
      <div className="page-header">
        <button className="page-back" onClick={() => nav(VIEWS.HOME)}>← Back to Home</button>
        <div className="page-title">Contact Us</div>
        <div className="page-sub">Columbus Tennis Club · Questions, feedback, or issues</div>
        <div className="red-bar" />
      </div>
      {sent ? (
        <div style={{ textAlign: "center", padding: "48px 20px" }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>✅</div>
          <div style={{ fontSize: "18px", fontWeight: "700", marginBottom: "8px" }}>Message Sent!</div>
          <div style={{ color: "var(--grey)", fontSize: "14px", lineHeight: "1.6", marginBottom: "28px" }}>
            Thank you for reaching out. A CTC administrator will review your message and get back to you as soon as possible.
          </div>
          <button className="btn-primary" onClick={() => nav(VIEWS.HOME)}>Back to Home</button>
        </div>
      ) : (
        <div style={{ background: "white", borderRadius: "6px", padding: "28px", boxShadow: "var(--shadow-sm)" }}>
          <div style={{ fontSize: "14px", color: "var(--charcoal)", lineHeight: "1.6", marginBottom: "20px" }}>
            Have a question about CTC, need help with your account, or want to report an issue?
            Fill out the form below and a site administrator will follow up with you.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            <div className="form-group">
              <label>Your Name *</label>
              <input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Jane Smith" />
            </div>
            <div className="form-group">
              <label>Your Email *</label>
              <input type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="jane@email.com" />
            </div>
            {/* Honeypot — hidden from real users, bots will fill it */}
            <div style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, overflow: "hidden" }} aria-hidden="true">
              <label>Leave blank</label>
              <input type="text" value={honeypot} onChange={e => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off" />
            </div>
            <div className="form-group">
              <label>Message *</label>
              <textarea value={form.message} onChange={e => set("message", e.target.value)}
                placeholder="Describe your question or issue..."
                style={{ minHeight: "120px", resize: "vertical" }} />
            </div>
            <button className="submit-btn" onClick={handleSubmit} disabled={sending}>
              {sending ? "Sending…" : "Send Message →"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function App() {
  const [view, setView] = useState(VIEWS.HOME);
  const [loggedIn, setLoggedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);       // Supabase auth user
  const [currentProfile, setCurrentProfile] = useState(null); // CTC profile row
  const [toast, setToast] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactReq, setContactReq] = useState(null);
  const [adminQueue, setAdminQueue] = useState([]);
  const [posts, setPosts] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [stringers, setStringers] = useState([]);
  const [filterSkill, setFilterSkill] = useState("All");
  const [filterDays, setFilterDays] = useState([]);
  const [filterTimes, setFilterTimes] = useState([]);
  const [filterPlay, setFilterPlay] = useState("All");
  const [filterConnection, setFilterConnection] = useState("All");
  const [bulletinPosts, setBulletinPosts] = useState([]);

  // Load bulletin posts from Supabase
  useEffect(() => {
    supabase.from("bulletin_posts").select("*").order("created_at", { ascending: false })
      .then(({ data, error }) => {
        if (!error && data) {
          setBulletinPosts(data.map(r => ({
            id: r.id,
            author: r.author_name,
            authorId: r.author_id,
            postedDate: r.created_at,
            day: r.day_label,
            date: r.play_date,
            timeStart: r.time_start,
            timeEnd: r.time_end,
            court: r.court,
            skillPref: r.skill_pref,
            playType: r.play_type,
            note: r.note,
            interestedUsers: r.interested || [],
          })));
        }
      });
  }, []);
  const [exchangeListings, setExchangeListings] = useState([]);

  // Load exchange listings from Supabase
  useEffect(() => {
    supabase.from("exchange_listings").select("*").order("created_at", { ascending: false })
      .then(({ data, error }) => {
        if (!error && data) {
          setExchangeListings(data.map(r => ({
            id: r.id,
            author: r.author_name,
            authorId: r.author_id,
            postedDate: r.created_at,
            title: r.title,
            category: r.category,
            listingType: r.listing_type,
            price: r.price,
            description: r.description,
            contact: r.contact,
            sold: r.sold,
          })));
        }
      });
  }, []);
  const [contactReqs, setContactReqs] = useState([]);
  const [sentRequests, setSentRequests] = useState([]); // requests current user has sent
  const [approvedMembers, setApprovedMembers] = useState([]);
  const [courtNames, setCourtNames] = useState([]);
  const dashBulletinEditTarget = useRef(null);
  const dashExchangeEditTarget = useRef(null);

  const showToast = (msg, type="success") => { setToast({msg,type}); setTimeout(()=>setToast(null),3200); };
  const toggleArr = (arr, val) => arr.includes(val) ? arr.filter(x=>x!==val) : [...arr,val];

  // ── SUPABASE AUTH LISTENER ───────────────────────────────────────────────
  useEffect(() => {
    // Listen for PASSWORD_RECOVERY event — Supabase fires this after consuming
    // the recovery token from the URL hash before our code can read it.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setView(VIEWS.RESET_PASSWORD);
      }
    });

    // Session restore on page load only
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        const { data: profile } = await supabase
          .from("profiles").select("*").eq("id", session.user.id).single();
        if (profile && profile.is_approved && profile.is_active) {
          setCurrentUser(session.user);
          setCurrentProfile(profile);
          setLoggedIn(true);
          setIsAdmin(profile.is_admin || false);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── SIGN OUT HANDLER ─────────────────────────────────────────────────────
  const handleSignOut = () => {
    Object.keys(localStorage).filter(k => k.startsWith('sb-')).forEach(k => localStorage.removeItem(k));
    window.location.href = '/';
  };

  // ── LOGIN HANDLER ────────────────────────────────────────────────────────
  const handleAppLogin = async (emailVal, passVal) => {
    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({ email: emailVal, password: passVal });
      if (authError) return { error: "Invalid email or password. Please try again." };
      if (!data?.user) return { error: "Login failed — please try again." };

      const { data: profile, error: profileError } = await supabase
        .from("profiles").select("*").eq("id", data.user.id).single();

      if (profileError || !profile) return { error: "Could not load your profile. Please try again." };
      if (!profile.is_approved || !profile.is_active) {
        await supabase.auth.signOut();
        return { error: "Your account is pending admin approval." };
      }

      const targetView = profile.is_admin ? VIEWS.ADMIN : VIEWS.DASHBOARD;
      setCurrentUser(data.user);
      setCurrentProfile(profile);
      setLoggedIn(true);
      setIsAdmin(profile.is_admin || false);
      setView(targetView);
      window.scrollTo(0, 0);
      return { success: true };
    } catch (e) {
      // login exception handled by returned error message
      return { error: "Unexpected error: " + e.message };
    }
  };

  // ── SUPABASE DATA LOADERS ─────────────────────────────────────────────────
  useEffect(() => {
    loadPosts();
    loadInstructors();
    loadStringers();
    loadCourtNames();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loggedIn) {
      loadAdminQueue();
      loadContactRequests();
      loadApprovedMembers();
    }
  }, [loggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadContactRequests = async () => {
    const uid = (await supabase.auth.getSession())?.data?.session?.user?.id;
    if (!uid) return;
    // Load requests received (pending only — for Dashboard)
    const { data: received } = await supabase
      .from("contact_requests")
      .select("id, from_id, to_id, status, created_at, profiles!contact_requests_from_id_fkey(id,name,ntrp,skill,play,email,phone)")
      .eq("to_id", uid)
      .eq("status", "pending")
      .order("created_at", { ascending: false });
    if (received) setContactReqs(received.map(r => ({
      id: r.id,
      from_id: r.from_id,
      to_id: r.to_id,
      from: r.profiles?.name || "Unknown",
      initials: r.profiles?.name?.split(" ").map(n=>n[0]).join("").toUpperCase() || "?",
      ntrp: r.profiles?.ntrp,
      skill: r.profiles?.skill,
      play: r.profiles?.play,
      email: r.profiles?.email,
      phone: r.profiles?.phone,
      date: new Date(r.created_at).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}),
    })));
    // Load requests sent (any status — for Connections button state)
    const { data: sent } = await supabase
      .from("contact_requests")
      .select("from_id, to_id, status")
      .eq("from_id", uid);
    // Also load requests received (any status — so "Connected" shows for both directions)
    const { data: recv } = await supabase
      .from("contact_requests")
      .select("from_id, to_id, status")
      .eq("to_id", uid);
    // Merge: for sent requests, key is to_id. For received, key is from_id.
    // We normalize into a single array keyed by "the other person's id"
    const allConnections = [];
    if (sent) sent.forEach(r => allConnections.push({ to_id: r.to_id, status: r.status }));
    if (recv) recv.forEach(r => allConnections.push({ to_id: r.from_id, status: r.status }));
    setSentRequests(allConnections);
  };

  const loadCourtNames = async () => {
    const { data } = await supabase.from("courts").select("name").order("name");
    if (data) setCourtNames([...data.map(c => c.name), "Other / TBD"]);
  };

  const loadApprovedMembers = async () => {
    const { data } = await supabase.from("profiles")
      .select("id,name,email,phone,ntrp,skill,play,days,times,gender,age_range")
      .eq("is_approved", true).eq("is_active", true).order("name");
    if (data) setApprovedMembers(data.map(p => ({
      id: p.id, name: p.name, email: p.email, phone: p.phone,
      ntrp: p.ntrp, skill: p.skill,
      play: p.play, days: p.days || [], times: p.times || [],
      gender: p.gender, ageRange: p.age_range,
      initials: p.name?.split(" ").map(n=>n[0]).join("").toUpperCase() || "?"
    })));
  };

  const loadPosts = async () => {
    const { data } = await supabase.from("posts").select("*").order("created_at", { ascending: false });
    if (data?.length) setPosts(data.map(p => ({
      id: p.id, title: p.title, body: p.body, author: p.author,
      images: p.images || [], date: new Date(p.created_at).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})
    })));
  };

  const loadInstructors = async () => {
    const { data } = await supabase.from("instructors").select("*").eq("approved", true);
    if (data?.length) setInstructors(data.map(i => ({
      id: i.id, name: i.name, email: i.email, phone: i.phone,
      lessonTypes: i.lesson_types || [], notes: i.notes, approved: true
    })));
  };

  const loadStringers = async () => {
    const { data } = await supabase.from("stringers").select("*").eq("approved", true);
    if (data?.length) setStringers(data.map(s => ({
      id: s.id, name: s.name, email: s.email, phone: s.phone, notes: s.notes, approved: true
    })));
  };

  const loadAdminQueue = async () => {
    // Load unapproved member registrations
    const { data: profileData } = await supabase.from("profiles")
      .select("*").eq("is_approved", false).eq("is_active", true)
      .order("created_at", { ascending: false });
    const profileQueue = (profileData || []).map(u => ({
      id: u.id, name: u.name, email: u.email, phone: u.phone,
      ntrp: u.ntrp || "—", skill: u.skill, play: u.play, gender: u.gender,
      ageRange: u.age_range, days: u.avail_days, times: u.avail_times,
      submitted: new Date(u.created_at).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})
    }));

    // Load unapproved stringer listings
    const { data: stringerData } = await supabase.from("stringers")
      .select("*").eq("approved", false)
      .order("created_at", { ascending: false });
    const stringerQueue = (stringerData || []).map(s => ({
      id: s.id, type: "Stringer Listing", table: "stringers",
      name: s.name, email: s.email, phone: s.phone || "—",
      notes: s.notes || "",
      submitted: new Date(s.created_at).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})
    }));

    // Load unapproved instructor listings
    const { data: instructorData } = await supabase.from("instructors")
      .select("*").eq("approved", false)
      .order("created_at", { ascending: false });
    const instructorQueue = (instructorData || []).map(i => ({
      id: i.id, type: "Instructor Listing", table: "instructors",
      name: i.name, email: i.email, phone: i.phone || "—",
      lessonTypes: i.lesson_types || [], notes: i.notes || "",
      submitted: new Date(i.created_at).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})
    }));

    setAdminQueue([...profileQueue, ...stringerQueue, ...instructorQueue]);
  };

  const nav = (v) => { setView(v); setMobileMenuOpen(false); window.scrollTo(0,0); };

  // ResetPasswordView defined outside App (like RegisterView and AdminView)

  // Page title & favicon
  useEffect(() => {
    document.title = "Columbus Tennis Club | Columbus, Indiana";
    let link = document.querySelector("link[rel~='icon']");
    if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
    link.type = "image/png";
    link.href = TENNIS_C_URL;
  }, []);

  // ── CSS ───────────────────────────────────────────────────────────────────
  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Source+Serif+4:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@400;500;600;700&display=swap');
    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
    :root{
      --red:#CC0000; --red-dark:#A00000; --red-light:#E82020;
      --black:#1A1A1A; --charcoal:#2C2C2C;
      --cream:#F5F0E8; --cream-dark:#EDE8DF; --cream-mid:#E4DDD2;
      --gold:#D4A017; --grey:#6B6B6B; --grey-light:#D8D3CA;
      --white:#FFFFFF; --green:#C8E632;
      --shadow-sm:0 1px 6px rgba(0,0,0,0.07);
      --shadow-md:0 3px 16px rgba(0,0,0,0.1);
      --shadow-lg:0 8px 40px rgba(0,0,0,0.18);
    }
    html{scroll-behavior:smooth}
    body{font-family:'DM Sans',sans-serif;background:var(--cream);color:var(--black);-webkit-font-smoothing:antialiased}
    .app{min-height:100vh;display:flex;flex-direction:column}

    /* NAV */
    .nav{background:var(--black);padding:0 28px;height:68px;display:flex;align-items:center;
      justify-content:space-between;position:sticky;top:0;z-index:100;border-bottom:3px solid var(--red)}
    .nav-brand{display:flex;align-items:center;gap:6px;cursor:pointer;text-decoration:none}
    .nav-brand-identity{display:flex;flex-direction:column;gap:1px}
    .nav-brand-ctc{font-family:'Bebas Neue',sans-serif;font-size:22px;letter-spacing:3px;
      color:var(--cream);line-height:1}
    .nav-brand-sub{font-family:'Bebas Neue',sans-serif;font-size:14px;letter-spacing:2px;
      color:rgba(245,240,232,0.5);line-height:1}
    .nav-links{display:flex;gap:6px;align-items:center;flex-wrap:wrap}
    .nav-btn{padding:7px 16px;border-radius:3px;border:none;cursor:pointer;font-family:'Bebas Neue',sans-serif;
      font-weight:400;font-size:20px;letter-spacing:1.5px;transition:all 0.15s}
    .nav-btn-ghost{background:transparent;color:rgba(245,240,232,0.7);border:1.5px solid rgba(255,255,255,0.15)}
    .nav-btn-ghost:hover{border-color:var(--red);color:var(--red)}
    .nav-btn-red{background:var(--red);color:white}
    .nav-btn-red:hover{background:var(--red-dark)}
    .nav-btn-link{background:transparent;color:rgba(245,240,232,0.65);border:none;font-size:20px}
    .nav-btn-link:hover{color:var(--cream)}
    .nav-btn-link.active{color:var(--red)}

    /* HERO */
    .hero{background:var(--black);height:100vh;min-height:600px;max-height:900px;
      position:relative;overflow:hidden;display:flex;flex-direction:column;justify-content:flex-start}

    /* Animated court — full bleed background */
    .hero-court-bg{position:absolute;inset:0;width:100%;height:100%;pointer-events:none}

    /* Diagonal red slash accent */
    .hero-slash{position:absolute;top:0;right:0;width:45%;height:100%;
      background:linear-gradient(160deg, transparent 45%, rgba(204,0,0,0.12) 45%);
      pointer-events:none;z-index:1}

    /* Large ghost C watermark */
    .hero-watermark{position:absolute;right:-4%;top:50%;transform:translateY(-50%);
      font-family:'Bebas Neue',sans-serif;font-size:clamp(400px,55vw,700px);
      line-height:1;color:transparent;
      -webkit-text-stroke:1.5px rgba(204,0,0,0.18);
      pointer-events:none;z-index:1;letter-spacing:-20px;user-select:none}

    .hero-content{position:relative;z-index:3;padding:16px 56px 72px;display:flex;
      flex-direction:column;gap:0;justify-content:flex-start}

    .hero-tag{display:inline-flex;align-items:center;gap:10px;margin-bottom:20px}
    .hero-tag-line{width:40px;height:2px;background:var(--red)}
    .hero-tag-text{font-size:10px;letter-spacing:5px;text-transform:uppercase;
      color:rgba(245,240,232,0.5);font-weight:700}

    /* Two-column hero body: title left, sub right */
    .hero-body{display:flex;align-items:flex-start;gap:48px;flex-wrap:wrap}
    .hero-title-col{flex:0 0 auto}
    .hero-sub-col{flex:1;min-width:240px;display:flex;align-items:center;
      padding-bottom:12px;align-self:center}

    .hero-title{font-family:'Bebas Neue',sans-serif;
      font-size:clamp(90px,14vw,190px);
      line-height:0.62;letter-spacing:1px;color:var(--cream);
      display:flex;flex-direction:column;gap:0;
      margin-bottom:0}
    .hero-title .outline{
      color:transparent;
      -webkit-text-stroke:3.5px var(--cream);
    }

    .hero-bottom-row{display:flex;align-items:flex-end;justify-content:space-between;
      flex-wrap:wrap;gap:20px}
    .hero-sub{font-family:'DM Sans',sans-serif;font-size:34px;
      color:rgba(245,240,232,0.88);line-height:1.65;
      font-weight:600;text-align:right;text-wrap:balance;
      text-shadow:0 2px 8px rgba(0,0,0,0.5)}
    .hero-scroll-cue{position:absolute;bottom:130px;left:50%;transform:translateX(-50%);
      display:flex;flex-direction:column;align-items:center;gap:8px;
      font-family:'Bebas Neue',sans-serif;font-size:13px;letter-spacing:5px;
      text-transform:uppercase;color:rgba(245,240,232,0.6);font-weight:700;
      z-index:3;cursor:pointer;animation:scrollPulse 2.4s ease-in-out infinite;
      overflow:visible}
    /* Chevron arrows using borders */
    .hero-scroll-chevron{display:flex;flex-direction:column;align-items:center;gap:4px}
    @keyframes scrollPulse{0%,100%{opacity:0.45}50%{opacity:1}}

    /* Rally ball — JS-animated, no CSS keyframe needed */
    .ball-rally{position:absolute;pointer-events:none;z-index:3;
      transition:transform 0s linear,left 0s,top 0s;will-change:transform}

    /* Red bottom border on hero */
    .hero::after{content:'';position:absolute;bottom:0;left:0;right:0;
      height:4px;background:var(--red);z-index:10}

    @media(max-width:900px){
      .hero{min-height:auto;max-height:none;height:auto;padding-bottom:20px}
      .hero-content{padding:16px 20px 20px}
      .hero-title{font-size:clamp(48px,11vw,90px) !important}
      .hero-title>div>div:first-child{width:110px !important;margin-top:-14px !important}
      .hero-title>div>div:first-child img{width:110px !important;height:110px !important}
      .hero-title>div>span{margin-left:-12px !important}
      .hero-title>.outline{padding-left:48px !important;margin-bottom:6px !important;
        -webkit-text-stroke:2px var(--cream) !important}
      .hero-title>.outline>span:first-child{font-size:1.5em !important;
        -webkit-text-stroke:4px rgba(245,240,232,0.9) !important}
      .hero-title>.outline>span:last-child{margin-left:0 !important}
      .hero-title>div:first-child{margin-bottom:6px !important}
      .hero-body{flex-direction:column;gap:16px}
      .hero-sub-col{align-self:flex-start}
      .hero-sub{font-size:18px;font-weight:700;text-align:left;
        text-shadow:0 2px 12px rgba(0,0,0,0.8),0 0 30px rgba(0,0,0,0.5) !important}
      .hero-overlay{background:linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.55) 40%, rgba(0,0,0,0.4) 70%, rgba(0,0,0,0.2) 100%) !important}
      .hero-scroll-cue{position:relative;bottom:auto;left:auto;transform:none;
        margin:12px auto 0;display:flex}
    }
    @media(max-width:480px){
      .hero-title{font-size:clamp(36px,10vw,60px) !important}
      .hero-title>div>div:first-child{width:80px !important;margin-top:-10px !important}
      .hero-title>div>div:first-child img{width:80px !important;height:80px !important}
      .hero-title>div>span{margin-left:-8px !important}
      .hero-title>.outline{padding-left:18px !important;margin-bottom:4px !important;
        -webkit-text-stroke:1.5px var(--cream) !important}
      .hero-title>.outline>span:first-child{font-size:1.5em !important;
        -webkit-text-stroke:3px rgba(245,240,232,0.9) !important}
      .hero-title>div:first-child{margin-bottom:4px !important}
      .hero-content{padding:10px 14px 16px}
      .hero-sub{font-size:16px;font-weight:700}
    }

    /* TILE GRID */
    .tile-section{padding:64px 32px;max-width:1200px;margin:0 auto;width:100%}
    .tile-section-title{font-family:'Bebas Neue',sans-serif;font-size:20px;letter-spacing:6px;
      text-transform:uppercase;color:var(--grey);margin-bottom:32px;text-align:center}
    .tile-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:20px}
    .tile{background:var(--black);border-radius:6px;overflow:hidden;cursor:pointer;
      transition:transform 0.2s,box-shadow 0.2s;position:relative;
      border:1px solid rgba(255,255,255,0.05);display:flex;flex-direction:column}
    .tile:hover{transform:translateY(-4px);box-shadow:var(--shadow-lg)}
    .tile-accent{height:4px;background:var(--red)}
    .tile-accent.gold{background:var(--gold)}
    .tile-accent.green{background:var(--green)}
    .tile-accent.charcoal{background:var(--charcoal);border-top:1px solid #444}
    .tile-inner{padding:28px 28px 24px;position:relative;flex:1;display:flex;flex-direction:column}
    .tile-icon{position:absolute;top:20px;right:20px;display:flex;align-items:center;
      pointer-events:none}
    .tile-title{font-family:'Bebas Neue',sans-serif;font-size:26px;letter-spacing:2px;
      color:var(--cream);line-height:1.1;margin-bottom:10px;padding-right:72px}
    .tile-desc{font-size:13px;color:rgba(245,240,232,0.55);line-height:1.6}
    .tile-cta{display:inline-flex;align-items:center;gap:6px;margin-top:18px;
      font-size:12px;font-weight:700;letter-spacing:1px;text-transform:uppercase;
      color:var(--red);transition:gap 0.15s}
    .tile:hover .tile-cta{gap:10px}
    .tile-members-badge{position:absolute;inset:0;overflow:hidden;border-radius:6px;pointer-events:none;z-index:2}
    .tile-members-badge::before{content:"";position:absolute;inset:0;
      background:repeating-linear-gradient(
        -35deg,
        transparent,
        transparent 28px,
        rgba(204,0,0,0.05) 28px,
        rgba(204,0,0,0.05) 30px
      )}
    .tile-members-badge::after{content:"MEMBERS ONLY";position:absolute;
      top:50%;left:50%;transform:translate(-50%,-50%) rotate(-35deg);
      font-family:'Bebas Neue',sans-serif;font-size:30px;letter-spacing:6px;font-weight:900;
      color:rgba(204,0,0,0.92);white-space:nowrap;pointer-events:none;
      text-shadow:-1px -1px 0 rgba(245,240,232,0.9),1px -1px 0 rgba(245,240,232,0.9),-1px 1px 0 rgba(245,240,232,0.9),1px 1px 0 rgba(245,240,232,0.9),0 0 8px rgba(245,240,232,0.25),0 2px 8px rgba(0,0,0,0.5)}
    .tile-coming-soon{position:absolute;inset:0;overflow:hidden;border-radius:6px;pointer-events:none;z-index:2}
    .tile-coming-soon::before{content:"";position:absolute;inset:0;
      background:repeating-linear-gradient(
        -35deg,
        transparent,
        transparent 28px,
        rgba(212,160,23,0.06) 28px,
        rgba(212,160,23,0.06) 30px
      )}
    .tile-coming-soon::after{content:"COMING SOON";position:absolute;
      top:50%;left:50%;transform:translate(-50%,-50%) rotate(-35deg);
      font-family:'Bebas Neue',sans-serif;font-size:30px;letter-spacing:6px;font-weight:900;
      color:rgba(212,160,23,0.92);white-space:nowrap;pointer-events:none;
      text-shadow:-1px -1px 0 rgba(245,240,232,0.9),1px -1px 0 rgba(245,240,232,0.9),-1px 1px 0 rgba(245,240,232,0.9),1px 1px 0 rgba(245,240,232,0.9),0 0 8px rgba(245,240,232,0.25),0 2px 8px rgba(0,0,0,0.5)}
    .tile-title-members{padding-top:0}

    /* PAGE SHELL */
    .page-wrap{flex:1;padding:40px 24px;max-width:1100px;margin:0 auto;width:100%}
    .page-header{margin-bottom:36px}
    .page-back{display:inline-flex;align-items:center;gap:6px;font-size:13px;font-weight:600;
      color:var(--grey);cursor:pointer;background:none;border:none;letter-spacing:0.3px;
      padding:0;font-family:'DM Sans',sans-serif;margin-bottom:20px;transition:color 0.15s}
    .page-back:hover{color:var(--red)}
    .page-title{font-family:'Bebas Neue',sans-serif;font-size:42px;letter-spacing:2px;color:var(--black)}
    .page-sub{color:var(--grey);font-size:14px;margin-top:4px}
    .red-bar{height:3px;background:var(--red);width:48px;margin-top:12px}

    /* CARDS */
    .card{background:white;border-radius:6px;box-shadow:var(--shadow-sm);overflow:hidden;
      border-top:3px solid var(--red)}
    .card-gold{border-top-color:var(--gold)}
    .card-body{padding:22px}
    .card-name{font-size:17px;font-weight:700;margin-bottom:4px}
    .card-meta{font-size:13px;color:var(--grey);line-height:1.6}
    .card-contact{display:flex;flex-direction:column;gap:4px;margin-top:10px;font-size:13px}
    .card-contact a{color:var(--red);text-decoration:none}
    .card-contact a:hover{text-decoration:underline}
    .card-notes{background:var(--cream-dark);border-radius:4px;padding:10px 14px;
      font-size:13px;color:var(--charcoal);margin-top:12px;line-height:1.5;font-style:italic}
    .card-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(300px,1fr));gap:16px}

    /* LESSON TYPE CHIPS */
    .lesson-chips{display:flex;flex-wrap:wrap;gap:5px;margin-top:10px}
    .lesson-chip{background:var(--black);color:var(--cream);border-radius:3px;
      padding:3px 9px;font-size:11px;font-weight:700;letter-spacing:0.5px}

    /* BLOG */
    .post-card{background:white;border-radius:6px;box-shadow:var(--shadow-sm);
      margin-bottom:20px;overflow:hidden;border-left:4px solid var(--red)}
    .post-header{padding:22px 24px 14px;border-bottom:1px solid var(--cream-dark)}
    .post-title{font-family:'Bebas Neue',sans-serif;font-size:26px;letter-spacing:1.5px;
      color:var(--black);margin-bottom:6px}
    .post-meta{font-size:12px;color:var(--grey);letter-spacing:0.5px}
    .post-body{padding:18px 24px;font-size:15px;line-height:1.75;color:var(--charcoal);
      font-family:'DM Sans',sans-serif}
    .post-images{display:flex;gap:12px;padding:0 24px 24px;flex-wrap:wrap}
    .post-img{width:220px;height:155px;object-fit:cover;border-radius:5px;
      border:1px solid var(--grey-light);cursor:zoom-in;transition:opacity 0.15s}
    .post-img:hover{opacity:0.85}
    /* Lightbox */
    .lightbox-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.88);z-index:9999;
      display:flex;align-items:center;justify-content:center;cursor:zoom-out}
    .lightbox-img{max-width:90vw;max-height:88vh;border-radius:6px;box-shadow:0 8px 48px rgba(0,0,0,0.6)}
    .lightbox-close{position:fixed;top:20px;right:28px;color:white;font-size:32px;
      cursor:pointer;font-family:'DM Sans',sans-serif;line-height:1;opacity:0.8}
    .lightbox-close:hover{opacity:1}

    /* CONNECTIONS / DIRECTORY */
    .filters{background:white;border-radius:6px;padding:18px 22px;margin-bottom:24px;
      box-shadow:var(--shadow-sm);display:flex;flex-wrap:wrap;gap:16px;align-items:flex-end}
    .filter-group{display:flex;flex-direction:column;gap:5px;min-width:130px}
    .filter-label{font-size:10px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:var(--grey)}
    .filter-select{padding:7px 11px;border:1.5px solid var(--grey-light);border-radius:3px;
      font-size:13px;background:var(--cream);font-family:'DM Sans',sans-serif;cursor:pointer}
    .filter-select:focus{outline:none;border-color:var(--red)}
    .player-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(270px,1fr));gap:16px}
    .player-card{background:white;border-radius:6px;padding:18px;box-shadow:var(--shadow-sm);
      border-top:3px solid var(--red);transition:transform 0.15s,box-shadow 0.15s}
    .player-card:hover{transform:translateY(-2px);box-shadow:var(--shadow-md)}
    .player-top{display:flex;align-items:center;gap:12px;margin-bottom:12px}
    .player-avatar{width:46px;height:46px;border-radius:50%;background:var(--black);color:var(--cream);
      display:flex;align-items:center;justify-content:center;font-family:'Bebas Neue',sans-serif;
      font-size:22px;letter-spacing:1px;flex-shrink:0;font-weight:900}
    .player-name{font-weight:700;font-size:15px;margin-bottom:4px}
    .avail-chip{background:var(--cream-dark);color:var(--black);border-radius:2px;
      padding:2px 6px;font-size:10px;font-weight:600}
    .avail-chip-time{background:rgba(204,0,0,0.08);color:var(--red)}
    .contact-req-btn{width:100%;margin-top:12px;padding:8px;background:transparent;
      border:1.5px solid var(--red);color:var(--red);border-radius:3px;font-size:12px;
      font-weight:700;cursor:pointer;letter-spacing:0.5px;transition:all 0.15s;
      font-family:'DM Sans',sans-serif}
    .contact-req-btn:hover{background:var(--red);color:white}

    /* FORMS */
    .form-section{background:white;border-radius:6px;padding:28px;box-shadow:var(--shadow-sm);
      margin-top:40px;border-top:3px solid var(--gold)}
    .form-section-title{font-family:'Bebas Neue',sans-serif;font-size:20px;letter-spacing:2px;
      margin-bottom:18px;color:var(--black)}
    .form-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px}
    .form-group{display:flex;flex-direction:column;gap:5px}
    .form-group.full{grid-column:1/-1}
    label{font-size:11px;font-weight:700;letter-spacing:0.5px;color:var(--grey);text-transform:uppercase}
    input,select,textarea{padding:9px 13px;border:1.5px solid var(--grey-light);border-radius:3px;
      font-size:14px;font-family:'DM Sans',sans-serif;background:var(--cream);color:var(--black);
      transition:border-color 0.15s}
    input::placeholder,textarea::placeholder{color:var(--grey-light);font-style:italic}
    input:focus,select:focus,textarea:focus{outline:none;border-color:var(--red)}
    textarea{resize:vertical;min-height:90px}
    .pill-group{display:flex;flex-wrap:wrap;gap:7px;margin-top:4px}
    .submit-btn{padding:12px 28px;background:var(--red);color:white;border:none;border-radius:3px;
      font-size:14px;font-weight:700;cursor:pointer;font-family:'DM Sans',sans-serif;
      letter-spacing:0.5px;transition:background 0.2s;margin-top:6px}
    .submit-btn:hover{background:var(--red-dark)}

    /* COURTS */
    .court-list{display:grid;grid-template-columns:repeat(auto-fill,minmax(340px,1fr));gap:20px;margin-bottom:32px}
    .court-card{background:white;border-radius:6px;padding:22px 24px;box-shadow:var(--shadow-sm);
      border-left:4px solid var(--red)}
    .court-card.private{border-left-color:var(--red)}
    .court-card-header{display:flex;align-items:flex-start;justify-content:space-between;gap:8px;margin-bottom:6px}
    .court-name{font-weight:700;font-size:17px;line-height:1.2;color:var(--black)}
    .court-badge{font-size:10px;font-weight:700;letter-spacing:1px;text-transform:uppercase;
      padding:3px 8px;border-radius:20px;white-space:nowrap;flex-shrink:0;margin-top:2px}
    .court-badge.public{background:rgba(58,125,68,0.15);color:#2A6E2A}
    .court-badge.private{background:rgba(204,0,0,0.12);color:#A00000}
    .court-meta{display:flex;align-items:center;gap:12px;font-size:13px;color:var(--grey);margin-bottom:10px;flex-wrap:wrap}
    .court-meta-item{display:flex;align-items:center;gap:4px}
    .court-addr{font-size:13px;color:var(--grey);margin-bottom:10px}
    .court-notes{font-size:13px;color:var(--charcoal);line-height:1.65;
      background:var(--cream-dark);padding:10px 14px;border-radius:3px}
    .map-container{background:var(--charcoal);border-radius:6px;overflow:hidden;
      box-shadow:var(--shadow-md);margin-bottom:12px;position:relative}
    .map-placeholder{width:100%;height:420px;display:flex;flex-direction:column;
      align-items:center;justify-content:center;gap:12px;color:rgba(245,240,232,0.5)}
    .map-pin{font-size:28px}
    .map-note{font-size:13px;text-align:center;max-width:340px;line-height:1.5}
    .map-embed{width:100%;height:420px;border:none}

    /* ADMIN */
    .admin-tabs{display:flex;gap:0;margin-bottom:28px;border-bottom:2px solid var(--grey-light)}
    .admin-tab{padding:10px 20px;font-size:13px;font-weight:600;cursor:pointer;border:none;
      background:transparent;color:var(--grey);border-bottom:3px solid transparent;
      margin-bottom:-2px;font-family:'DM Sans',sans-serif;transition:all 0.15s}
    .admin-tab.active{color:var(--red);border-bottom-color:var(--red)}
    .queue-card{background:white;border-radius:6px;padding:18px 22px;box-shadow:var(--shadow-sm);
      margin-bottom:12px;display:flex;align-items:center;justify-content:space-between;
      flex-wrap:wrap;gap:12px;border-left:4px solid var(--gold)}
    .queue-info h3{font-size:15px;font-weight:700;margin-bottom:3px}
    .queue-info p{font-size:12px;color:var(--grey)}
    .queue-actions{display:flex;gap:8px}
    .btn-approve{padding:7px 16px;background:#2A6E2A;color:white;border:none;border-radius:3px;
      font-weight:700;font-size:12px;cursor:pointer;font-family:'DM Sans',sans-serif}
    .btn-approve:hover{background:#1E501E}
    .btn-decline{padding:7px 16px;background:transparent;color:var(--red);
      border:1.5px solid var(--red);border-radius:3px;font-weight:700;font-size:12px;
      cursor:pointer;font-family:'DM Sans',sans-serif}
    .btn-decline:hover{background:var(--red);color:white}
    .badge{background:var(--red);color:white;border-radius:50%;width:20px;height:20px;
      display:inline-flex;align-items:center;justify-content:center;font-size:11px;
      font-weight:700;margin-left:6px}

    /* AUTH */
    .auth-wrap{min-height:calc(100vh - 128px);display:flex;align-items:center;
      justify-content:center;padding:40px 20px;background:var(--black);position:relative;overflow:hidden}
    .auth-box{background:white;border-radius:6px;max-width:420px;width:100%;
      overflow:hidden;box-shadow:var(--shadow-lg)}
    .auth-head{background:var(--red);color:white;padding:28px;text-align:center;
      border-bottom:3px solid var(--red-dark)}
    .auth-title{font-family:'Bebas Neue',sans-serif;font-size:36px;letter-spacing:3px;margin-top:10px}
    .auth-sub{font-size:14px;opacity:0.85;margin-top:4px;letter-spacing:2px;font-weight:600}
    .auth-body{padding:28px}
    .auth-form{display:flex;flex-direction:column;gap:14px}
    .auth-switch{text-align:center;margin-top:14px;font-size:13px;color:var(--grey)}
    .auth-switch a{color:var(--red);cursor:pointer;font-weight:600}
    .demo-note{background:var(--cream-dark);border-radius:4px;padding:10px 14px;
      font-size:12px;color:var(--grey);margin-top:10px;line-height:1.5}

    /* MODAL */
    .modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.65);display:flex;
      align-items:center;justify-content:center;z-index:200;padding:20px}
    .modal-box{background:white;border-radius:8px;max-width:460px;width:100%;
      overflow:hidden;box-shadow:var(--shadow-lg);padding:28px 28px 22px}
    .modal{background:white;border-radius:6px;max-width:460px;width:100%;
      overflow:hidden;box-shadow:var(--shadow-lg)}
    .modal-head{background:var(--black);color:var(--cream);padding:16px 22px;
      border-bottom:3px solid var(--red);font-family:'Bebas Neue',sans-serif;
      font-size:18px;letter-spacing:2px}
    .modal-body{padding:22px}
    .modal-actions{display:flex;gap:10px;margin-top:18px}

    /* TOAST — defined in responsive section below with accessibility attrs */
    .toast.error{border-left-color:var(--red)}
    @keyframes slideIn{from{transform:translateX(40px);opacity:0}to{transform:translateX(0);opacity:1}}

    /* EQUIPMENT EXCHANGE */
    .exchange-filter-bar{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:24px;align-items:center}
    .exchange-cat-btn{padding:6px 14px;border-radius:20px;border:1.5px solid var(--grey-light);
      background:transparent;color:var(--grey);font-size:12px;font-weight:700;cursor:pointer;
      letter-spacing:0.4px;transition:all 0.15s;font-family:'DM Sans',sans-serif}
    .exchange-cat-btn.active{border-color:var(--black);background:var(--black);color:var(--cream)}
    .exchange-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(320px,1fr));gap:20px;margin-bottom:40px}
    .exchange-card{background:white;border-radius:6px;box-shadow:var(--shadow-sm);
      overflow:hidden;border-top:4px solid #3A7D44;position:relative;transition:box-shadow 0.15s}
    .exchange-card:hover{box-shadow:var(--shadow-md)}
    .exchange-card.sold-card{opacity:0.55;border-top-color:var(--grey-light)}
    .exchange-card-header{padding:16px 18px 10px;display:flex;justify-content:space-between;align-items:flex-start;gap:8px}
    .exchange-cat-tag{font-size:10px;font-weight:800;letter-spacing:1px;text-transform:uppercase;
      color:white;background:#3A7D44;border-radius:3px;padding:3px 8px;white-space:nowrap}
    .exchange-cat-tag.sold-tag{background:var(--grey)}
    .exchange-listing-type{font-size:10px;font-weight:800;letter-spacing:0.8px;
      color:var(--red);text-transform:uppercase;white-space:nowrap}
    .exchange-listing-type.give{color:#3A7D44}
    .exchange-title{font-size:16px;font-weight:700;color:var(--black);padding:0 18px;margin-bottom:6px;line-height:1.3}
    .exchange-price{font-family:'Bebas Neue',sans-serif;font-size:26px;letter-spacing:1px;
      color:var(--black);padding:0 18px;margin-bottom:10px}
    .exchange-price.free-price{color:#3A7D44}
    .exchange-desc{font-size:13px;color:var(--charcoal);padding:0 18px 14px;line-height:1.6;
      border-bottom:1px solid var(--cream-mid)}
    .exchange-footer{padding:12px 18px;display:flex;align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap}
    .exchange-author{font-size:12px;color:var(--grey)}
    .exchange-author strong{color:var(--black)}
    .exchange-days-left{font-size:11px;font-weight:600}
    .exchange-contact-btn{padding:7px 16px;border-radius:3px;border:1.5px solid #3A7D44;
      background:transparent;color:#3A7D44;font-size:12px;font-weight:700;cursor:pointer;
      letter-spacing:0.5px;transition:all 0.15s;font-family:'DM Sans',sans-serif}
    .exchange-contact-btn:hover{background:#3A7D44;color:white}
    .exchange-mark-sold-btn{padding:5px 12px;border-radius:3px;border:1.5px solid var(--grey-light);
      background:transparent;color:var(--grey);font-size:11px;font-weight:600;cursor:pointer;
      font-family:'DM Sans',sans-serif;transition:all 0.15s}
    .exchange-mark-sold-btn:hover{border-color:var(--red);color:var(--red)}
    .exchange-sold-overlay{position:absolute;top:12px;right:12px;background:var(--grey);
      color:white;font-family:'Bebas Neue',sans-serif;font-size:14px;letter-spacing:2px;
      padding:4px 10px;border-radius:3px}

    /* BULLETIN BOARD */
    .bulletin-notice{background:rgba(176,125,42,0.12);border:1.5px solid rgba(176,125,42,0.35);
      border-radius:5px;padding:12px 16px;margin-bottom:24px;display:flex;gap:10px;
      font-size:13px;color:var(--charcoal);line-height:1.6}
    .bulletin-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(340px,1fr));gap:20px;margin-bottom:40px}
    .bulletin-card{background:white;border-radius:6px;box-shadow:var(--shadow-sm);
      overflow:hidden;border-top:4px solid #B07D2A;position:relative}
    .bulletin-card-own{border-top-color:var(--red)}
    .bulletin-card-header{display:flex;align-items:flex-start;gap:10px;padding:16px 18px 10px}
    .bulletin-pin{font-size:20px;line-height:1}
    .bulletin-author{font-weight:700;font-size:15px;color:var(--black)}
    .bulletin-meta{font-size:11px;color:var(--grey);margin-top:2px;letter-spacing:0.2px}
    .bulletin-action-btn{background:transparent;border:1.5px solid var(--grey-light);
      border-radius:3px;cursor:pointer;padding:5px 8px;font-size:14px;transition:all 0.15s;
      display:inline-flex;align-items:center;justify-content:center}
    .bulletin-action-btn:hover{border-color:var(--gold);background:rgba(212,160,23,0.08)}
    .bulletin-action-delete:hover{border-color:var(--red)!important;background:rgba(204,0,0,0.06)!important}
    .bulletin-details{padding:4px 18px 12px;border-bottom:1px solid var(--cream-mid)}
    .bulletin-detail-row{display:flex;gap:10px;margin-bottom:6px;align-items:baseline}
    .bulletin-detail-label{font-size:11px;font-weight:700;color:var(--grey);
      letter-spacing:0.3px;min-width:62px;flex-shrink:0}
    .bulletin-detail-val{font-size:13px;color:var(--black);line-height:1.4}
    .bulletin-note{font-size:13px;color:var(--charcoal);font-style:italic;
      margin-top:8px;padding:8px 12px;background:var(--cream-dark);border-radius:3px;line-height:1.5}
    .bulletin-card-footer{padding:12px 18px;display:flex;align-items:center;
      justify-content:space-between;gap:10px;flex-wrap:wrap}
    .bulletin-interested{font-size:12px;color:var(--grey);font-weight:500}
    .bulletin-interest-btn{padding:7px 16px;border-radius:3px;border:1.5px solid #B07D2A;
      background:transparent;color:#B07D2A;font-size:12px;font-weight:700;cursor:pointer;
      letter-spacing:0.5px;transition:all 0.15s;font-family:'DM Sans',sans-serif}
    .bulletin-interest-btn:hover{background:#B07D2A;color:white}
    .bulletin-interest-btn-active{background:#B07D2A!important;color:white!important}

    /* FOOTER */
    .footer{background:var(--black);color:var(--grey);text-align:center;padding:28px;
      font-size:11px;letter-spacing:1.5px;border-top:3px solid var(--red)}
    .footer-brand{font-family:'Bebas Neue',sans-serif;font-size:18px;letter-spacing:3px;
      color:var(--cream);margin-bottom:6px}
    .footer-brand span{color:var(--red)}

    /* DIVIDER */
    .divider{height:1px;background:var(--cream-mid);margin:28px 0;border:none}

    /* RESPONSIVE */
    .mobile-menu-btn{display:none;background:none;border:none;cursor:pointer;padding:8px;
      color:var(--cream);flex-shrink:0}
    .mobile-menu-btn svg{display:block}
    .mobile-overlay{display:none}
    .mobile-drawer{display:none}
    .mobile-drawer-header{display:none}
    .mobile-drawer-close{display:none}
    .mobile-drawer .nav-btn-mobile{display:none}
    .mobile-drawer-divider{display:none}

    @media(max-width:768px){
      .tile-grid{grid-template-columns:1fr 1fr}
      .hero-inner{padding:48px 24px}
      .nav-links .nav-btn-link{display:none}
      .nav-links .nav-btn-ghost,.nav-links .nav-btn-red{display:none}
      .form-grid{grid-template-columns:1fr}
      .form-group.full{grid-column:1}

      /* Hamburger button visible on mobile */
      .mobile-menu-btn{display:flex;align-items:center;justify-content:center}

      /* Mobile slide-out drawer */
      .mobile-overlay{position:fixed;inset:0;z-index:199;display:none}
      .mobile-overlay.open{display:block;background:rgba(0,0,0,0.5)}
      .mobile-drawer{display:flex;position:fixed;top:0;right:0;bottom:0;width:280px;max-width:85vw;
        background:var(--black);z-index:200;padding:20px 0;
        transform:translateX(100%);transition:transform 0.25s ease;
        flex-direction:column;border-left:3px solid var(--red);
        overflow-y:auto;-webkit-overflow-scrolling:touch}
      .mobile-drawer.open{transform:translateX(0)}
      .mobile-drawer-header{display:flex;align-items:center;justify-content:space-between;
        padding:8px 20px 20px;border-bottom:1px solid rgba(255,255,255,0.1);margin-bottom:8px}
      .mobile-drawer-close{display:block;background:none;border:none;cursor:pointer;color:var(--cream);
        font-size:24px;padding:4px 8px;line-height:1}
      .mobile-drawer .nav-btn-mobile{display:block;width:100%;text-align:left;
        padding:14px 24px;border:none;background:transparent;
        color:rgba(245,240,232,0.75);cursor:pointer;
        font-family:'Bebas Neue',sans-serif;font-size:20px;letter-spacing:1.5px;
        transition:all 0.15s}
      .mobile-drawer .nav-btn-mobile:hover,
      .mobile-drawer .nav-btn-mobile:focus{background:rgba(255,255,255,0.05);
        color:var(--cream)}
      .mobile-drawer .nav-btn-mobile.active{color:var(--red);
        border-left:3px solid var(--red);padding-left:21px}
      .mobile-drawer-divider{display:block;height:1px;background:rgba(255,255,255,0.08);
        margin:8px 20px}
      .mobile-drawer .nav-btn-mobile.btn-cta{margin:8px 20px;width:auto;
        text-align:center;border-radius:3px}
      .mobile-drawer .nav-btn-mobile.btn-red{background:var(--red);
        color:white;padding:12px 20px}
      .mobile-drawer .nav-btn-mobile.btn-ghost{border:1.5px solid rgba(255,255,255,0.15);
        color:rgba(245,240,232,0.7);padding:12px 20px;margin-top:4px}
    }
    @media(max-width:480px){
      .tile-grid{grid-template-columns:1fr}
      .tile-inner{padding:22px 20px 18px}
    }

    /* Admin Dashboard mobile responsive */
    @media(max-width:768px){
      .admin-tabs{flex-wrap:wrap;gap:0}
      .admin-tab{padding:8px 12px;font-size:12px;flex-shrink:0}
      .queue-card{flex-direction:column;align-items:flex-start}
      .queue-actions{width:100%}
      .queue-actions button{flex:1}
      .page-wrap{overflow-x:hidden;max-width:100vw}
    }

    /* SKIP LINK (accessibility) */
    .skip-link{position:absolute;top:-100%;left:50%;transform:translateX(-50%);
      background:var(--red);color:white;padding:12px 24px;border-radius:0 0 4px 4px;
      font-weight:700;font-size:14px;z-index:999;text-decoration:none;
      transition:top 0.2s}
    .skip-link:focus{top:0}

    /* TOAST accessibility */
    .toast{position:fixed;bottom:24px;right:24px;z-index:300;background:var(--black);
      color:var(--cream);padding:13px 18px;border-radius:5px;box-shadow:var(--shadow-lg);
      font-size:14px;font-weight:500;border-left:4px solid var(--green);
      animation:slideIn 0.3s ease;max-width:320px}
  `;

  // ── HOME ──────────────────────────────────────────────────────────────────
  const HomeView = () => (
    <>
      {/* Hero — full viewport, full-bleed background image */}
      <div className="hero">
        {/* Full-bleed hero background image */}
        <div style={{position:"absolute",inset:0,zIndex:0}}>
          <img src={HERO_IMAGE_URL} alt="Tennis court"
            style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:"center"}}/>
          <div className="hero-overlay" style={{position:"absolute",inset:0,
            background:"linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 40%, rgba(0,0,0,0.15) 100%)"}}/>
        </div>

        {/* Hero content */}
        <div className="hero-content">
          <div className="hero-tag">
            <div className="hero-tag-line"/>
            <div className="hero-tag-text">Columbus, Indiana · Est. 2026</div>
          </div>

          <div className="hero-body">
            {/* Left col — stacked title with TennisBallC replacing DancingCMark */}
            <div className="hero-title-col">
              <div className="hero-title">
                <div style={{display:"flex",alignItems:"center",gap:"0px",marginBottom:"12px"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"center",width:"220px",flexShrink:0,marginTop:"-28px"}}>
                    <TennisBallC size={220}/>
                  </div>
                  <span style={{marginLeft:"-24px"}}>OLUMBUS</span>
                </div>
                <div className="outline" style={{
                  display:"flex",alignItems:"center",justifyContent:"flex-start",
                  margin:"0",marginBottom:"12px",paddingLeft:"69px"
                }}>
                  <span style={{fontSize:"1.16em",letterSpacing:"1px",
                    WebkitTextFillColor:"#C8D82E",WebkitTextStroke:"7px rgba(245,240,232,0.9)",
                    paintOrder:"stroke fill"}}>T</span><span>ENNIS</span>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:"0px",marginTop:"0"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"center",width:"220px",flexShrink:0,marginTop:"-28px"}}>
                    <TennisBallC size={220}/>
                  </div>
                  <span style={{marginLeft:"-24px"}}>LUB</span>
                </div>
              </div>
            </div>

            {/* Right col — supporting text aligned to bottom of title */}
            <div className="hero-sub-col">
              <p className="hero-sub">
                Your FREE community hub for court locations, players, instructors, and everything else tennis in Columbus, Indiana and the surrounding area.
              </p>
            </div>
          </div>
        </div>

        {/* Scroll cue — centered chevron arrow pointing down */}
        <div className="hero-scroll-cue" onClick={()=>window.scrollBy({top:window.innerHeight,behavior:'smooth'})}>
          <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"11px",letterSpacing:"4px",
            color:"rgba(245,240,232,0.4)"}}>SCROLL</span>
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:"5px",marginTop:"2px"}}>
            <svg width="32" height="16" viewBox="0 0 32 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <polyline points="2,2 16,14 30,2" stroke="rgba(245,240,232,0.7)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <svg width="32" height="16" viewBox="0 0 32 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{opacity:0.45}}>
              <polyline points="2,2 16,14 30,2" stroke="rgba(245,240,232,0.7)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
      </div>

      {/* Tile Grid */}
      <div className="tile-section">
        <div className="tile-section-title">Explore the Club</div>
        <div className="tile-grid">

{/* Find a Playing Partner */}
          <div className="tile" onClick={()=>nav(loggedIn?VIEWS.CONNECTIONS:VIEWS.LOGIN)}>
            <div className="tile-accent"/>
            {!loggedIn && <div className="tile-members-badge">MEMBERS ONLY</div>}
            <div className="tile-inner">
              <div className="tile-icon"><IconHandshake size={56}/></div>
              <div className="tile-title">Find A<br/>Playing Partner</div>
              <div className="tile-desc">Connect with local players at your skill level and with compatible availability.</div>
              <div className="tile-cta">CTC Connections →</div>
            </div>
          </div>

          {/* Blog */}
          <div className="tile" onClick={()=>nav(VIEWS.BLOG)}>
            <div className="tile-accent" style={{background:"var(--gold)"}}/>
            <div className="tile-inner">
              <div className="tile-icon"><IconEvents size={56}/></div>
              <div className="tile-title">Events &amp;<br/>News</div>
              <div className="tile-desc">Community events, announcements, and tennis news from the CTC admin team.</div>
              <div className="tile-cta">Read Latest →</div>
            </div>
          </div>

          {/* Login / Register */}
          <div className="tile" onClick={()=>nav(loggedIn?VIEWS.DASHBOARD:VIEWS.LOGIN)}>
            <div className="tile-accent charcoal"/>
            <div className="tile-inner">
              <div className="tile-icon"><IconMember size={56} loggedIn={loggedIn}/></div>
              <div className="tile-title">{loggedIn ? <span>Member<br/>Dashboard</span> : <span>Member<br/>Login</span>}</div>
              <div className="tile-desc">{loggedIn?"Access your profile, manage contact requests, and explore the full directory.":"Register or sign in to access CTC Connections and your member profile."}</div>
              <div className="tile-cta">{loggedIn?"Go to Dashboard →":"Sign In / Register →"}</div>
            </div>
          </div>

{/* Courts */}
          <div className="tile" onClick={()=>nav(VIEWS.COURTS)}>
            <div className="tile-accent gold"/>
            <div className="tile-inner">
              <div className="tile-icon"><IconCourts size={56}/></div>
              <div className="tile-title">Court<br/>Locations</div>
              <div className="tile-desc">Find public and club tennis courts in Columbus, Indiana and surrounding communities.</div>
              <div className="tile-cta">View Courts →</div>
            </div>
          </div>

          {/* Instructors */}
          <div className="tile" onClick={()=>nav(VIEWS.INSTRUCTORS)}>
            <div className="tile-accent" style={{background:"#4A7C4A"}}/>
            <div className="tile-inner">
              <div className="tile-icon"><IconInstructor size={56}/></div>
              <div className="tile-title">Lesson<br/>Instructors</div>
              <div className="tile-desc">Discover qualified tennis instructors offering lessons for all ages and skill levels.</div>
              <div className="tile-cta">Browse Instructors →</div>
            </div>
          </div>

          {/* Match Bulletin Board */}
          <div className="tile" onClick={()=>nav(loggedIn?VIEWS.BULLETIN:VIEWS.LOGIN)}>
            <div className="tile-accent" style={{background:"#B07D2A"}}/>
            {!loggedIn && <div className="tile-members-badge">MEMBERS ONLY</div>}
            <div className="tile-inner">
              <div className="tile-icon"><IconBulletin size={56}/></div>
              <div className="tile-title">Match<br/>Bulletin Board</div>
              <div className="tile-desc">Post a time you're looking to play and find others to join you on the court.</div>
              <div className="tile-cta">View Board →</div>
            </div>
          </div>

          {/* Stringing */}
          <div className="tile" onClick={()=>nav(VIEWS.STRINGING)}>
            <div className="tile-accent" style={{background:"#5B4FCF"}}/>
            <div className="tile-inner">
              <div className="tile-icon"><IconStringing size={56}/></div>
              <div className="tile-title">Racquet<br/>Stringing</div>
              <div className="tile-desc">Local stringing services and technicians who can restring your racquet.</div>
              <div className="tile-cta">Find a Stringer →</div>
            </div>
          </div>

          {/* Equipment Exchange */}
          <div className="tile" onClick={()=>nav(loggedIn?VIEWS.EXCHANGE:VIEWS.LOGIN)}>
            <div className="tile-accent" style={{background:"#3A7D44"}}/>
            {!loggedIn && <div className="tile-members-badge">MEMBERS ONLY</div>}
            <div className="tile-inner">
              <div className="tile-icon"><IconExchange size={56}/></div>
              <div className="tile-title">Equipment<br/>Exchange</div>
              <div className="tile-desc">Buy, sell, or give away tennis equipment with other CTC members.</div>
              <div className="tile-cta">Browse Listings →</div>
            </div>
          </div>

          {/* Challenge Ladders — Coming Soon */}
          <div className="tile" style={{cursor:"default"}}>
            <div className="tile-accent" style={{background:"#0057A8"}}/>
            <div className="tile-coming-soon"/>
            <div className="tile-inner">
              <div className="tile-icon">
                {/* Pyramid icon — stacked blocks, yellow top block = #1 position */}
                <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Row 5 (bottom): 5 blocks */}
                  <rect x="3.5"  y="43" width="9" height="9" rx="1.5" stroke="rgba(245,240,232,0.75)" strokeWidth="1.4"/>
                  <rect x="13.5" y="43" width="9" height="9" rx="1.5" stroke="rgba(245,240,232,0.75)" strokeWidth="1.4"/>
                  <rect x="23.5" y="43" width="9" height="9" rx="1.5" stroke="rgba(245,240,232,0.75)" strokeWidth="1.4"/>
                  <rect x="33.5" y="43" width="9" height="9" rx="1.5" stroke="rgba(245,240,232,0.75)" strokeWidth="1.4"/>
                  <rect x="43.5" y="43" width="9" height="9" rx="1.5" stroke="rgba(245,240,232,0.75)" strokeWidth="1.4"/>
                  {/* Row 4: 4 blocks */}
                  <rect x="8.5"  y="33" width="9" height="9" rx="1.5" stroke="rgba(245,240,232,0.75)" strokeWidth="1.4"/>
                  <rect x="18.5" y="33" width="9" height="9" rx="1.5" stroke="rgba(245,240,232,0.75)" strokeWidth="1.4"/>
                  <rect x="28.5" y="33" width="9" height="9" rx="1.5" stroke="rgba(245,240,232,0.75)" strokeWidth="1.4"/>
                  <rect x="38.5" y="33" width="9" height="9" rx="1.5" stroke="rgba(245,240,232,0.75)" strokeWidth="1.4"/>
                  {/* Row 3: 3 blocks */}
                  <rect x="13.5" y="23" width="9" height="9" rx="1.5" stroke="rgba(245,240,232,0.75)" strokeWidth="1.4"/>
                  <rect x="23.5" y="23" width="9" height="9" rx="1.5" stroke="rgba(245,240,232,0.75)" strokeWidth="1.4"/>
                  <rect x="33.5" y="23" width="9" height="9" rx="1.5" stroke="rgba(245,240,232,0.75)" strokeWidth="1.4"/>
                  {/* Row 2: 2 blocks */}
                  <rect x="18.5" y="13" width="9" height="9" rx="1.5" stroke="rgba(245,240,232,0.75)" strokeWidth="1.4"/>
                  <rect x="28.5" y="13" width="9" height="9" rx="1.5" stroke="rgba(245,240,232,0.75)" strokeWidth="1.4"/>
                  {/* Row 1 (top): 1 block — yellow highlight = #1 */}
                  <rect x="23.5" y="3" width="9" height="9" rx="1.5" stroke="#F5C800" strokeWidth="1.8" fill="rgba(245,200,0,0.18)"/>
                </svg>
              </div>
              <div className="tile-title">Challenge<br/>Ladders</div>
              <div className="tile-desc">Compete in skill-based ladder rankings, challenge players above you, and climb to the top.</div>
              <div className="tile-cta" style={{color:"var(--gold)",opacity:0.5,cursor:"default"}}>
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginRight:"4px"}}>
                  <circle cx="6.5" cy="6.5" r="5.5" stroke="currentColor" strokeWidth="1.4"/>
                  <line x1="6.5" y1="3.5" x2="6.5" y2="6.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                  <line x1="6.5" y1="6.5" x2="9" y2="8.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>
                Coming Soon
              </div>
            </div>
          </div>

                  </div>
      </div>
    </>
  );

  // ── COURT MAP (Leaflet / OpenStreetMap) ─────────────────────────────────
  const CourtMap = ({ courts }) => {
    // Build a Google Maps embed URL centered on Columbus, IN
    // Individual court pins are handled via the "Get Directions" links on each card
    const courtsWithCoords = courts.filter(c => c.lat && c.lng);
    // If we have courts with coordinates, build a static map-like view using an embed
    const center = "39.2014,-85.9214";
    const embedUrl = `https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d48000!2d-85.9214!3d39.2014!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sus`;

    return (
      <div style={{marginTop:"32px",marginBottom:"40px"}}>
        <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"18px",letterSpacing:"2px",
          color:"var(--black)",marginBottom:"10px"}}>Court Map</div>
        <div style={{borderRadius:"6px",overflow:"hidden",border:"1px solid var(--grey-light)",
          boxShadow:"var(--shadow-sm)"}}>
          <iframe
            title="Columbus Indiana Tennis Courts Map"
            src={embedUrl}
            width="100%"
            height="420"
            style={{border:"none",display:"block"}}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
        {courtsWithCoords.length > 0 && (
          <div style={{marginTop:"12px",display:"flex",flexWrap:"wrap",gap:"8px"}}>
            {courtsWithCoords.map(c => {
              const mapsUrl = 'https://www.google.com/maps/search/' + encodeURIComponent(c.address || c.name + ' Columbus Indiana');
              return (
                <a key={c.id || c.name} href={mapsUrl} target="_blank" rel="noopener noreferrer"
                  style={{display:"inline-flex",alignItems:"center",gap:"4px",padding:"5px 12px",
                    background:"white",border:"1px solid var(--grey-light)",borderRadius:"20px",
                    fontSize:"12px",color:"var(--charcoal)",textDecoration:"none",fontWeight:"600",
                    fontFamily:"'DM Sans',sans-serif",transition:"all 0.15s"}}>
                  <span style={{color: c.is_closed ? "#e67e22" : c.type === "private" ? "#e67e22" : "#3A7D44"}}>●</span>
                  {c.name}
                </a>
              );
            })}
          </div>
        )}
        <div style={{fontSize:"11px",color:"var(--grey)",marginTop:"8px",letterSpacing:"0.5px"}}>
          📍 Click a court name above to get directions · Map shows Columbus, Indiana area
        </div>
      </div>
    );
  };

  // ── COURTS ────────────────────────────────────────────────────────────────
  const CourtsView = () => {
    const [courts, setCourts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(null);
    const emptyForm = {name:"",type:"public",address:"",courts_count:"",lights:"true",notes:"",lat:"",lng:""};
    const [form, setForm] = useState(emptyForm);
    const setF = (k,v) => setForm(f=>({...f,[k]:v}));

    useEffect(() => {
      supabase.from("courts").select("*").order("name")
        .then(({data}) => {
          if(data) setCourts(data);
          setLoading(false);
        });
    }, []);

    const handleSubmit = async () => {
      if(!form.name || !form.address) { showToast("Name and address are required.","error"); return; }
      const payload = {
        name: form.name, type: form.type, address: form.address,
        courts_count: form.courts_count ? parseInt(form.courts_count) : null,
        lights: form.lights, notes: form.notes,
        lat: form.lat ? parseFloat(form.lat) : null,
        lng: form.lng ? parseFloat(form.lng) : null,
        is_closed: false,
      };
      if(editingId) {
        const {error} = await supabase.from("courts").update(payload).eq("id", editingId);
        if(error) { showToast("Error updating court.","error"); return; }
        setCourts(prev => prev.map(c => c.id===editingId ? {...c,...payload,id:editingId} : c));
        showToast("Court updated!");
        setEditingId(null);
      } else {
        const {data,error} = await supabase.from("courts").insert(payload).select().single();
        if(error) { showToast("Error adding court.","error"); return; }
        setCourts(prev => [...prev, data].sort((a,b)=>a.name.localeCompare(b.name)));
        showToast("Court added!");
      }
      setForm(emptyForm);
      setShowForm(false);
    };

    const handleEdit = (c) => {
      setForm({
        name:c.name||"" , type:c.type||"public", address:c.address||"",
        courts_count:c.courts_count||"", lights:c.lights||"true",
        notes:c.notes||"", lat:c.lat||"", lng:c.lng||""
      });
      setEditingId(c.id);
      setShowForm(true);
      window.scrollTo(0,0);
    };

    const handleDelete = async (id, name) => {
      if(!pendingDelete || pendingDelete !== id) {
        setPendingDelete(id);
        showToast(`⚠️ Click Delete again on "${name}" to confirm.`, "error");
        setTimeout(() => setPendingDelete(null), 4000);
        return;
      }
      setPendingDelete(null);
      const {error} = await supabase.from("courts").delete().eq("id", id);
      if(error) { showToast("Error deleting court.","error"); return; }
      setCourts(prev => prev.filter(c => c.id !== id));
      showToast("Court deleted.","error");
    };

    const handleToggleClosed = async (c) => {
      const newVal = !c.is_closed;
      const {error} = await supabase.from("courts").update({is_closed:newVal}).eq("id", c.id);
      if(error) { showToast("Error updating court.","error"); return; }
      setCourts(prev => prev.map(x => x.id===c.id ? {...x,is_closed:newVal} : x));
      showToast(newVal ? `${c.name} marked as temporarily closed.` : `${c.name} reopened.`);
    };

    const lightsLabel = (l) => l===true||l==="true" ? "💡 Lighted" : l==="partial" ? "💡 Partially lighted" : "🌙 No lights";

    return (
      <div className="page-wrap">
        <div className="page-header">
          <button className="page-back" onClick={()=>nav(VIEWS.HOME)}>← Back to Home</button>
          <div className="page-title" style={{display:"flex",alignItems:"center",gap:"12px"}}>
            <IconCourts size={36} dark/>Court Locations</div>
          <div className="page-sub">Public and club courts in Columbus, Indiana and surrounding areas</div>
          <div className="red-bar"/>
        </div>

        {isAdmin && (
          <div style={{marginBottom:"20px"}}>
            <button className="submit-btn" style={{margin:0}} onClick={()=>{
              if(showForm&&!editingId){setShowForm(false);}
              else{setForm(emptyForm);setEditingId(null);setShowForm(true);}
            }}>
              {showForm&&!editingId?"Cancel":"+ Add Court"}
            </button>
          </div>
        )}

        {isAdmin && showForm && (
          <div className="form-section" style={{marginTop:0,marginBottom:"28px"}}>
            <div className="form-section-title">{editingId?"Edit Court":"Add New Court"}</div>
            <div className="form-grid">
              <div className="form-group"><label>Court / Facility Name *</label>
                <input value={form.name} onChange={e=>setF("name",e.target.value)} placeholder="Donner Park Tennis Courts"/></div>
              <div className="form-group"><label>Type</label>
                <select value={form.type} onChange={e=>setF("type",e.target.value)}>
                  <option value="public">Public</option>
                  <option value="private">Private / Club</option>
                </select></div>
              <div className="form-group full"><label>Address *</label>
                <input value={form.address} onChange={e=>setF("address",e.target.value)} placeholder="750 Donner Dr, Columbus, IN 47201"/></div>
              <div className="form-group"><label>Number of Courts</label>
                <input type="number" min="1" value={form.courts_count} onChange={e=>setF("courts_count",e.target.value)} placeholder="4"/></div>
              <div className="form-group"><label>Lighting</label>
                <select value={form.lights} onChange={e=>setF("lights",e.target.value)}>
                  <option value="true">Lighted</option>
                  <option value="partial">Partially Lighted</option>
                  <option value="false">No Lights</option>
                </select></div>
              <div className="form-group"><label>Latitude (optional)</label>
                <input value={form.lat} onChange={e=>setF("lat",e.target.value)} placeholder="39.2014"/></div>
              <div className="form-group"><label>Longitude (optional)</label>
                <input value={form.lng} onChange={e=>setF("lng",e.target.value)} placeholder="-85.9214"/></div>
              <div className="form-group full"><label>Notes</label>
                <textarea value={form.notes} onChange={e=>setF("notes",e.target.value)}
                  placeholder="Hours, surface type, reservations, public access info..."/></div>
            </div>
            <div style={{display:"flex",gap:"12px"}}>
              <button className="submit-btn" style={{margin:0}} onClick={handleSubmit}>
                {editingId?"Save Changes →":"Add Court →"}
              </button>
              <button onClick={()=>{setShowForm(false);setEditingId(null);setForm(emptyForm);}}
                style={{padding:"12px 20px",background:"transparent",border:"1.5px solid var(--grey-light)",
                  borderRadius:"3px",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontWeight:"600"}}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{color:"var(--grey)",padding:"40px 0",textAlign:"center"}}>Loading courts...</div>
        ) : (
          <div className="court-list">
            {courts.map(c=>(
              <div className={`court-card${c.type==="private"?" private":""}`} key={c.id}
                style={{opacity:c.is_closed?0.6:1,position:"relative"}}>
                {c.is_closed && (
                  <div style={{position:"absolute",top:"10px",right:isAdmin?"120px":"12px",
                    background:"#e67e22",color:"white",fontSize:"10px",fontWeight:"700",
                    letterSpacing:"1.5px",padding:"3px 8px",borderRadius:"3px"}}>
                    TEMPORARILY CLOSED
                  </div>
                )}
                <div className="court-card-header">
                  <div className="court-name">{c.name}</div>
                  <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
                    <span className={`court-badge ${c.type}`}>{c.type}</span>
                    {isAdmin && (
                      <div style={{display:"flex",gap:"6px"}}>
                        <button onClick={()=>handleToggleClosed(c)}
                          style={{fontSize:"11px",fontWeight:"700",padding:"3px 8px",borderRadius:"3px",
                            cursor:"pointer",border:"1.5px solid",fontFamily:"'DM Sans',sans-serif",
                            background:c.is_closed?"#3A7D44":"#e67e22",
                            borderColor:c.is_closed?"#3A7D44":"#e67e22",color:"white"}}>
                          {c.is_closed?"Reopen":"Close"}
                        </button>
                        <button onClick={()=>handleEdit(c)}
                          style={{fontSize:"11px",fontWeight:"700",padding:"3px 8px",borderRadius:"3px",
                            cursor:"pointer",border:"1.5px solid var(--charcoal)",
                            background:"var(--charcoal)",color:"white",fontFamily:"'DM Sans',sans-serif"}}>
                          Edit
                        </button>
                        <button onClick={()=>handleDelete(c.id,c.name)}
                          style={{fontSize:"11px",fontWeight:"700",padding:"3px 8px",borderRadius:"3px",
                            cursor:"pointer",border:"1.5px solid var(--red)",fontFamily:"'DM Sans',sans-serif",
                            background:pendingDelete===c.id?"#8B0000":"var(--red)",color:"white"}}>
                          {pendingDelete===c.id?"Confirm?":"Delete"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="court-meta">
                  <span className="court-meta-item">🎾 {c.courts_count} {c.courts_count===1?"court":"courts"}</span>
                  <span className="court-meta-item">{lightsLabel(c.lights)}</span>
                </div>
                <div className="court-addr">📌 {c.address}</div>
                <div className="court-notes">{c.notes}</div>
              </div>
            ))}
          </div>
        )}

        {/* OpenStreetMap via Leaflet */}
        <CourtMap courts={courts} />
      </div>
    );
  };

  // ── INSTRUCTORS ───────────────────────────────────────────────────────────
  const InstructorsView = () => {
    const [form, setForm] = useState({name:"",email:"",phone:"",lessonTypes:[],notes:""});
    const [submitted, setSubmitted] = useState(false);
    const [activeFilter, setActiveFilter] = useState(null);
    const set=(k,v)=>setForm(f=>({...f,[k]:v}));
    const toggleLT=(v)=>setForm(f=>({...f,lessonTypes:toggleArr(f.lessonTypes,v)}));

    const sortedInstructors = [...instructors]
      .filter(i=>i.approved)
      .sort((a,b)=>{
        const lastA = a.name.trim().split(" ").slice(-1)[0];
        const lastB = b.name.trim().split(" ").slice(-1)[0];
        return lastA.localeCompare(lastB);
      });

    const filtered = activeFilter
      ? sortedInstructors.filter(i=>i.lessonTypes.includes(activeFilter))
      : sortedInstructors;

    return (
      <div className="page-wrap">
        <div className="page-header">
          <button className="page-back" onClick={()=>nav(VIEWS.HOME)}>← Back to Home</button>
          <div className="page-title" style={{display:"flex",alignItems:"center",gap:"12px"}}>
            <IconInstructor size={36} dark/>Lesson Instructors</div>
          <div className="page-sub">Qualified tennis instructors in the Columbus, Indiana area</div>
          <div className="red-bar"/>
        </div>

        {/* Filter bar */}
        <div style={{marginBottom:"20px"}}>
          <div style={{fontSize:"11px",fontWeight:"700",letterSpacing:"2px",textTransform:"uppercase",
            color:"var(--grey)",marginBottom:"10px"}}>Filter by Lesson Type</div>
          <div className="pill-group">
            <button
              onClick={()=>setActiveFilter(null)}
              style={{padding:"6px 14px",borderRadius:"20px",border:"1.5px solid",cursor:"pointer",
                fontSize:"12px",fontWeight:"600",fontFamily:"'DM Sans',sans-serif",transition:"all 0.15s",
                background:activeFilter===null?"var(--red)":"transparent",
                borderColor:activeFilter===null?"var(--red)":"var(--grey-light)",
                color:activeFilter===null?"white":"var(--grey)"}}>
              All
            </button>
            {LESSON_TYPES.map(t=>(
              <button key={t}
                onClick={()=>setActiveFilter(activeFilter===t?null:t)}
                style={{padding:"6px 14px",borderRadius:"20px",border:"1.5px solid",cursor:"pointer",
                  fontSize:"12px",fontWeight:"600",fontFamily:"'DM Sans',sans-serif",transition:"all 0.15s",
                  background:activeFilter===t?"var(--red)":"transparent",
                  borderColor:activeFilter===t?"var(--red)":"var(--grey-light)",
                  color:activeFilter===t?"white":"var(--grey)"}}>
                {t}
              </button>
            ))}
          </div>
          {activeFilter && (
            <div style={{fontSize:"12px",color:"var(--grey)",marginTop:"10px"}}>
              Showing {filtered.length} instructor{filtered.length!==1?"s":""} offering <strong>{activeFilter}</strong> lessons
              <button onClick={()=>setActiveFilter(null)}
                style={{marginLeft:"8px",background:"none",border:"none",color:"var(--red)",
                  cursor:"pointer",fontSize:"12px",fontWeight:"700"}}>✕ Clear</button>
            </div>
          )}
        </div>

        <div className="card-grid">
          {filtered.length > 0 ? filtered.map(i=>(
            <div className="card" key={i.id}>
              <div className="card-body">
                <div className="card-name">{i.name}</div>
                <div className="lesson-chips">
                  {(i.lessonTypes||[]).map(t=><span key={t} className="lesson-chip">{t}</span>)}
                </div>
                <div className="card-contact">
                  <a href={`mailto:${i.email}`}>✉️ {i.email}</a>
                  {i.phone && <span>📞 {i.phone}</span>}
                </div>
                {i.notes && <div className="card-notes">"{i.notes}"</div>}
              </div>
            </div>
          )) : (
            <div style={{gridColumn:"1/-1",textAlign:"center",padding:"40px",color:"var(--grey)",fontSize:"14px"}}>
              No instructors found offering <strong>{activeFilter}</strong> lessons yet.
            </div>
          )}
        </div>

        <hr className="divider"/>

        {!submitted ? (
          <div className="form-section">
            <div className="form-section-title">{isAdmin?"Add an Instructor":"Submit Your Listing"}</div>
            {!isAdmin && <p style={{fontSize:"13px",color:"var(--grey)",marginBottom:"16px"}}>Submit your own instructor listing for admin review. Once approved it will appear in the directory above.</p>}
            <div className="form-grid">
              <div className="form-group"><label>Full Name *</label><input value={form.name} onChange={e=>set("name",e.target.value)} placeholder="Maria Sandoval"/></div>
              <div className="form-group"><label>Email Address *</label><input type="email" value={form.email} onChange={e=>set("email",e.target.value)} placeholder="maria@email.com"/></div>
              <div className="form-group"><label>Cell Phone (optional)</label><input value={form.phone} onChange={e=>set("phone",e.target.value)} placeholder="812-555-0100"/></div>
              <div className="form-group full">
                <label>Lesson Types Offered</label>
                <div className="pill-group">
                  {LESSON_TYPES.map(t=>(
                    <AvailPill key={t} label={t} active={form.lessonTypes.includes(t)} onClick={()=>toggleLT(t)}/>
                  ))}
                </div>
              </div>
              <div className="form-group full">
                <label>Additional Notes <span style={{fontWeight:400,textTransform:"none",fontSize:"11px"}}>(max 100 characters)</span></label>
                <input maxLength={100} value={form.notes} onChange={e=>set("notes",e.target.value)} placeholder="e.g. USPTA certified, available weekends..."/>
                <span style={{fontSize:"11px",color:"var(--grey)",textAlign:"right"}}>{form.notes.length}/100</span>
              </div>
            </div>
            <button className="submit-btn" onClick={()=>{
              if(!form.name||!form.email){showToast("Please fill in required fields.","error");return;}
              if(isAdmin){
                supabase.from("instructors").insert({
                  name:form.name, email:form.email, phone:form.phone,
                  lesson_types:form.lessonTypes, notes:form.notes, approved:true
                }).select().single().then(({data,error})=>{
                  if(!error&&data) setInstructors(prev=>[...prev,{
                    id:data.id, name:data.name, email:data.email, phone:data.phone,
                    lessonTypes:data.lesson_types||[], notes:data.notes, approved:true
                  }]);
                });
                showToast("Instructor listing added!");
                setForm({name:"",email:"",phone:"",lessonTypes:[],notes:""});
              } else {
                // Non-admin: submit for review (approved=false, admin will approve)
                supabase.from("instructors").insert({
                  name:form.name, email:form.email, phone:form.phone,
                  lesson_types:form.lessonTypes, notes:form.notes, approved:false
                }).then(({error})=>{
                  if(error) showToast("Error submitting listing. Please try again.","error");
                });
                setSubmitted(true);
              }
            }}>
              {isAdmin?"Add to Directory →":"Submit for Review →"}
            </button>
          </div>
        ) : (
          <div style={{background:"white",borderRadius:"6px",padding:"28px",textAlign:"center",boxShadow:"var(--shadow-sm)"}}>
            <TennisBall size={48}/>
            <h3 style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"24px",letterSpacing:"2px",margin:"16px 0 8px"}}>Listing Submitted!</h3>
            <p style={{color:"var(--grey)",fontSize:"14px"}}>An admin will review your submission and add it to the directory once approved.</p>
          </div>
        )}
      </div>
    );
  };

  // ── STRINGING ─────────────────────────────────────────────────────────────
  const StringingView = () => {
    const [form, setForm] = useState({name:"",email:"",phone:"",notes:""});
    const [submitted, setSubmitted] = useState(false);
    const set=(k,v)=>setForm(f=>({...f,[k]:v}));

    return (
      <div className="page-wrap">
        <div className="page-header">
          <button className="page-back" onClick={()=>nav(VIEWS.HOME)}>← Back to Home</button>
          <div className="page-title" style={{display:"flex",alignItems:"center",gap:"12px"}}>
            <IconStringing size={36} dark/>Racquet Stringing</div>
          <div className="page-sub">Local stringing services and technicians</div>
          <div className="red-bar"/>
        </div>

        <div className="card-grid">
          {stringers.filter(s=>s.approved).map(s=>(
            <div className="card card-gold" key={s.id}>
              <div className="card-body">
                <div className="card-name">{s.name}</div>
                <div className="card-contact">
                  <a href={`mailto:${s.email}`}>✉️ {s.email}</a>
                  {s.phone && <span>📞 {s.phone}</span>}
                </div>
                {s.notes && <div className="card-notes">"{s.notes}"</div>}
              </div>
            </div>
          ))}
        </div>

        <hr className="divider"/>

        {!submitted ? (
          <div className="form-section">
            <div className="form-section-title">{isAdmin?"Add a Stringer":"Submit Your Listing"}</div>
            {!isAdmin && <p style={{fontSize:"13px",color:"var(--grey)",marginBottom:"16px"}}>Submit your own stringing service listing for admin review.</p>}
            <div className="form-grid">
              <div className="form-group"><label>Name or Business Name *</label><input value={form.name} onChange={e=>set("name",e.target.value)} placeholder="String Theory Columbus"/></div>
              <div className="form-group"><label>Email Address *</label><input type="email" value={form.email} onChange={e=>set("email",e.target.value)} placeholder="info@email.com"/></div>
              <div className="form-group"><label>Phone (optional)</label><input value={form.phone} onChange={e=>set("phone",e.target.value)} placeholder="812-555-0100"/></div>
              <div className="form-group full">
                <label>Additional Notes <span style={{fontWeight:400,textTransform:"none",fontSize:"11px"}}>(brands, turnaround, pricing, etc.)</span></label>
                <textarea value={form.notes} onChange={e=>set("notes",e.target.value)} placeholder="e.g. Babolat & Wilson strings in stock. 24–48hr turnaround. $25 labor."/>
              </div>
            </div>
            <button className="submit-btn" onClick={()=>{
              if(!form.name||!form.email){showToast("Please fill in required fields.","error");return;}
              if(isAdmin){
                supabase.from("stringers").insert({
                  name:form.name, email:form.email, phone:form.phone,
                  notes:form.notes, approved:true
                }).select().single().then(({data,error})=>{
                  if(!error&&data) setStringers(prev=>[...prev,{
                    id:data.id, name:data.name, email:data.email,
                    phone:data.phone, notes:data.notes, approved:true
                  }]);
                });
                showToast("Stringer listing added!");
                setForm({name:"",email:"",phone:"",notes:""});
              } else {
                // Non-admin: submit for review (approved=false, admin will approve)
                supabase.from("stringers").insert({
                  name:form.name, email:form.email, phone:form.phone,
                  notes:form.notes, approved:false
                }).then(({error})=>{
                  if(error) showToast("Error submitting listing. Please try again.","error");
                });
                setSubmitted(true);
              }
            }}>
              {isAdmin?"Add to Directory →":"Submit for Review →"}
            </button>
          </div>
        ) : (
          <div style={{background:"white",borderRadius:"6px",padding:"28px",textAlign:"center",boxShadow:"var(--shadow-sm)"}}>
            <TennisBall size={48}/>
            <h3 style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"24px",letterSpacing:"2px",margin:"16px 0 8px"}}>Listing Submitted!</h3>
            <p style={{color:"var(--grey)",fontSize:"14px"}}>An admin will review your submission shortly.</p>
          </div>
        )}
      </div>
    );
  };

  // ── BLOG ──────────────────────────────────────────────────────────────────
  const BlogView = () => {
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({title:"",body:""});
    const [imageFiles, setImageFiles] = useState([]); // actual File objects
    const [imagePreviews, setImagePreviews] = useState([]); // preview data URLs
    const [publishing, setPublishing] = useState(false);
    const [lightbox, setLightbox] = useState(null);
    const [editingPost, setEditingPost] = useState(null);
    const [editPostForm, setEditPostForm] = useState({title:"",body:""});
    const [editImageFiles, setEditImageFiles] = useState([]); // new File objects for edit
    const [editImagePreviews, setEditImagePreviews] = useState([]); // preview data URLs for edit
    const [savingPost, setSavingPost] = useState(false);
    const set=(k,v)=>setForm(f=>({...f,[k]:v}));

    const handleFileSelect = (e) => {
      const files = Array.from(e.target.files).slice(0, 3);
      setImageFiles(files);
      // Generate preview data URLs via FileReader (more reliable than blob URLs)
      Promise.all(files.map(f => new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(f);
      }))).then(setImagePreviews);
    };

    const removeImage = (idx) => {
      setImageFiles(prev => prev.filter((_, i) => i !== idx));
      setImagePreviews(prev => prev.filter((_, i) => i !== idx));
    };

    const handleEditFileSelect = (e) => {
      const existingCount = (editPostForm.images || []).length;
      const maxNew = 3 - existingCount;
      if(maxNew <= 0) { showToast("Maximum 3 images per post.", "error"); return; }
      const files = Array.from(e.target.files).slice(0, maxNew);
      setEditImageFiles(files);
      Promise.all(files.map(f => new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(f);
      }))).then(setEditImagePreviews);
    };

    const removeEditNewImage = (idx) => {
      setEditImageFiles(prev => prev.filter((_, i) => i !== idx));
      setEditImagePreviews(prev => prev.filter((_, i) => i !== idx));
    };

    const handlePublish = async () => {
      if(!form.title||!form.body){showToast("Title and body are required.","error");return;}
      setPublishing(true);

      // Upload images to Supabase Storage
      const uploadedUrls = [];
      for (const file of imageFiles) {
        const ext = file.name.split('.').pop().toLowerCase();
        const fileName = `${Date.now()}-${Math.random().toString(36).slice(2,8)}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from('post-images')
          .upload(fileName, file, { contentType: file.type });
        if (uploadError) {
          showToast(`Error uploading ${file.name}: ${uploadError.message}`, "error");
          setPublishing(false);
          return;
        }
        const { data: urlData } = supabase.storage
          .from('post-images')
          .getPublicUrl(fileName);
        uploadedUrls.push(urlData.publicUrl);
      }

      // Insert post with uploaded image URLs
      const { data, error } = await supabase.from("posts").insert({
        title: form.title, body: form.body,
        images: uploadedUrls, author: "CTC Admin",
      }).select().single();
      setPublishing(false);
      if(error){ showToast("Error publishing post.","error"); return; }
      const dateStr = new Date(data.created_at).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"});
      setPosts(prev=>[{id:data.id,title:data.title,body:data.body,
        images:data.images||[],date:dateStr,author:data.author},...prev]);
      setForm({title:"",body:""});
      setImageFiles([]);
      setImagePreviews([]);
      setShowForm(false);
      showToast("Post published!");
    };

    const handleEditPost = (p) => {
      setEditingPost(p.id);
      setEditPostForm({title:p.title, body:p.body, images: p.images || []});
      setEditImageFiles([]);
      setEditImagePreviews([]);
      window.scrollTo(0,0);
    };

    const handleSavePost = async () => {
      if(!editPostForm.title||!editPostForm.body){showToast("Title and body are required.","error");return;}
      setSavingPost(true);

      // Upload any new images added during edit
      const newUploadedUrls = [];
      for (const file of editImageFiles) {
        const ext = file.name.split('.').pop().toLowerCase();
        const fileName = `${Date.now()}-${Math.random().toString(36).slice(2,8)}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from('post-images')
          .upload(fileName, file, { contentType: file.type });
        if (uploadError) {
          showToast(`Error uploading ${file.name}: ${uploadError.message}`, "error");
          setSavingPost(false);
          return;
        }
        const { data: urlData } = supabase.storage
          .from('post-images')
          .getPublicUrl(fileName);
        newUploadedUrls.push(urlData.publicUrl);
      }

      const allImages = [...(editPostForm.images || []), ...newUploadedUrls];
      const { error } = await supabase.from("posts").update({
        title: editPostForm.title, body: editPostForm.body,
        images: allImages,
      }).eq("id", editingPost);
      setSavingPost(false);
      if(error){showToast("Error saving post.","error");return;}
      setPosts(prev=>prev.map(p=>p.id===editingPost?{...p,title:editPostForm.title,body:editPostForm.body,images:allImages}:p));
      setEditingPost(null);
      setEditImageFiles([]);
      setEditImagePreviews([]);
      showToast("✅ Post updated!");
    };

    return (
      <div className="page-wrap">
        {/* Lightbox overlay */}
        {lightbox && (
          <div className="lightbox-overlay" onClick={()=>setLightbox(null)}>
            <div className="lightbox-close" onClick={()=>setLightbox(null)}>✕</div>
            <img src={lightbox} alt="Enlarged" className="lightbox-img"
              onClick={e=>e.stopPropagation()}/>
          </div>
        )}

        <div className="page-header">
          <button className="page-back" onClick={()=>nav(VIEWS.HOME)}>← Back to Home</button>
          <div className="page-title" style={{display:"flex",alignItems:"center",gap:"12px"}}>
            <IconEvents size={36} dark/>Events &amp; News</div>
          <div className="page-sub">Community updates, events, and announcements from CTC</div>
          <div className="red-bar"/>
        </div>

        {isAdmin && (
          <div style={{marginBottom:"24px"}}>
            <button className="submit-btn" onClick={()=>setShowForm(s=>!s)}>
              {showForm?"Cancel":"+ New Post"}
            </button>
          </div>
        )}

        {isAdmin && showForm && (
          <div className="form-section" style={{marginTop:0,marginBottom:"28px"}}>
            <div className="form-section-title">Create New Post</div>
            <div className="form-grid">
              <div className="form-group full"><label>Post Title *</label>
                <input value={form.title} onChange={e=>set("title",e.target.value)} placeholder="Spring Mixer — April 12th"/>
              </div>
              <div className="form-group full"><label>Post Body *</label>
                <textarea style={{minHeight:"140px"}} value={form.body} onChange={e=>set("body",e.target.value)}
                  placeholder="Write your post content here..."/>
              </div>
              <div className="form-group full">
                <label>Attach Images <span style={{fontWeight:400,textTransform:"none",fontSize:"11px"}}>(JPG, PNG, WebP, GIF · max 5MB each · up to 3)</span></label>
                <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple
                  onChange={handleFileSelect}
                  style={{background:"white",padding:"6px"}}/>
                {imagePreviews.length > 0 && (
                  <div style={{display:"flex",gap:"12px",marginTop:"12px",flexWrap:"wrap"}}>
                    {imagePreviews.map((url, i) => (
                      <div key={i} style={{position:"relative"}}>
                        <img src={url} alt={`Preview ${i+1}`}
                          style={{width:"120px",height:"90px",objectFit:"cover",borderRadius:"6px",
                            border:"2px solid var(--grey-light)"}}/>
                        <button onClick={() => removeImage(i)}
                          style={{position:"absolute",top:"-6px",right:"-6px",width:"22px",height:"22px",
                            borderRadius:"50%",background:"var(--red)",color:"white",border:"none",
                            fontSize:"12px",fontWeight:"700",cursor:"pointer",display:"flex",
                            alignItems:"center",justifyContent:"center",lineHeight:1}}>✕</button>
                        <div style={{fontSize:"10px",color:"var(--grey)",marginTop:"4px",textAlign:"center"}}>
                          {imageFiles[i]?.name?.length > 15 ? imageFiles[i].name.slice(0,12)+"..." : imageFiles[i]?.name}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <button className="submit-btn" onClick={handlePublish} disabled={publishing}>
              {publishing ? "Uploading & Publishing…" : "Publish Post →"}
            </button>
          </div>
        )}

        {posts.map(p=>(
          <div className="post-card" key={p.id}>
            <div className="post-header">
              <div className="post-title">{p.title}</div>
              <div style={{display:"flex",alignItems:"center",gap:"12px"}}>
                <div className="post-meta">{p.author} · {p.date}</div>
                {isAdmin && (
                  <div style={{display:"flex",gap:"8px"}}>
                    <button onClick={()=>handleEditPost(p)}
                      style={{background:"none",border:"none",cursor:"pointer",color:"var(--charcoal)",
                        fontSize:"11px",fontWeight:"700",fontFamily:"'DM Sans',sans-serif",
                        letterSpacing:"1px",textTransform:"uppercase",padding:"2px 6px"}}>
                      Edit
                    </button>
                    <button onClick={async()=>{
                      await supabase.from("posts").delete().eq("id",p.id);
                      setPosts(prev=>prev.filter(x=>x.id!==p.id));
                      showToast("Post deleted.","error");
                    }} style={{background:"none",border:"none",cursor:"pointer",color:"var(--red)",
                      fontSize:"11px",fontWeight:"700",fontFamily:"'DM Sans',sans-serif",
                      letterSpacing:"1px",textTransform:"uppercase",padding:"2px 6px"}}>
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
            {editingPost===p.id ? (
              <div style={{padding:"16px 24px",borderTop:"1px solid var(--grey-light)"}}>
                <div className="form-group" style={{marginBottom:"12px"}}>
                  <label style={{fontWeight:"600",fontSize:"13px",display:"block",marginBottom:"6px"}}>Title</label>
                  <input value={editPostForm.title} onChange={e=>setEditPostForm(f=>({...f,title:e.target.value}))}
                    style={{width:"100%",padding:"8px 12px",border:"1px solid #ccc",borderRadius:"4px",
                      fontSize:"15px",boxSizing:"border-box"}}/>
                </div>
                <div className="form-group" style={{marginBottom:"12px"}}>
                  <label style={{fontWeight:"600",fontSize:"13px",display:"block",marginBottom:"6px"}}>Body</label>
                  <textarea value={editPostForm.body} onChange={e=>setEditPostForm(f=>({...f,body:e.target.value}))}
                    style={{width:"100%",padding:"8px 12px",border:"1px solid #ccc",borderRadius:"4px",
                      fontSize:"15px",minHeight:"120px",boxSizing:"border-box",resize:"vertical"}}/>
                </div>
                {editPostForm.images && editPostForm.images.length > 0 && (
                  <div style={{marginBottom:"12px"}}>
                    <label style={{fontWeight:"600",fontSize:"13px",display:"block",marginBottom:"8px"}}>
                      Attached Images <span style={{fontWeight:400,fontSize:"11px",color:"var(--grey)"}}>(click ✕ to remove)</span>
                    </label>
                    <div style={{display:"flex",gap:"12px",flexWrap:"wrap"}}>
                      {editPostForm.images.map((url, i) => (
                        <div key={i} style={{position:"relative"}}>
                          <img src={url} alt={`Image ${i+1}`}
                            style={{width:"120px",height:"90px",objectFit:"cover",borderRadius:"6px",
                              border:"2px solid var(--grey-light)"}}/>
                          <button onClick={() => setEditPostForm(f => ({...f, images: f.images.filter((_, idx) => idx !== i)}))}
                            style={{position:"absolute",top:"-6px",right:"-6px",width:"22px",height:"22px",
                              borderRadius:"50%",background:"var(--red)",color:"white",border:"none",
                              fontSize:"12px",fontWeight:"700",cursor:"pointer",display:"flex",
                              alignItems:"center",justifyContent:"center",lineHeight:1}}>✕</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Add new images to existing post */}
                {(editPostForm.images || []).length < 3 && (
                  <div style={{marginBottom:"12px"}}>
                    <label style={{fontWeight:"600",fontSize:"13px",display:"block",marginBottom:"6px"}}>
                      Add Images <span style={{fontWeight:400,fontSize:"11px",color:"var(--grey)"}}>(JPG, PNG, WebP, GIF · max 5MB each · up to {3 - (editPostForm.images || []).length} more)</span>
                    </label>
                    <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple
                      onChange={handleEditFileSelect}
                      style={{background:"white",padding:"6px"}}/>
                    {editImagePreviews.length > 0 && (
                      <div style={{display:"flex",gap:"12px",marginTop:"12px",flexWrap:"wrap"}}>
                        {editImagePreviews.map((url, i) => (
                          <div key={i} style={{position:"relative"}}>
                            <img src={url} alt={`New ${i+1}`}
                              style={{width:"120px",height:"90px",objectFit:"cover",borderRadius:"6px",
                                border:"2px solid #C8E632"}}/>
                            <button onClick={() => removeEditNewImage(i)}
                              style={{position:"absolute",top:"-6px",right:"-6px",width:"22px",height:"22px",
                                borderRadius:"50%",background:"var(--red)",color:"white",border:"none",
                                fontSize:"12px",fontWeight:"700",cursor:"pointer",display:"flex",
                                alignItems:"center",justifyContent:"center",lineHeight:1}}>✕</button>
                            <div style={{fontSize:"10px",color:"#3A7D44",marginTop:"4px",textAlign:"center",fontWeight:"600"}}>New</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div style={{display:"flex",gap:"10px"}}>
                  <button className="submit-btn" style={{margin:0}} onClick={handleSavePost} disabled={savingPost}>
                    {savingPost ? "Uploading & Saving…" : "Save Changes →"}
                  </button>
                  <button onClick={()=>{setEditingPost(null);setEditImageFiles([]);setEditImagePreviews([]);}}
                    style={{padding:"10px 18px",background:"transparent",border:"1.5px solid var(--grey-light)",
                      borderRadius:"3px",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontWeight:"600"}}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="post-body">{p.body}</div>
            )}
            {p.images.length>0 && (
              <div className="post-images">
                {p.images.map((img,i)=>(
                  <img key={i} src={img} alt={`Photo ${i+1}`} className="post-img"
                    onClick={()=>setLightbox(img)}/>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // ── CONNECTIONS ───────────────────────────────────────────────────────────
  const ConnectionsView = () => {
    const CURRENT_USER_ID = currentProfile?.id || null;
    const toggleDay  = d => setFilterDays(prev  => prev.includes(d)  ? prev.filter(x=>x!==d)  : [...prev,d]);
    const toggleTime = t => setFilterTimes(prev => prev.includes(t) ? prev.filter(x=>x!==t) : [...prev,t]);
    const anyFilter  = filterSkill!=="All" || filterDays.length>0 || filterTimes.length>0 || filterPlay!=="All" || filterConnection!=="All";

    // Use real approved+active members from Supabase (loaded in App state)
    const sorted = [...approvedMembers].filter(p => p.id !== CURRENT_USER_ID).sort((a,b)=>{
      const lastA = a.name.trim().split(" ").slice(-1)[0];
      const lastB = b.name.trim().split(" ").slice(-1)[0];
      return lastA.localeCompare(lastB);
    });

    const filtered = sorted.filter(p=>{
      if(filterSkill!=="All" && p.skill!==filterSkill) return false;
      if(filterDays.length>0  && !filterDays.some(d=>p.days.includes(d)))   return false;
      if(filterTimes.length>0 && !filterTimes.some(t=>p.times.includes(t))) return false;
      if(filterPlay!=="All"   && p.play!==filterPlay && p.play!=="Both")     return false;
      if(filterConnection!=="All") {
        const conn = sentRequests.find(r => r.to_id === p.id);
        const isConnected = conn?.status === "accepted";
        if(filterConnection==="Connected" && !isConnected) return false;
        if(filterConnection==="Not Connected" && isConnected) return false;
      }
      return true;
    });

    const pillBtn = (label, active, onClick) => (
      <button key={label} onClick={onClick} style={{
        padding:"5px 12px",borderRadius:"20px",border:"1.5px solid",cursor:"pointer",
        fontSize:"12px",fontWeight:"600",fontFamily:"'DM Sans',sans-serif",transition:"all 0.15s",
        background: active ? "var(--red)" : "transparent",
        borderColor: active ? "var(--red)" : "var(--grey-light)",
        color: active ? "white" : "var(--grey)",
      }}>{label}</button>
    );

    return (
      <div className="page-wrap">
        <div className="page-header">
          <button className="page-back" onClick={()=>nav(VIEWS.HOME)}>← Back to Home</button>
          <div className="page-title" style={{display:"flex",alignItems:"center",gap:"12px"}}>
            <IconHandshake size={36} dark/>CTC Connections</div>
          <div className="page-sub">Find players in Columbus, Indiana who match your level and schedule · {filtered.length} player{filtered.length!==1?"s":""} found</div>
          <div className="red-bar"/>
        </div>

        {/* Info note about connections */}
        <div style={{background:"#FFFDE7",border:"1px solid #F5E6A3",borderRadius:"6px",padding:"12px 16px",
          marginBottom:"20px",fontSize:"13px",color:"var(--charcoal)",lineHeight:"1.6"}}>
          <strong>📌 How Connections Work:</strong> When both you and another member approve a connection request,
          a confirmation email is sent to each of you containing the other's contact details (email and phone).
          Please keep your profile contact information current so your connections can reach you. Once connected,
          a member's tile will show <span style={{color:"#3A7D44",fontWeight:"700"}}>✅ Connected</span> — refer
          to your confirmation email for their contact info.
        </div>

        {/* Filter Panel */}
        <div className="filters" style={{flexDirection:"column",alignItems:"stretch",gap:"14px"}}>
          <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"16px",letterSpacing:"2px",
            color:"var(--charcoal)",textTransform:"uppercase",display:"flex",alignItems:"center",gap:"8px"}}>
            🔍 Filter Players
            <span style={{fontWeight:400,fontFamily:"'DM Sans',sans-serif",fontSize:"12px",
              letterSpacing:"0",textTransform:"none",color:"var(--grey)"}}>
              — narrow results by skill, availability, or play style
            </span>
          </div>
          <div style={{display:"flex",flexWrap:"wrap",gap:"20px",alignItems:"flex-end"}}>
            {/* Skill Level dropdown */}
            <div className="filter-group">
              <div className="filter-label">Skill Level</div>
              <select className="filter-select" value={filterSkill} onChange={e=>setFilterSkill(e.target.value)}>
                {["All",...SKILLS].map(o=><option key={o}>{o}</option>)}
              </select>
            </div>
            {/* Play Type dropdown */}
            <div className="filter-group">
              <div className="filter-label">Play Type</div>
              <select className="filter-select" value={filterPlay} onChange={e=>setFilterPlay(e.target.value)}>
                {["All",...PLAY_TYPES].map(o=><option key={o}>{o}</option>)}
              </select>
            </div>
            {/* Connection Status dropdown */}
            <div className="filter-group">
              <div className="filter-label">Connection Status</div>
              <select className="filter-select" value={filterConnection} onChange={e=>setFilterConnection(e.target.value)}>
                {["All","Connected","Not Connected"].map(o=><option key={o}>{o}</option>)}
              </select>
            </div>
            {anyFilter && (
              <button onClick={()=>{setFilterSkill("All");setFilterDays([]);setFilterTimes([]);setFilterPlay("All");setFilterConnection("All");}}
                style={{alignSelf:"flex-end",padding:"7px 13px",background:"transparent",
                  border:"1.5px solid var(--grey-light)",borderRadius:"3px",cursor:"pointer",
                  fontSize:"12px",color:"var(--grey)",fontFamily:"'DM Sans',sans-serif",fontWeight:"600"}}>
                Clear All ✕
              </button>
            )}
          </div>

          {/* Days multi-select pills */}
          <div>
            <div className="filter-label" style={{marginBottom:"8px"}}>Days Available <span style={{fontWeight:400,textTransform:"none",fontSize:"11px"}}>(select one or more)</span></div>
            <div style={{display:"flex",flexWrap:"wrap",gap:"7px"}}>
              {DAYS.map(d => pillBtn(d, filterDays.includes(d), ()=>toggleDay(d)))}
            </div>
          </div>

          {/* Times multi-select pills */}
          <div>
            <div className="filter-label" style={{marginBottom:"8px"}}>Time of Day <span style={{fontWeight:400,textTransform:"none",fontSize:"11px"}}>(select one or more)</span></div>
            <div style={{display:"flex",flexWrap:"wrap",gap:"7px"}}>
              {TIMES.map(t => pillBtn(t, filterTimes.includes(t), ()=>toggleTime(t)))}
            </div>
          </div>
        </div>

        <div className="player-grid">
          {filtered.map(p=>(
            <div className="player-card" key={p.id}>
              <div className="player-top">
                <div className="player-avatar">{p.initials}</div>
                <div>
                  <div className="player-name">{p.name}</div>
                  <RatingBadge ntrp={p.ntrp} skill={p.skill}/>
                </div>
              </div>
              <div style={{fontSize:"13px",color:"var(--grey)",marginBottom:"6px"}}>
                <span style={{fontWeight:"600",color:"var(--charcoal)"}}>Play Type: </span>
                {p.play==="Singles"?"🎾 Singles":p.play==="Doubles"?"👥 Doubles":"🎾👥 Both"}
              </div>
              <div style={{display:"flex",flexWrap:"wrap",gap:"4px",marginBottom:"4px"}}>
                {(p.days||[]).map(d=><span key={d} className="avail-chip">{d}</span>)}
              </div>
              <div style={{display:"flex",flexWrap:"wrap",gap:"4px"}}>
                {(p.times||[]).map(t=><span key={t} className="avail-chip avail-chip-time">{t}</span>)}
              </div>
              {(() => {
                const sent = sentRequests.find(r => r.to_id === p.id);
                if (sent?.status === "accepted") return (
                  <div style={{width:"100%",marginTop:"12px",padding:"8px",textAlign:"center",
                    fontSize:"12px",fontWeight:"700",color:"#3A7D44",letterSpacing:"0.5px"}}>
                    ✅ Connected
                  </div>
                );
                if (sent?.status === "pending") return (
                  <div style={{width:"100%",marginTop:"12px",padding:"8px",textAlign:"center",
                    fontSize:"12px",fontWeight:"700",color:"var(--grey)",letterSpacing:"0.5px"}}>
                    ⏳ Request Pending
                  </div>
                );
                if (sent?.status === "declined") return (
                  <button className="contact-req-btn" onClick={()=>setContactReq(p)}>Request Contact Info</button>
                );
                return <button className="contact-req-btn" onClick={()=>setContactReq(p)}>Request Contact Info</button>;
              })()}
            </div>
          ))}
          {filtered.length===0&&(
            <div style={{gridColumn:"1/-1",textAlign:"center",padding:"60px 20px",color:"var(--grey)"}}>
              <TennisBall size={48} opacity={0.3}/>
              <p style={{marginTop:"14px",fontSize:"15px"}}>No players match your filters.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── LOGIN ─────────────────────────────────────────────────────────────────
  const LoginView = ({ onLogin }) => {
    const [email, setEmail] = useState("");
    const [pass, setPass] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [forgotMode, setForgotMode] = useState(false);
    const [forgotEmail, setForgotEmail] = useState("");
    const [forgotSending, setForgotSending] = useState(false);
    const [forgotSent, setForgotSent] = useState(false);

    const handleLogin = async () => {
      const emailVal = document.getElementById('login-email')?.value || email;
      const passVal = document.getElementById('login-pass')?.value || pass;
      if (!emailVal || !passVal) { setError("Please enter your email and password."); return; }
      setLoading(true); setError("");
      const result = await onLogin(emailVal, passVal);
      if (result?.error) {
        setError(result.error);
        setLoading(false);
      }
    };

    const handleForgotPassword = async () => {
      const emailVal = document.getElementById('forgot-email')?.value || forgotEmail;
      if (!emailVal) { setError("Please enter your email address."); return; }
      setForgotSending(true); setError("");
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(emailVal, {
        redirectTo: window.location.origin,
      });
      setForgotSending(false);
      if (resetError) { setError(resetError.message); return; }
      setForgotSent(true);
    };

    return (
      <div className="auth-wrap">
        <svg style={{position:"absolute",inset:0,width:"100%",height:"100%",pointerEvents:"none",opacity:0.07}} viewBox="0 0 800 500" preserveAspectRatio="xMidYMid slice">
          <rect x="60" y="40" width="680" height="420" fill="none" stroke="var(--black)" strokeWidth="2.5"/>
          <line x1="60" y1="250" x2="740" y2="250" stroke="var(--black)" strokeWidth="3"/>
          <line x1="230" y1="40" x2="230" y2="460" stroke="var(--black)" strokeWidth="1.5"/>
          <line x1="570" y1="40" x2="570" y2="460" stroke="var(--black)" strokeWidth="1.5"/>
        </svg>
        <div className="auth-box">
          <div className="auth-head">
            <TennisBallC size={98}/>
            <div className="auth-title">{forgotMode ? "RESET PASSWORD" : "MEMBER LOGIN"}</div>
            <div className="auth-sub">COLUMBUS TENNIS CLUB</div>
          </div>
          <div className="auth-body">
            {forgotMode ? (
              forgotSent ? (
                <div style={{textAlign:"center",padding:"20px 0"}}>
                  <div style={{fontSize:"48px",marginBottom:"12px"}}>📧</div>
                  <div style={{fontSize:"16px",fontWeight:"700",marginBottom:"8px",color:"var(--charcoal)"}}>Check Your Email</div>
                  <div style={{fontSize:"13px",color:"var(--grey)",lineHeight:"1.6",marginBottom:"20px"}}>
                    If an account exists for that email address, we've sent a password reset link.
                    Please check your inbox (and spam folder) and click the link to set a new password.
                  </div>
                  <button onClick={()=>{setForgotMode(false);setForgotSent(false);setForgotEmail("");setError("");}}
                    style={{background:"none",border:"none",color:"var(--red)",cursor:"pointer",
                      fontFamily:"'DM Sans',sans-serif",fontSize:"14px",fontWeight:"600",
                      textDecoration:"underline",padding:0}}>
                    ← Back to Sign In
                  </button>
                </div>
              ) : (
                <div className="auth-form">
                  <div style={{fontSize:"13px",color:"var(--grey)",lineHeight:"1.6",marginBottom:"8px"}}>
                    Enter the email address associated with your CTC account and we'll send you a link to reset your password.
                  </div>
                  <div className="form-group"><label>Email Address</label>
                    <input id="forgot-email" type="email" autoComplete="email" placeholder="your@email.com"
                      defaultValue="" onChange={e=>setForgotEmail(e.target.value)}
                      onKeyDown={e=>{if(e.key==='Enter')handleForgotPassword();}}/></div>
                  <button className="submit-btn" onClick={handleForgotPassword} disabled={forgotSending}>
                    {forgotSending ? "Sending…" : "Send Reset Link →"}
                  </button>
                  {error && <div style={{color:"var(--red)",fontSize:"13px",marginTop:"8px",textAlign:"center"}}>{error}</div>}
                  <div style={{textAlign:"center",marginTop:"12px"}}>
                    <button onClick={()=>{setForgotMode(false);setError("");}}
                      style={{background:"none",border:"none",color:"var(--red)",cursor:"pointer",
                        fontFamily:"'DM Sans',sans-serif",fontSize:"13px",fontWeight:"600",
                        textDecoration:"underline",padding:0}}>
                      ← Back to Sign In
                    </button>
                  </div>
                </div>
              )
            ) : (
              <>
                <div className="auth-form">
                  <div className="form-group"><label>Email Address</label>
                    <input id="login-email" type="email" autoComplete="email" placeholder="your@email.com" defaultValue="" onChange={e=>setEmail(e.target.value)}/></div>
                  <div className="form-group"><label>Password</label>
                    <input id="login-pass" type="password" autoComplete="current-password" placeholder="••••••••" defaultValue="" onChange={e=>setPass(e.target.value)} onKeyDown={e=>{if(e.key==='Enter')handleLogin();}}/></div>
                  <div style={{textAlign:"right",marginTop:"-6px",marginBottom:"4px"}}>
                    <button onClick={()=>{setForgotMode(true);setError("");}}
                      style={{background:"none",border:"none",color:"var(--grey)",cursor:"pointer",
                        fontFamily:"'DM Sans',sans-serif",fontSize:"12px",fontWeight:"600",
                        padding:0,textDecoration:"underline"}}>
                      Forgot Password?
                    </button>
                  </div>
                  <button className="submit-btn" onClick={handleLogin}>
                    {loading ? "Signing In…" : "Sign In →"}
                  </button>
                  {error && <div style={{color:"var(--red)",fontSize:"13px",marginTop:"8px",textAlign:"center"}}>{error}</div>}
                </div>
                <div className="demo-note">🎾 New to CTC? Register below to request membership. Admin approval is required before your first login.</div>
                <div className="auth-switch">Not a member? <button onClick={()=>nav(VIEWS.REGISTER)} style={{background:"none",border:"none",color:"var(--red)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:"inherit",fontWeight:"600",padding:0,textDecoration:"underline"}}>Register here</button></div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── REGISTER ──────────────────────────────────────────────────────────────
  // RegisterView defined outside App
  // AdminView defined outside App

  // ── EQUIPMENT EXCHANGE ────────────────────────────────────────────────────
  const ExchangeView = () => {
    const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
    const now = Date.now();
    const CURRENT_USER = currentProfile?.name || "";
    const CURRENT_USER_ID = currentProfile?.id || null;

    const [catFilter, setCatFilter] = useState("All");
    const [showSold, setShowSold] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [showContact, setShowContact] = useState(null); // listing id
    const emptyForm = {title:"",category:"",listingType:"For Sale",price:"",description:"",contact:""};
    const [form, setForm] = useState(emptyForm);
    const setF = (k,v) => setForm(f=>({...f,[k]:v}));

    // Auto-open edit form if navigated here from Dashboard
    useEffect(() => {
      if(dashExchangeEditTarget.current) {
        const l = dashExchangeEditTarget.current;
        setForm({title:l.title||"",category:l.category||"",listingType:l.listingType||"For Sale",
          price:l.price||"",description:l.description||"",contact:l.contact||""});
        setEditingId(l.id);
        setShowForm(true);
        dashExchangeEditTarget.current = null;
      }
    }, []);

    // Safe date parse: append T12:00:00 to avoid UTC midnight → Eastern prior-day bug
    const safeDate = (d) => new Date(typeof d === 'string' && d.length === 10 ? d + "T12:00:00" : d);
    const daysLeft = (postedDate) =>
      Math.ceil((THIRTY_DAYS - (now - safeDate(postedDate).getTime())) / (24*60*60*1000));
    const isExpired = (l) => now - safeDate(l.postedDate).getTime() >= THIRTY_DAYS;

    const activeListings = exchangeListings
      .filter(l => !isExpired(l))
      .filter(l => showSold || !l.sold)
      .filter(l => catFilter === "All" || l.category === catFilter)
      .sort((a,b) => {
        if(a.sold && !b.sold) return 1;
        if(!a.sold && b.sold) return -1;
        return new Date(b.postedDate) - new Date(a.postedDate);
      });

    const handleSubmit = async () => {
      if(!form.title || !form.category || !form.description || !form.contact) {
        showToast("Please fill in all required fields.","error"); return;
      }
      if(form.listingType === "For Sale" && !form.price) {
        showToast("Please enter a price, or switch to Give Away.","error"); return;
      }
      if(editingId) {
        const { error } = await supabase.from("exchange_listings").update({
          title: form.title, category: form.category, listing_type: form.listingType,
          price: form.price, description: form.description, contact: form.contact,
        }).eq("id", editingId);
        if(error) { showToast("Error updating listing.","error"); return; }
        setExchangeListings(prev => prev.map(l => l.id === editingId ? {...l, ...form} : l));
        showToast("✏️ Listing updated!");
        setEditingId(null);
      } else {
        const { data, error } = await supabase.from("exchange_listings").insert({
          author_id: CURRENT_USER_ID, author_name: CURRENT_USER,
          title: form.title, category: form.category, listing_type: form.listingType,
          price: form.price, description: form.description, contact: form.contact,
          sold: false,
        }).select().single();
        if(error) { showToast("Error posting listing.","error"); return; }
        const newListing = {
          id: data.id, author: data.author_name, authorId: data.author_id,
          postedDate: data.created_at, title: data.title, category: data.category,
          listingType: data.listing_type, price: data.price,
          description: data.description, contact: data.contact, sold: data.sold,
        };
        setExchangeListings(prev => [newListing, ...prev]);
        showToast("🏷️ Listing posted to Equipment Exchange!");
        // Notify admins of new listing (fire and forget)
        fetch(EDGE_FN_URLS.NOTIFY_NEW_POST, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            postType: "Equipment Exchange",
            authorName: CURRENT_USER,
            title: form.title,
            detail: `${form.category} · ${form.listingType}${form.price ? " · $" + form.price : ""}`,
          }),
        }).catch(() => {});
      }
      setForm(emptyForm);
      setShowForm(false);
    };

    const handleMarkSold = async (id) => {
      const { error } = await supabase.from("exchange_listings").update({ sold: true }).eq("id", id);
      if(error) { showToast("Error updating listing.","error"); return; }
      setExchangeListings(prev => prev.map(l => l.id === id ? {...l, sold: true} : l));
      showToast("✅ Item marked as sold/given away. Thanks for keeping the board current!");
    };

    const handleDelete = async (id) => {
      const { error } = await supabase.from("exchange_listings").delete().eq("id", id);
      if(error) { showToast("Error removing listing.","error"); return; }
      setExchangeListings(prev => prev.filter(l => l.id !== id));
      showToast("🗑️ Listing removed.","error");
    };

    const handleEdit = (listing) => {
      setForm({title:listing.title, category:listing.category, listingType:listing.listingType,
        price:listing.price, description:listing.description, contact:listing.contact});
      setEditingId(listing.id);
      setShowForm(true);
      window.scrollTo(0,0);
    };

    const catCounts = ["All",...EXCHANGE_CATS].reduce((acc,cat) => {
      acc[cat] = cat === "All"
        ? exchangeListings.filter(l=>!isExpired(l)&&!l.sold).length
        : exchangeListings.filter(l=>!isExpired(l)&&!l.sold&&l.category===cat).length;
      return acc;
    },{});

    return (
      <div className="page-wrap">
        <div className="page-header">
          <button className="page-back" onClick={()=>nav(VIEWS.HOME)}>← Back to Home</button>
          <div className="page-title" style={{display:"flex",alignItems:"center",gap:"12px"}}>
            <IconExchange size={36} dark/>Equipment Exchange
          </div>
          <div className="page-sub">Members-only · Buy, sell, or give away tennis gear with fellow CTC members · Listings auto-archive after 30 days</div>
          <div className="red-bar" style={{background:"#3A7D44"}}/>
        </div>

        {/* Archive policy notice */}
        <div className="bulletin-notice" style={{borderColor:"rgba(58,125,68,0.35)",background:"rgba(58,125,68,0.08)"}}>
          <span>🏷️</span>
          <span>Listings are automatically removed 30 days after posting. Once your item sells or is claimed, please use the <strong>"Mark as Sold / Claimed"</strong> button so others know it's gone. You can delete your listing at any time.</span>
        </div>

        {/* Post form */}
        {showForm ? (
          <div className="form-section" style={{borderTopColor:"#3A7D44", marginBottom:"32px"}}>
            <div className="form-section-title">{editingId ? "✏️ Edit Your Listing" : "🏷️ Post a New Listing"}</div>
            <div className="form-grid">
              <div className="form-group full">
                <label>Item Title *</label>
                <input value={form.title} onChange={e=>setF("title",e.target.value)}
                  placeholder="e.g. Wilson Blade 98 v8 — Like New, 4 3/8 grip"/>
              </div>
              <div className="form-group">
                <label>Category *</label>
                <select value={form.category} onChange={e=>setF("category",e.target.value)}>
                  <option value="">Select…</option>
                  {EXCHANGE_CATS.map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Listing Type *</label>
                <select value={form.listingType} onChange={e=>setF("listingType",e.target.value)}>
                  <option value="For Sale">For Sale</option>
                  <option value="Give Away">Give Away (Free)</option>
                </select>
              </div>
              {form.listingType === "For Sale" && (
                <div className="form-group">
                  <label>Asking Price *</label>
                  <input value={form.price} onChange={e=>setF("price",e.target.value)} placeholder="e.g. $85 or OBO"/>
                </div>
              )}
              <div className="form-group">
                <label>Contact Info *</label>
                <input value={form.contact} onChange={e=>setF("contact",e.target.value)}
                  placeholder="Email or phone for interested buyers"/>
              </div>
              <div className="form-group full">
                <label>Description * <span style={{fontWeight:400,textTransform:"none",fontSize:"11px"}}>(condition, specs, pickup arrangements, etc.)</span></label>
                <textarea rows={4} value={form.description} onChange={e=>setF("description",e.target.value)}
                  placeholder="Describe the item — condition, size, brand details, pickup location, etc."/>
              </div>
            </div>
            <div style={{display:"flex",gap:"10px",marginTop:"12px",flexWrap:"wrap"}}>
              <button className="submit-btn" style={{background:"#3A7D44"}} onClick={handleSubmit}>
                {editingId ? "Save Changes" : "Post Listing"}
              </button>
              <button onClick={()=>{setShowForm(false);setEditingId(null);setForm(emptyForm);}}
                style={{padding:"12px 20px",background:"transparent",border:"1.5px solid var(--grey-light)",
                  borderRadius:"3px",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontWeight:"600",fontSize:"14px"}}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div style={{marginBottom:"24px"}}>
            <button className="submit-btn" style={{background:"#3A7D44"}} onClick={()=>setShowForm(true)}>
              + Post a Listing
            </button>
          </div>
        )}

        {/* Category filter bar */}
        <div className="exchange-filter-bar">
          {["All",...EXCHANGE_CATS].map(cat=>(
            <button key={cat}
              className={`exchange-cat-btn${catFilter===cat?" active":""}`}
              onClick={()=>setCatFilter(cat)}>
              {cat}{catCounts[cat]>0 ? ` (${catCounts[cat]})` : ""}
            </button>
          ))}
          <label style={{marginLeft:"auto",fontSize:"12px",color:"var(--grey)",
            display:"flex",alignItems:"center",gap:"6px",cursor:"pointer",fontWeight:600}}>
            <input type="checkbox" checked={showSold} onChange={e=>setShowSold(e.target.checked)}/>
            Show sold / claimed
          </label>
        </div>

        {/* Listings grid */}
        {activeListings.length === 0 ? (
          <div style={{textAlign:"center",padding:"60px 20px",color:"var(--grey)"}}>
            <div style={{fontSize:"48px",marginBottom:"16px"}}>🏷️</div>
            <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"22px",letterSpacing:"2px",marginBottom:"8px"}}>
              {catFilter==="All" ? "No Active Listings" : `No ${catFilter} Listings`}
            </div>
            <div style={{fontSize:"14px"}}>Be the first to post something!</div>
          </div>
        ) : (
          <div className="exchange-grid">
            {activeListings.map(listing => {
              const isOwner = listing.authorId === CURRENT_USER_ID;
              const dl = daysLeft(listing.postedDate);
              const showingContact = showContact === listing.id;
              return (
                <div key={listing.id} className={`exchange-card${listing.sold?" sold-card":""}`}>
                  {listing.sold && <div className="exchange-sold-overlay">SOLD / CLAIMED</div>}
                  <div className="exchange-card-header">
                    <span className={`exchange-cat-tag${listing.sold?" sold-tag":""}`}>{listing.category}</span>
                    <span className={`exchange-listing-type${listing.listingType==="Give Away"?" give":""}`}>
                      {listing.listingType}
                    </span>
                  </div>
                  <div className="exchange-title">{listing.title}</div>
                  <div className={`exchange-price${listing.listingType==="Give Away"?" free-price":""}`}>
                    {listing.listingType==="Give Away" ? "FREE" : listing.price}
                  </div>
                  <div className="exchange-desc">{listing.description}</div>
                  <div className="exchange-footer">
                    <div>
                      <div className="exchange-author">Posted by <strong>{listing.author}</strong></div>
                      <div className={`exchange-days-left`} style={{
                        color: dl<=7 ? "var(--red)" : "var(--grey)", fontSize:"11px", marginTop:"2px"
                      }}>
                        {listing.sold ? "Closed" : `${dl} day${dl!==1?"s":""} left`}
                      </div>
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:"6px",alignItems:"flex-end"}}>
                      {!listing.sold && !isOwner && (
                        showingContact ? (
                          <div style={{fontSize:"13px",fontWeight:"600",color:"var(--black)",
                            background:"var(--cream-dark)",padding:"6px 10px",borderRadius:"3px"}}>
                            📧 {listing.contact}
                          </div>
                        ) : (
                          <button className="exchange-contact-btn" onClick={()=>setShowContact(listing.id)}>
                            Contact Seller
                          </button>
                        )
                      )}
                      {isOwner && !listing.sold && (
                        <div style={{display:"flex",gap:"6px"}}>
                          <button className="exchange-mark-sold-btn" onClick={()=>handleMarkSold(listing.id)}>
                            Mark Sold
                          </button>
                          <button className="bulletin-action-btn" onClick={()=>handleEdit(listing)} aria-label="Edit listing"><IconPencil/></button>
                          <button className="bulletin-action-btn bulletin-action-delete" onClick={()=>handleDelete(listing.id)} aria-label="Delete listing"><IconTrash/></button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ── MATCH BULLETIN BOARD ──────────────────────────────────────────────────
  const BulletinView = () => {
    const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
    const now = Date.now();

    // Time slot options in 30-min increments 6am–10pm
    const TIME_SLOTS = [];
    for(let h=6; h<=22; h++) {
      for(let m=0; m<60; m+=30) {
        if(h===22 && m>0) break;
        const ampm = h<12 ? "AM" : "PM";
        const hr = h>12 ? h-12 : h===0 ? 12 : h;
        const min = m===0 ? "00" : "30";
        TIME_SLOTS.push(`${hr}:${min} ${ampm}`);
      }
    }

    // Auto-archive: filter posts where match date+time has passed, AND older than 30 days
    const activePosts = bulletinPosts
      .filter(p => {
        // 30-day post expiry
        if(now - new Date(p.postedDate).getTime() >= THIRTY_DAYS) return false;
        // Match datetime expiry — if date is set, check if it has passed
        if(p.date) {
          const matchDate = new Date(p.date + "T12:00:00"); // noon local to avoid UTC-offset expiry bug
          // Parse start time to get hour offset
          if(p.timeStart) {
            const [time, ampm] = p.timeStart.split(" ");
            const [hr, min] = time.split(":").map(Number);
            let hours = hr;
            if(ampm==="PM" && hr!==12) hours += 12;
            if(ampm==="AM" && hr===12) hours = 0;
            matchDate.setHours(hours, Number(min), 0, 0);
          } else {
            matchDate.setHours(23,59,59,999); // end of day if no time
          }
          if(now > matchDate.getTime()) return false;
        }
        return true;
      })
      .sort((a,b) => {
        // Sort by match date ascending (soonest first), then post date
        if(a.date && b.date) return new Date(a.date) - new Date(b.date);
        return new Date(b.postedDate) - new Date(a.postedDate);
      });

    const CURRENT_USER = currentProfile?.name || "";
    const CURRENT_USER_ID = currentProfile?.id || null;

    const emptyForm = { day:"", date:"", timeStart:"", timeEnd:"", court:"", skillPref:"", playType:"Singles", note:"" };
    const [form, setForm] = useState(emptyForm);
    const [editingId, setEditingId] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const setF = (k,v) => setForm(f=>({...f,[k]:v}));

    // Auto-open edit form if navigated here from Dashboard
    useEffect(() => {
      if(dashBulletinEditTarget.current) {
        const post = dashBulletinEditTarget.current;
        setForm({ day:post.day||"", date:post.date||"", timeStart:post.timeStart||"",
          timeEnd:post.timeEnd||"", court:post.court, skillPref:post.skillPref||"",
          playType:post.playType||"Singles", note:post.note||"" });
        setEditingId(post.id);
        setShowForm(true);
        dashBulletinEditTarget.current = null;
      }
    }, []);

    // Auto-populate day of week from date picker
    const handleDateChange = (val) => {
      setF("date", val);
      if(val) {
        const d = new Date(val + "T12:00:00"); // noon to avoid timezone edge cases
        const dayNames = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
        setF("day", dayNames[d.getDay()]);
      } else {
        setF("day","");
      }
    };

    const formatMatchDate = (dateStr) => {
      if(!dateStr) return "";
      const d = new Date(dateStr + "T12:00:00");
      return d.toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"});
    };

    const daysUntilExpiry = (postedDate) => {
      const msLeft = THIRTY_DAYS - (now - new Date(postedDate).getTime());
      return Math.ceil(msLeft / (24*60*60*1000));
    };

    const formatPostedDate = (iso) => {
      const d = new Date(iso);
      return d.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"});
    };

    // Today's date as YYYY-MM-DD for min date on picker
    const todayStr = new Date().toISOString().slice(0,10);

    const handleSubmit = async () => {
      if(!form.date || !form.timeStart || !form.timeEnd || !form.court) {
        showToast("Please fill in date, start time, end time, and court.","error"); return;
      }
      if(editingId) {
        const { error } = await supabase.from("bulletin_posts").update({
          play_date: form.date, day_label: form.day, time_start: form.timeStart,
          time_end: form.timeEnd, court: form.court, skill_pref: form.skillPref,
          play_type: form.playType, note: form.note,
        }).eq("id", editingId);
        if(error) { showToast("Error updating post.","error"); return; }
        setBulletinPosts(prev => prev.map(p => p.id===editingId ? {...p, ...form} : p));
        showToast("✏️ Post updated!");
        setEditingId(null);
      } else {
        const { data, error } = await supabase.from("bulletin_posts").insert({
          author_id: CURRENT_USER_ID, author_name: CURRENT_USER,
          play_date: form.date, day_label: form.day, time_start: form.timeStart,
          time_end: form.timeEnd, court: form.court, skill_pref: form.skillPref,
          play_type: form.playType, note: form.note, interested: [],
        }).select().single();
        if(error) { showToast("Error creating post.","error"); return; }
        const newPost = { id: data.id, author: data.author_name, authorId: data.author_id,
          postedDate: data.created_at, day: data.day_label, date: data.play_date,
          timeStart: data.time_start, timeEnd: data.time_end, court: data.court,
          skillPref: data.skill_pref, playType: data.play_type, note: data.note,
          interestedUsers: [] };
        setBulletinPosts(prev => [newPost, ...prev]);
        showToast("📌 Post added to the Match Bulletin Board!");
        // Notify admins of new post (fire and forget)
        fetch(EDGE_FN_URLS.NOTIFY_NEW_POST, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            postType: "Match Bulletin",
            authorName: CURRENT_USER,
            title: `${form.playType} · ${form.court} · ${form.date || form.day}`,
            detail: form.note || "",
          }),
        }).catch(() => {});
      }
      setForm(emptyForm);
      setShowForm(false);
    };

    const handleDelete = async (id) => {
      const { error } = await supabase.from("bulletin_posts").delete().eq("id", id);
      if(error) { showToast("Error deleting post.","error"); return; }
      setBulletinPosts(prev => prev.filter(p => p.id !== id));
      showToast("🗑️ Post deleted.","error");
    };

    const handleEdit = (post) => {
      setForm({ day:post.day||"", date:post.date||"", timeStart:post.timeStart||"",
        timeEnd:post.timeEnd||"", court:post.court, skillPref:post.skillPref,
        playType:post.playType, note:post.note });
      setEditingId(post.id);
      setShowForm(true);
      window.scrollTo(0,0);
    };

    const handleInterest = async (postId) => {
      const post = bulletinPosts.find(p => p.id === postId);
      if(!post) return;
      const already = post.interestedUsers.includes(CURRENT_USER);
      const updated = already
        ? post.interestedUsers.filter(u => u !== CURRENT_USER)
        : [...post.interestedUsers, CURRENT_USER];
      const { error } = await supabase.from("bulletin_posts")
        .update({ interested: updated }).eq("id", postId);
      if(error) { showToast("Error updating interest.","error"); return; }
      if(!already) showToast(`👍 Marked as interested! ${post.author} can see your name on this post.`);
      setBulletinPosts(prev => prev.map(p => p.id === postId ? {...p, interestedUsers: updated} : p));
    };

    return (
      <div className="page-wrap">
        <div className="page-header">
          <button className="page-back" onClick={()=>nav(VIEWS.HOME)}>← Back to Home</button>
          <div className="page-title" style={{display:"flex",alignItems:"center",gap:"12px"}}>
            <IconBulletin size={36} dark/>Match Bulletin Board
          </div>
          <div className="page-sub">Posts are automatically removed once the match date/time has passed, or after 30 days. If you find a partner through another channel, please delete your post to keep the board current.</div>
          <div className="red-bar"/>
        </div>

        {/* Post / Edit form */}
        {showForm ? (
          <div className="form-section" style={{borderTopColor:"#B07D2A", marginBottom:"32px"}}>
            <div className="form-section-title">{editingId ? "✏️ Edit Your Post" : "📌 Post to the Match Bulletin Board"}</div>
            <p style={{fontSize:"13px",color:"var(--grey)",marginBottom:"16px",lineHeight:"1.6"}}>
              Your post will be visible to all members and will <strong>automatically archive once the match time passes</strong>.
              You can delete or edit it at any time.
            </p>
            <div className="form-grid">

              {/* Date picker — auto-fills Day of Week */}
              <div className="form-group">
                <label>Date *</label>
                <input type="date" min={todayStr} value={form.date}
                  onChange={e=>handleDateChange(e.target.value)}
                  style={{fontFamily:"'DM Sans',sans-serif"}}/>
              </div>

              {/* Day of week — read-only, auto-populated */}
              <div className="form-group">
                <label>Day of Week <span style={{fontWeight:400,textTransform:"none",fontSize:"11px"}}>(auto-filled)</span></label>
                <input value={form.day} readOnly
                  style={{background:"var(--cream-dark)",color:"var(--grey)",cursor:"default"}}
                  placeholder="Populated from date"/>
              </div>

              {/* Start time */}
              <div className="form-group">
                <label>Start Time *</label>
                <select value={form.timeStart} onChange={e=>setF("timeStart",e.target.value)}>
                  <option value="">Select start time...</option>
                  {TIME_SLOTS.map(t=><option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              {/* End time */}
              <div className="form-group">
                <label>End Time *</label>
                <select value={form.timeEnd} onChange={e=>setF("timeEnd",e.target.value)}>
                  <option value="">Select end time...</option>
                  {TIME_SLOTS.map(t=><option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label>Court Location *</label>
                <select value={form.court} onChange={e=>setF("court",e.target.value)}>
                  <option value="">Select court...</option>
                  {courtNames.map(c=>(
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Preferred Skill Level</label>
                <input value={form.skillPref} onChange={e=>setF("skillPref",e.target.value)} placeholder="e.g. 3.0–4.0, Any level"/>
              </div>

              <div className="form-group">
                <label>Play Type</label>
                <select value={form.playType} onChange={e=>setF("playType",e.target.value)}>
                  {PLAY_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              <div className="form-group full">
                <label>Short Note <span style={{fontWeight:400,textTransform:"none",fontSize:"11px"}}>(optional, max 140 characters)</span></label>
                <input maxLength={140} value={form.note} onChange={e=>setF("note",e.target.value)}
                  placeholder="e.g. Casual rally, all welcome. Bring a can of balls."/>
              </div>
            </div>
            <div style={{display:"flex",gap:"10px",marginTop:"12px",flexWrap:"wrap"}}>
              <button className="submit-btn" onClick={handleSubmit}>
                {editingId ? "Save Changes" : "Post to Board"}
              </button>
              <button onClick={()=>{setShowForm(false);setEditingId(null);setForm(emptyForm);}}
                style={{padding:"12px 20px",background:"transparent",border:"1.5px solid var(--grey-light)",
                  borderRadius:"3px",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontWeight:"600",fontSize:"14px"}}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div style={{marginBottom:"24px"}}>
            <button className="submit-btn" onClick={()=>setShowForm(true)}>
              + Post to Match Bulletin Board
            </button>
          </div>
        )}

        {/* Active posts */}
        {activePosts.length === 0 ? (
          <div style={{textAlign:"center",padding:"60px 20px",color:"var(--grey)"}}>
            <div style={{fontSize:"48px",marginBottom:"16px"}}>📌</div>
            <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"22px",letterSpacing:"2px",marginBottom:"8px"}}>Board is Empty</div>
            <div style={{fontSize:"14px"}}>No active posts yet. Be the first to post a session!</div>
          </div>
        ) : (
          <div className="bulletin-grid">
            {activePosts.map(post => {
              const isOwner = post.authorId === CURRENT_USER_ID;
              const isInterested = post.interestedUsers.includes(CURRENT_USER);
              const daysLeft = daysUntilExpiry(post.postedDate);
              // Format time display — support both old (time string) and new (timeStart/timeEnd) formats
              const timeDisplay = post.timeStart && post.timeEnd
                ? `${post.timeStart} – ${post.timeEnd}`
                : post.time || "";
              const dateDisplay = post.date ? formatMatchDate(post.date) : post.date;
              return (
                <div key={post.id} className={`bulletin-card${isOwner?" bulletin-card-own":""}`}>
                  <div className="bulletin-card-header">
                    <div className="bulletin-pin">📌</div>
                    <div style={{flex:1}}>
                      <div className="bulletin-author">{post.author}</div>
                      <div className="bulletin-meta">Posted {formatPostedDate(post.postedDate)}
                        {post.editedDate && " · edited"} · <span style={{color: daysLeft<=7?"var(--red)":"var(--grey)"}}>
                          expires in {daysLeft} day{daysLeft!==1?"s":""}
                        </span>
                      </div>
                    </div>
                    {isOwner && (
                      <div style={{display:"flex",gap:"6px"}}>
                        <button className="bulletin-action-btn" onClick={()=>handleEdit(post)} aria-label="Edit post"><IconPencil/></button>
                        <button className="bulletin-action-btn bulletin-action-delete" onClick={()=>handleDelete(post.id)} aria-label="Delete post"><IconTrash/></button>
                      </div>
                    )}
                  </div>

                  <div className="bulletin-details">
                    <div className="bulletin-detail-row">
                      <span className="bulletin-detail-label">📅 When</span>
                      <span className="bulletin-detail-val">{dateDisplay}{timeDisplay ? ` · ${timeDisplay}` : ""}</span>
                    </div>
                    <div className="bulletin-detail-row">
                      <span className="bulletin-detail-label">📍 Court</span>
                      <span className="bulletin-detail-val">{post.court}</span>
                    </div>
                    <div className="bulletin-detail-row">
                      <span className="bulletin-detail-label">🎾 Type</span>
                      <span className="bulletin-detail-val">{post.playType}{post.skillPref ? ` · ${post.skillPref}` : ""}</span>
                    </div>
                    {post.note && (
                      <div className="bulletin-note">"{post.note}"</div>
                    )}
                  </div>

                  <div className="bulletin-card-footer">
                    {post.interestedUsers.length > 0 && (
                      <div className="bulletin-interested">
                        {isOwner
                          ? <>{post.interestedUsers.length} interested: {post.interestedUsers.join(", ")}</>
                          : <>{post.interestedUsers.length} player{post.interestedUsers.length!==1?"s":""} interested</>
                        }
                      </div>
                    )}
                    {!isOwner && (
                      <button
                        className={`bulletin-interest-btn${isInterested?" bulletin-interest-btn-active":""}`}
                        onClick={()=>handleInterest(post.id)}>
                        {isInterested ? "✓ Interested" : "I'm Interested"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ── CONTACT MODAL ─────────────────────────────────────────────────────────
  const ContactModal = () => {
    if(!contactReq)return null;
    return (
      <div className="modal-overlay" onClick={()=>setContactReq(null)} role="dialog" aria-label="Request contact information" aria-modal="true">
        <div className="modal" onClick={e=>e.stopPropagation()}>
          <div className="modal-head">Request Contact Info</div>
          <div className="modal-body">
            <p style={{fontSize:"15px",lineHeight:"1.65"}}>Send a contact request to <strong>{contactReq.name}</strong>?</p>
            <p style={{fontSize:"13px",color:"var(--grey)",marginTop:"10px",lineHeight:"1.6"}}>
              They'll receive an email and can approve or decline. If approved, both of you will receive each other's contact information by email.
            </p>
            <div className="modal-actions">
              <button className="submit-btn" style={{margin:0,flex:1}} onClick={async()=>{
                // Insert request to DB
                const { error } = await supabase.from("contact_requests").insert({
                  from_id: currentProfile.id,
                  to_id: contactReq.id,
                  status: "pending",
                });
                if(error) {
                  if(error.code === "23505") {
                    showToast("You already sent a request to this member.","error");
                  } else {
                    showToast("Error sending request.","error");
                  }
                  setContactReq(null); return;
                }
                // Notify member B by email
                fetch(EDGE_FN_URLS.NOTIFY_CONTACT_REQ, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    fromName: currentProfile.name,
                    toName: contactReq.name,
                    toEmail: contactReq.email,
                  }),
                }).catch(() => {});
                // Update sentRequests so Connections button shows "Pending" immediately
                setSentRequests(prev => [...prev, { to_id: contactReq.id, status: "pending" }]);
                setContactReq(null);
                showToast(`📧 Request sent to ${contactReq.name}!`);
              }}>
                Send Request
              </button>
              <button onClick={()=>setContactReq(null)}
                style={{flex:1,padding:"12px",background:"transparent",border:"1.5px solid var(--grey-light)",borderRadius:"3px",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontWeight:"600"}}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── PROFILE EDITOR ───────────────────────────────────────────────────────
  const ProfileEditor = ({ profile, showToast, setCurrentProfile, toggleArr }) => {
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
      name: profile.name || "",
      phone: profile.phone || "",
      ntrp: profile.ntrp || "",
      skill: profile.skill || "",
      play: profile.play || "",
      days: profile.days || [],
      times: profile.times || [],
    });
    const setF = (k, v) => setForm(f => ({...f, [k]: v}));

    const handleSave = async () => {
      if (!form.name) { showToast("Name is required.", "error"); return; }
      setSaving(true);
      const { error } = await supabase.from("profiles").update({
        name: form.name, phone: form.phone, ntrp: form.ntrp,
        skill: form.skill, play: form.play,
        days: form.days, times: form.times,
      }).eq("id", profile.id);
      setSaving(false);
      if (error) { showToast("Error saving profile.", "error"); return; }
      setCurrentProfile(prev => ({...prev, ...form}));
      setEditing(false);
      showToast("✅ Profile updated!");
    };

    if (!editing) return (
      <div style={{display:"flex",alignItems:"flex-start",gap:"20px",flexWrap:"wrap"}}>
        <div className="player-avatar" style={{width:"60px",height:"60px",fontSize:"26px",flexShrink:0}}>
          {profile.name?.split(" ").map(n=>n[0]).join("").toUpperCase()||"?"}
        </div>
        <div style={{flex:1,minWidth:"200px"}}>
          <div style={{fontWeight:"700",fontSize:"18px",marginBottom:"6px"}}>{profile.name}</div>
          <div style={{marginBottom:"10px"}}><RatingBadge ntrp={profile.ntrp} skill={profile.skill}/></div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:"8px 24px",
            fontSize:"13px",color:"var(--charcoal)"}}>
            <div><span style={{color:"var(--grey)",fontWeight:"600"}}>Phone: </span>{profile.phone||"—"}</div>
            <div><span style={{color:"var(--grey)",fontWeight:"600"}}>Play Type: </span>{profile.play||"—"}</div>
            <div><span style={{color:"var(--grey)",fontWeight:"600"}}>Days: </span>
              {(profile.days||[]).map(d=><span key={d} className="avail-chip" style={{marginRight:"4px"}}>{d}</span>)}
            </div>
            <div><span style={{color:"var(--grey)",fontWeight:"600"}}>Times: </span>
              {(profile.times||[]).map(t=><span key={t} className="avail-chip avail-chip-time" style={{marginRight:"4px"}}>{t}</span>)}
            </div>
          </div>
        </div>
        <button style={{padding:"9px 18px",background:"transparent",border:"1.5px solid var(--grey-light)",
          borderRadius:"3px",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontWeight:"600",
          fontSize:"13px",color:"var(--grey)",whiteSpace:"nowrap",alignSelf:"flex-start"}}
          onClick={()=>setEditing(true)}>
          ✏️ Edit Profile
        </button>
      </div>
    );

    return (
      <div>
        <div className="form-grid" style={{marginBottom:"16px"}}>
          <div className="form-group"><label>Full Name *</label>
            <input value={form.name} onChange={e=>setF("name",e.target.value)}/></div>
          <div className="form-group"><label>Phone</label>
            <input value={form.phone} onChange={e=>setF("phone",e.target.value)} placeholder="812-555-0000"/></div>
          <div className="form-group"><label>NTRP Rating</label>
            <select value={form.ntrp} onChange={e=>setF("ntrp",e.target.value)}>
              <option value="">Select…</option>
              {NTRPS.map(r=><option key={r}>{r}</option>)}
            </select></div>
          <div className="form-group"><label>Skill Level</label>
            <select value={form.skill} onChange={e=>setF("skill",e.target.value)}>
              <option value="">Select…</option>
              {SKILLS.map(s=><option key={s}>{s}</option>)}
            </select></div>
          <div className="form-group"><label>Play Style</label>
            <select value={form.play} onChange={e=>setF("play",e.target.value)}>
              <option value="">Select…</option>
              {PLAY_TYPES.map(p=><option key={p}>{p}</option>)}
            </select></div>
        </div>
        <div style={{marginBottom:"12px"}}>
          <label style={{fontWeight:"600",fontSize:"13px",display:"block",marginBottom:"8px"}}>
            Available Days</label>
          <div style={{display:"flex",flexWrap:"wrap",gap:"8px"}}>
            {DAYS.map(d=>(
              <AvailPill key={d} label={d} active={form.days.includes(d)}
                onClick={()=>setF("days",toggleArr(form.days,d))}/>
            ))}
          </div>
        </div>
        <div style={{marginBottom:"20px"}}>
          <label style={{fontWeight:"600",fontSize:"13px",display:"block",marginBottom:"8px"}}>
            Available Times</label>
          <div style={{display:"flex",flexWrap:"wrap",gap:"8px"}}>
            {TIMES.map(t=>(
              <AvailPill key={t} label={t} active={form.times.includes(t)}
                onClick={()=>setF("times",toggleArr(form.times,t))}/>
            ))}
          </div>
        </div>
        <div style={{display:"flex",gap:"12px"}}>
          <button className="submit-btn" style={{margin:0}} onClick={handleSave} disabled={saving}>
            {saving?"Saving…":"Save Changes →"}
          </button>
          <button onClick={()=>setEditing(false)}
            style={{padding:"10px 18px",background:"transparent",border:"1.5px solid var(--grey-light)",
              borderRadius:"3px",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontWeight:"600"}}>
            Cancel
          </button>
        </div>
      </div>
    );
  };

  // ── MEMBER DASHBOARD ──────────────────────────────────────────────────────
  const DashboardView = () => {
    const profile = currentProfile;
    const [acceptedReq, setAcceptedReq] = useState(null); // drives the contact info modal
    const [expandedBulletinId, setExpandedBulletinId] = useState(null);
    const [expandedExchangeId, setExpandedExchangeId] = useState(null);

    const myBulletinPosts = bulletinPosts.filter(p => p.authorId === profile?.id);
    const myExchangeListings = exchangeListings.filter(l => l.authorId === profile?.id);

    const handleAccept = async (r) => {
      // Update status in DB
      const { error } = await supabase.from("contact_requests")
        .update({ status: "accepted" }).eq("id", r.id);
      if(error) { showToast("Error accepting request.","error"); return; }
      // Remove from pending list
      setContactReqs(prev => prev.filter(x => x.id !== r.id));
      // Show contact info modal
      setAcceptedReq(r);
      // Email both parties
      fetch(EDGE_FN_URLS.NOTIFY_CONTACT_ACC, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fromName: r.from,
          fromEmail: r.email,
          fromPhone: r.phone,
          toName: currentProfile.name,
          toEmail: currentProfile.email,
          toPhone: currentProfile.phone,
        }),
      }).catch(() => {});
      // Update sentRequests so Connections button shows "Connected" for this person
      setSentRequests(prev => [...prev, { to_id: r.from_id, status: "accepted" }]);
    };

    const handleDecline = async (id) => {
      const { error } = await supabase.from("contact_requests")
        .update({ status: "declined" }).eq("id", id);
      if(error) { showToast("Error declining request.","error"); return; }
      setContactReqs(prev => prev.filter(r => r.id !== id));
      showToast("Request declined.");
    };

    const closeAcceptModal = () => {
      setAcceptedReq(null);
    };

    // Navigate to Bulletin Board with edit form pre-opened for this post
    const dashEditBulletin = (post) => {
      dashBulletinEditTarget.current = post;
      nav(VIEWS.BULLETIN);
    };
    const dashDeleteBulletin = async (id) => {
      await supabase.from("bulletin_posts").delete().eq("id", id);
      setBulletinPosts(prev => prev.filter(p => p.id !== id));
      setExpandedBulletinId(null);
      showToast("🗑️ Post deleted.");
    };

    // Navigate to Exchange with edit form pre-opened for this listing
    const dashEditExchange = (listing) => {
      dashExchangeEditTarget.current = listing;
      nav(VIEWS.EXCHANGE);
    };
    const dashDeleteExchange = async (id) => {
      const { error } = await supabase.from("exchange_listings").delete().eq("id", id);
      if (error) { showToast("Error deleting listing.", "error"); return; }
      setExchangeListings(prev => prev.filter(l => l.id !== id));
      setExpandedExchangeId(null);
      showToast("🗑️ Listing deleted.");
    };

    const renderSection = (title, count, children) => (
      <div style={{background:"white",borderRadius:"6px",boxShadow:"var(--shadow-sm)",
        marginBottom:"24px",overflow:"hidden"}}>
        <div style={{padding:"16px 22px 14px",borderBottom:"1px solid var(--cream-dark)",
          display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"18px",letterSpacing:"2px",
            color:"var(--black)"}}>{title}</div>
          {count!==undefined &&
            <span style={{background:"var(--cream-dark)",borderRadius:"20px",padding:"2px 10px",
              fontSize:"12px",fontWeight:"700",color:"var(--charcoal)"}}>{count}</span>}
        </div>
        <div style={{padding:"18px 22px"}}>{children}</div>
      </div>
    );

    const renderEmpty = (msg) => (
      <div style={{textAlign:"center",padding:"20px 0",color:"var(--grey)",fontSize:"14px"}}>
        <TennisBall size={32} opacity={0.25}/>
        <div style={{marginTop:"10px"}}>{msg}</div>
      </div>
    );

    return (
      <div className="page-wrap">
        <div className="page-header">
          <button className="page-back" onClick={()=>nav(VIEWS.HOME)}>← Back to Home</button>
          <div className="page-title" style={{display:"flex",alignItems:"center",gap:"12px"}}>
            <IconMember size={36} dark loggedIn/>Member Dashboard</div>
          <div className="page-sub">Welcome back, {profile.name}</div>
          <div className="red-bar"/>
        </div>

        {renderSection("My Profile", undefined,
          <ProfileEditor profile={profile} showToast={showToast}
            setCurrentProfile={setCurrentProfile} toggleArr={toggleArr}/>
        )}

        {/* Accept modal */}
        {acceptedReq && (
          <div className="modal-overlay" onClick={closeAcceptModal} role="dialog" aria-label="Contact information" aria-modal="true">
            <div className="modal-box" onClick={e=>e.stopPropagation()}
              style={{maxWidth:"400px",textAlign:"center"}}>
              <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"22px",letterSpacing:"2px",
                marginBottom:"4px"}}>Request Accepted</div>
              <div style={{fontSize:"13px",color:"var(--grey)",marginBottom:"20px"}}>
                Here is {acceptedReq.from}'s contact information
              </div>
              <div className="player-avatar" style={{width:"56px",height:"56px",fontSize:"22px",
                margin:"0 auto 16px"}}>
                {acceptedReq.initials}
              </div>
              <div style={{fontWeight:"700",fontSize:"17px",marginBottom:"4px"}}>{acceptedReq.from}</div>
              <div style={{marginBottom:"18px"}}><RatingBadge ntrp={acceptedReq.ntrp} skill={acceptedReq.skill}/></div>
              <div style={{background:"var(--cream-dark)",borderRadius:"6px",padding:"14px 20px",
                textAlign:"left",marginBottom:"16px"}}>
                <div style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"10px",fontSize:"14px"}}>
                  <span style={{fontSize:"16px"}}>✉️</span>
                  <a href={"mailto:"+acceptedReq.email} style={{color:"var(--charcoal)",fontWeight:"600"}}>
                    {acceptedReq.email}
                  </a>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:"10px",fontSize:"14px"}}>
                  <span style={{fontSize:"16px"}}>📞</span>
                  <a href={"tel:"+acceptedReq.phone} style={{color:"var(--charcoal)",fontWeight:"600"}}>
                    {acceptedReq.phone}
                  </a>
                </div>
              </div>
              <div style={{fontSize:"12px",color:"var(--grey)",background:"rgba(212,160,23,0.08)",
                border:"1px solid rgba(212,160,23,0.3)",borderRadius:"5px",padding:"10px 14px",
                marginBottom:"18px",textAlign:"left",lineHeight:"1.6"}}>
                📧 <strong>{acceptedReq.from}</strong> will receive your contact info by email so you can both get in touch.
              </div>
              <button className="submit-btn" style={{margin:"0 0 10px",width:"100%"}}
                onClick={closeAcceptModal}>
                Got it
              </button>
              <div style={{fontSize:"11px",color:"var(--grey)"}}>
                This request will be removed from your dashboard
              </div>
            </div>
          </div>
        )}

        {renderSection("Contact Requests", contactReqs.length > 0 ? contactReqs.length : undefined,
          contactReqs.length === 0 ? renderEmpty("No pending contact requests.") : (
            <div style={{display:"flex",flexDirection:"column",gap:"10px"}}>
              {contactReqs.map(r=>(
                <div key={r.id} style={{display:"flex",alignItems:"center",gap:"14px",padding:"12px 16px",
                  borderRadius:"5px",background:"var(--cream-dark)",border:"1.5px solid var(--grey-light)"}}>
                  <div className="player-avatar" style={{width:"38px",height:"38px",fontSize:"16px",flexShrink:0}}>
                    {r.initials}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:"700",fontSize:"14px",marginBottom:"4px"}}>{r.from}</div>
                    <div style={{fontSize:"12px",color:"var(--grey)",display:"flex",alignItems:"center",gap:"8px"}}>
                      <RatingBadge ntrp={r.ntrp} skill={r.skill}/>
                      <span>{r.play} · Requested {r.date}</span>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:"8px",flexShrink:0}}>
                    <button onClick={()=>handleAccept(r)}
                      style={{padding:"7px 14px",background:"var(--charcoal)",color:"white",border:"none",
                        borderRadius:"3px",cursor:"pointer",fontSize:"12px",fontWeight:"700",
                        fontFamily:"'DM Sans',sans-serif"}}>
                      Accept
                    </button>
                    <button onClick={()=>handleDecline(r.id)}
                      style={{padding:"7px 14px",background:"transparent",color:"var(--grey)",
                        border:"1.5px solid var(--grey-light)",borderRadius:"3px",cursor:"pointer",
                        fontSize:"12px",fontWeight:"600",fontFamily:"'DM Sans',sans-serif"}}>
                      Decline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {renderSection("My Bulletin Board Posts", myBulletinPosts.length,
          myBulletinPosts.length === 0 ? renderEmpty("You have no active posts on the Match Bulletin Board.") : (
            <div style={{display:"flex",flexDirection:"column",gap:"10px"}}>
              {myBulletinPosts.map(p => {
                const timeDisplay = p.timeStart && p.timeEnd ? p.timeStart+" – "+p.timeEnd : p.time||"";
                const dateDisplay = p.date ? new Date(p.date+"T12:00:00").toLocaleDateString("en-US",
                  {weekday:"long",month:"long",day:"numeric"}) : p.date;
                const isExpanded = expandedBulletinId === p.id;
                return (
                  <div key={p.id} style={{borderRadius:"5px",border:"1.5px solid "+(isExpanded?"var(--red)":"var(--grey-light)"),
                    overflow:"hidden",transition:"border-color 0.15s"}}>
                    <div onClick={()=>setExpandedBulletinId(isExpanded ? null : p.id)}
                      style={{padding:"12px 16px",background:isExpanded?"rgba(204,0,0,0.03)":"var(--cream-dark)",
                        cursor:"pointer",display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:"12px"}}>
                      <div style={{flex:1}}>
                        <div style={{fontSize:"13px",fontWeight:"700",marginBottom:"4px",color:"var(--black)"}}>
                          📅 {dateDisplay}{timeDisplay ? " · "+timeDisplay : ""}
                        </div>
                        <div style={{fontSize:"13px",color:"var(--charcoal)",marginBottom: p.interestedUsers.length > 0 ? "4px" : "0"}}>
                          📍 {p.court} &nbsp;·&nbsp; 🎾 {p.playType}
                          {p.skillPref && <span> · {p.skillPref}</span>}
                        </div>
                        {p.interestedUsers.length > 0 && (
                          <div style={{fontSize:"12px",color:"var(--red)",fontWeight:"600"}}>
                            {p.interestedUsers.length} interested · {p.interestedUsers.join(", ")}
                          </div>
                        )}
                      </div>
                      <span style={{fontSize:"11px",color:"var(--grey)",whiteSpace:"nowrap",paddingTop:"2px"}}>
                        {isExpanded ? "▲ close" : "▼ manage"}
                      </span>
                    </div>
                    {isExpanded && (
                      <div style={{padding:"12px 16px",background:"white",borderTop:"1px solid var(--grey-light)",
                        display:"flex",gap:"10px"}}>
                        <button onClick={()=>dashEditBulletin(p)}
                          style={{display:"flex",alignItems:"center",justifyContent:"center",gap:"6px",padding:"8px 16px",
                            background:"var(--charcoal)",color:"white",border:"none",borderRadius:"3px",
                            cursor:"pointer",fontSize:"13px",fontWeight:"600",fontFamily:"'DM Sans',sans-serif"}}>
                          Edit Post
                        </button>
                        <button onClick={()=>dashDeleteBulletin(p.id)}
                          style={{display:"flex",alignItems:"center",gap:"6px",padding:"8px 16px",
                            background:"transparent",color:"var(--red)",border:"1.5px solid var(--red)",
                            borderRadius:"3px",cursor:"pointer",fontSize:"13px",fontWeight:"600",
                            fontFamily:"'DM Sans',sans-serif"}}>
                          <IconTrash size={14}/> Delete
                        </button>
                        <span style={{marginLeft:"auto",fontSize:"12px",color:"var(--grey)",alignSelf:"center"}}>
                          Edit opens the Match Board with this post ready to update
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )
        )}

        {renderSection("My Equipment Exchange Listings", myExchangeListings.length,
          myExchangeListings.length === 0 ? renderEmpty("You have no active listings on the Equipment Exchange.") : (
            <div style={{display:"flex",flexDirection:"column",gap:"10px"}}>
              {myExchangeListings.map(l=>(
                <div key={l.id} style={{borderRadius:"5px",border:"1.5px solid "+(expandedExchangeId===l.id?"#3A7D44":"var(--grey-light)"),
                  overflow:"hidden",transition:"border-color 0.15s"}}>
                  <div onClick={()=>setExpandedExchangeId(expandedExchangeId===l.id ? null : l.id)}
                    style={{padding:"12px 16px",background:expandedExchangeId===l.id?"rgba(58,125,68,0.03)":"var(--cream-dark)",
                      cursor:"pointer",display:"flex",alignItems:"center",gap:"14px"}}>
                    <div style={{flex:1,minWidth:"160px"}}>
                      <div style={{fontWeight:"700",fontSize:"14px",marginBottom:"3px"}}>{l.title}</div>
                      <div style={{fontSize:"12px",color:"var(--grey)"}}>
                        {l.category} · {l.listingType==="Give Away"
                          ? <span style={{color:"#3A7D44",fontWeight:"700"}}>FREE</span>
                          : <span style={{fontWeight:"700"}}>{l.price}</span>}
                        {l.sold && <span style={{marginLeft:"8px",color:"var(--grey)",fontStyle:"italic"}}> · Sold/Claimed</span>}
                      </div>
                    </div>
                    <div style={{fontSize:"12px",color:"var(--grey)"}}>
                      Posted {new Date(l.postedDate).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
                    </div>
                    <span style={{fontSize:"11px",color:"var(--grey)",whiteSpace:"nowrap"}}>
                      {expandedExchangeId===l.id ? "▲ close" : "▼ manage"}
                    </span>
                  </div>
                  {expandedExchangeId===l.id && (
                    <div style={{padding:"12px 16px",background:"white",borderTop:"1px solid var(--grey-light)",
                      display:"flex",gap:"10px"}}>
                      <button onClick={()=>dashEditExchange(l)}
                        style={{display:"flex",alignItems:"center",justifyContent:"center",gap:"6px",padding:"8px 16px",
                          background:"var(--charcoal)",color:"white",border:"none",borderRadius:"3px",
                          cursor:"pointer",fontSize:"13px",fontWeight:"600",fontFamily:"'DM Sans',sans-serif"}}>
                        Edit Listing
                      </button>
                      <button onClick={()=>dashDeleteExchange(l.id)}
                        style={{display:"flex",alignItems:"center",gap:"6px",padding:"8px 16px",
                          background:"transparent",color:"var(--red)",border:"1.5px solid var(--red)",
                          borderRadius:"3px",cursor:"pointer",fontSize:"13px",fontWeight:"600",
                          fontFamily:"'DM Sans',sans-serif"}}>
                        <IconTrash size={14}/> Delete
                      </button>
                      <span style={{marginLeft:"auto",fontSize:"12px",color:"var(--grey)",alignSelf:"center"}}>
                        Edit opens the Equipment Exchange with this listing ready to update
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        )}

      </div>
    );
  };

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <ErrorBoundary>
    <>
      <style>{css}</style>
      <div className="app">

        {/* Skip to content link (accessibility) */}
        <a href="#main-content" className="skip-link">Skip to main content</a>

        {/* NAV */}
        <nav className="nav" aria-label="Main navigation">
          <div className="nav-brand" onClick={()=>nav(VIEWS.HOME)} role="button" tabIndex={0}
            aria-label="Columbus Tennis Club home"
            onKeyDown={e=>{if(e.key==='Enter')nav(VIEWS.HOME);}}>
            <TennisBallC size={44}/>
            <div className="nav-brand-identity">
              <div className="nav-brand-ctc">CTC</div>
              <div className="nav-brand-sub">Columbus, Indiana</div>
            </div>
          </div>
          <div className="nav-links">
            {!loggedIn && <>
              <button className={`nav-btn nav-btn-link${view===VIEWS.COURTS?" active":""}`} onClick={()=>nav(VIEWS.COURTS)}>Courts</button>
              <button className={`nav-btn nav-btn-link${view===VIEWS.INSTRUCTORS?" active":""}`} onClick={()=>nav(VIEWS.INSTRUCTORS)}>Instructors</button>
              <button className={`nav-btn nav-btn-link${view===VIEWS.STRINGING?" active":""}`} onClick={()=>nav(VIEWS.STRINGING)}>Stringing</button>
              <button className={`nav-btn nav-btn-link${view===VIEWS.BLOG?" active":""}`} onClick={()=>nav(VIEWS.BLOG)}>Events</button>
            </>}
            {loggedIn && <>
              <button className={`nav-btn nav-btn-link${view===VIEWS.CONNECTIONS?" active":""}`} onClick={()=>nav(VIEWS.CONNECTIONS)}>Connections</button>
              <button className={`nav-btn nav-btn-link${view===VIEWS.DASHBOARD?" active":""}`} onClick={()=>nav(VIEWS.DASHBOARD)}>My Dashboard</button>
              <button className={`nav-btn nav-btn-link${view===VIEWS.BULLETIN?" active":""}`} onClick={()=>nav(VIEWS.BULLETIN)}>Match Board</button>
              <button className={`nav-btn nav-btn-link${view===VIEWS.EXCHANGE?" active":""}`} onClick={()=>nav(VIEWS.EXCHANGE)}>Exchange</button>
              {isAdmin&&(
                <button className={`nav-btn nav-btn-link${view===VIEWS.ADMIN?" active":""}`} onClick={()=>nav(VIEWS.ADMIN)}>
                  Admin{adminQueue.length>0&&<span className="badge" aria-label={`${adminQueue.length} pending`}>{adminQueue.length}</span>}
                </button>
              )}
            </>}
            {loggedIn
              ? <button className="nav-btn nav-btn-ghost" onClick={handleSignOut}>Sign Out</button>
              : <>
                  <button className="nav-btn nav-btn-ghost" onClick={()=>nav(VIEWS.LOGIN)}>Login</button>
                  <button className="nav-btn nav-btn-red" onClick={()=>nav(VIEWS.REGISTER)}>Join CTC</button>
                </>
            }
          </div>

          {/* Hamburger button — visible on mobile only */}
          <button className="mobile-menu-btn" onClick={()=>setMobileMenuOpen(true)}
            aria-label="Open navigation menu" aria-expanded={mobileMenuOpen}>
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <line x1="4" y1="7" x2="24" y2="7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
              <line x1="4" y1="14" x2="24" y2="14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
              <line x1="4" y1="21" x2="24" y2="21" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
          </button>
        </nav>

        {/* Mobile drawer overlay */}
        <div className={`mobile-overlay${mobileMenuOpen?" open":""}`}
          onClick={()=>setMobileMenuOpen(false)} aria-hidden="true"/>
        <div className={`mobile-drawer${mobileMenuOpen?" open":""}`}
          role="dialog" aria-label="Navigation menu" aria-modal="true">
          <div className="mobile-drawer-header">
            <TennisBallC size={32}/>
            <button className="mobile-drawer-close" onClick={()=>setMobileMenuOpen(false)}
              aria-label="Close navigation menu">✕</button>
          </div>
          {!loggedIn && <>
            <button className={`nav-btn-mobile${view===VIEWS.COURTS?" active":""}`} onClick={()=>nav(VIEWS.COURTS)}>Courts</button>
            <button className={`nav-btn-mobile${view===VIEWS.INSTRUCTORS?" active":""}`} onClick={()=>nav(VIEWS.INSTRUCTORS)}>Instructors</button>
            <button className={`nav-btn-mobile${view===VIEWS.STRINGING?" active":""}`} onClick={()=>nav(VIEWS.STRINGING)}>Stringing</button>
            <button className={`nav-btn-mobile${view===VIEWS.BLOG?" active":""}`} onClick={()=>nav(VIEWS.BLOG)}>Events &amp; News</button>
            <div className="mobile-drawer-divider"/>
            <button className="nav-btn-mobile btn-cta btn-ghost" onClick={()=>nav(VIEWS.LOGIN)}>Login</button>
            <button className="nav-btn-mobile btn-cta btn-red" onClick={()=>nav(VIEWS.REGISTER)}>Join CTC</button>
          </>}
          {loggedIn && <>
            <button className={`nav-btn-mobile${view===VIEWS.DASHBOARD?" active":""}`} onClick={()=>nav(VIEWS.DASHBOARD)}>My Dashboard</button>
            <button className={`nav-btn-mobile${view===VIEWS.CONNECTIONS?" active":""}`} onClick={()=>nav(VIEWS.CONNECTIONS)}>Connections</button>
            <button className={`nav-btn-mobile${view===VIEWS.BULLETIN?" active":""}`} onClick={()=>nav(VIEWS.BULLETIN)}>Match Board</button>
            <button className={`nav-btn-mobile${view===VIEWS.EXCHANGE?" active":""}`} onClick={()=>nav(VIEWS.EXCHANGE)}>Exchange</button>
            <button className={`nav-btn-mobile${view===VIEWS.COURTS?" active":""}`} onClick={()=>nav(VIEWS.COURTS)}>Courts</button>
            <button className={`nav-btn-mobile${view===VIEWS.INSTRUCTORS?" active":""}`} onClick={()=>nav(VIEWS.INSTRUCTORS)}>Instructors</button>
            <button className={`nav-btn-mobile${view===VIEWS.BLOG?" active":""}`} onClick={()=>nav(VIEWS.BLOG)}>Events &amp; News</button>
            {isAdmin&&(
              <button className={`nav-btn-mobile${view===VIEWS.ADMIN?" active":""}`} onClick={()=>nav(VIEWS.ADMIN)}>
                Admin {adminQueue.length>0&&`(${adminQueue.length})`}
              </button>
            )}
            <div className="mobile-drawer-divider"/>
            <button className="nav-btn-mobile btn-cta btn-ghost" onClick={handleSignOut}>Sign Out</button>
          </>}
        </div>

        {/* MAIN CONTENT */}
        <main id="main-content">

        {/* VIEWS */}
        {view===VIEWS.HOME        && <HomeView/>}
        {view===VIEWS.COURTS      && <CourtsView/>}
        {view===VIEWS.INSTRUCTORS && <InstructorsView/>}
        {view===VIEWS.STRINGING   && <StringingView/>}
        {view===VIEWS.BLOG        && <BlogView/>}
        {view===VIEWS.CONNECTIONS && loggedIn && <ConnectionsView/>}
        {view===VIEWS.BULLETIN    && loggedIn && <BulletinView/>}
        {view===VIEWS.EXCHANGE    && loggedIn && <ExchangeView/>}
        {view===VIEWS.DASHBOARD   && loggedIn && <DashboardView/>}
        {view===VIEWS.LOGIN       && <LoginView onLogin={handleAppLogin}/>}
        {view===VIEWS.REGISTER    && <RegisterView
          nav={nav} showToast={showToast} setAdminQueue={setAdminQueue} VIEWS={VIEWS}
          toggleArr={toggleArr} GENDERS={GENDERS} AGE_RANGES={AGE_RANGES} NTRPS={NTRPS}
          SKILLS={SKILLS} PLAY_TYPES={PLAY_TYPES} DAYS={DAYS} TIMES={TIMES}
          DancingCMark={DancingCMark} TennisBall={TennisBall} AvailPill={AvailPill}/>}
        {view===VIEWS.ADMIN       && isAdmin && <AdminView
          nav={nav} showToast={showToast} VIEWS={VIEWS}
          adminQueue={adminQueue} setAdminQueue={setAdminQueue}
          instructors={instructors} setInstructors={setInstructors}
          stringers={stringers} setStringers={setStringers}
          bulletinPosts={bulletinPosts} setBulletinPosts={setBulletinPosts}
          exchangeListings={exchangeListings} setExchangeListings={setExchangeListings}
          LESSON_TYPES={LESSON_TYPES} toggleArr={toggleArr}
          RatingBadge={RatingBadge} TennisBall={TennisBall} MembersTab={MembersTab}
          onRefreshMembers={loadApprovedMembers}/>}
        {view===VIEWS.TERMS    && <TermsView nav={nav} VIEWS={VIEWS}/>}
        {view===VIEWS.RESET_PASSWORD && <ResetPasswordView nav={nav} showToast={showToast} VIEWS={VIEWS}/>}
        {view===VIEWS.PRIVACY  && <PrivacyView nav={nav} VIEWS={VIEWS}/>}
        {view===VIEWS.CONTACT  && <ContactUsView nav={nav} showToast={showToast} VIEWS={VIEWS}/>}
        </main>

        <footer className="footer">
          <div className="footer-brand">COLUMBUS TENNIS <span>CLUB</span></div>
          <div>Columbus, Indiana · Est. 2026 · <span style={{color:"var(--red)"}}>CTC</span></div>
          <div style={{marginTop:"12px",display:"flex",alignItems:"center",justifyContent:"center",gap:"16px",fontSize:"12px"}}>
            <button onClick={()=>nav(VIEWS.TERMS)}
              style={{background:"none",border:"none",color:"var(--grey)",cursor:"pointer",fontSize:"12px",
                fontFamily:"'DM Sans',sans-serif",textDecoration:"underline",padding:0}}>
              Terms &amp; Community Guidelines
            </button>
            <span style={{color:"var(--grey)",opacity:0.4}}>·</span>
            <button onClick={()=>nav(VIEWS.PRIVACY)}
              style={{background:"none",border:"none",color:"var(--grey)",cursor:"pointer",fontSize:"12px",
                fontFamily:"'DM Sans',sans-serif",textDecoration:"underline",padding:0}}>
              Privacy Policy
            </button>
            <span style={{color:"var(--grey)",opacity:0.4}}>·</span>
            <button onClick={()=>nav(VIEWS.CONTACT)}
              style={{background:"none",border:"none",color:"var(--grey)",cursor:"pointer",fontSize:"12px",
                fontFamily:"'DM Sans',sans-serif",textDecoration:"underline",padding:0}}>
              Contact Us
            </button>
          </div>
        </footer>

        {/* MODALS & TOAST */}
        <ContactModal/>
        {toast&&<div className={`toast${toast.type==="error"?" error":""}`} role="status" aria-live="polite">{toast.msg}</div>}
      </div>
    </>
    </ErrorBoundary>
  );
}